// /team-and-agents — agentic + people orchestration canvas.
//
// Same panzoom + Board + Dots + Connections stack /studios uses, but
// the nodes are People + Agents instead of project tiles. Modeled on
// canvas/pages/studios.js so a future refactor can lift the shared
// panzoom shell out into one base component.
//
// Surface:
//   - Panzoom canvas (mouse-wheel pan, ⌘/ctrl + wheel zoom, drag-pan
//     when the cursor isn't on a node).
//   - Person cards (avatar + name + role/email) — draggable.
//   - Agent cards (◆ glyph + name + caption) — draggable.
//   - SVG connections layer between nodes, derived from
//     `agent.boundTo` defaults + user-added `root._tagConnections`.
//   - Click a node → opens TeamMemberModal / AgentModal with details.
//     Agent modal renders the full `skills/agents/<id>.md` instructions
//     (bundled in canvas/functions/agentInstructions.js).
//   - Connect mode: header toggle → click first node → click second
//     node → adds a `{from,to}` pair to `root._tagConnections`.
//
// State surface on root:
//   - _tagPositions: { [id]: { x, y } }    — per-node coords
//   - _tagConnections: [{ from, to }]      — user-added links
//   - _tagConnectMode: boolean             — header toggle
//   - _tagConnectFrom: string | null       — first picked node
//   - _selectedTeamMember: person | null   — TeamMemberModal target
//   - _selectedAgent: agent | null         — AgentModal target

// initPanzoomBoard + the geom helpers (pointInRect / nodeUnderPoint /
// groupBbox / bezierBetweenNodes) live in canvas/functions/ and are
// registered as `el.call(...)` methods via canvas/context.js. We invoke
// them through the function registry rather than importing — keeps
// /team-and-agents on the same reuse contract every other canvas page
// follows (studios, embed/CanvasScene, /analytics/map).
//
// The agent personas (`AGENT_INSTRUCTIONS`, ~80 KB of markdown) load on
// demand: only provisioning and the agent editor read them, never a render.
// A static import put them in the shell's EAGER entry (this page rides
// canvas/components/eagerSlice.js), so every cold boot downloaded them;
// workspace/__tests__/entryBoundary.test.js holds the boundary.
const _agentInstructions = () =>
  import('../functions/agentInstructions.js').then((m) => m.AGENT_INSTRUCTIONS || {})
import { canvasAgentDisplayName } from '../functions/agentDisplayName.js'
import {
  canvasAgentRoster,
  canvasPlatformAgents,
  canvasRosterShaped
} from '../functions/agentRoster.js'
import { GROUP_SLOTS, nextFreeSlot, slotOf } from '../functions/slotGroups.js'
import { canvasResolveClaims } from '../functions/claimResolver.js'
import { getSDK } from '@symbo-ls/workspace-shared/functions/sdk.js'
import { coreHeaders } from '@symbo-ls/workspace-shared/functions/coreHeaders.js'
import { workspaceApiBase } from '@symbo-ls/workspace-shared/functions/workspaceApiBase.js'
import { cascadeMenu } from '@symbo-ls/workspace-shared/components/CascadeMenu.js'
// getObserver/setObserver drive an UNTRACKED region for the viewport-windowing
// first-paint seed — computing the initial visible set WITHOUT subscribing the
// People/Agents children factory to `_tagPositions` (which is rewritten on
// every drag pointermove, so a subscription would unmount+remount every node
// per move). MUST import from 'smbls' (not '@symbo.ls/signal') so we share the
// framework's single OBSERVER instance — a second signal instance silently
// no-ops setObserver (workspace memory: duplicate-signal-instance).
import { getObserver, setObserver } from 'smbls'

// Create-menu rows (New human / New agent) — shared by the floating "+" island
// AND the background right-click cascade menu (single source). `root` captured
// from the opener (the dropdown portal doesn't inherit page state);
// `closeDropdown()` with no id closes the top layer, so both openers work.
const tagCreateRows = (root) => {
  const rows = {}
  // /agents is agents-only — the "Human" create row is hidden there (only on
  // /team-and-agents + /my-agents can you invite a teammate).
  if (!root?._canvasAgentsOnly) {
    rows.NewHuman = {
      extends: 'CanvasCreateMenuItem',
      label: (el) => el.call('polyglot', 'canvas.team.create.humanLabel'),
      hint: (el) => el.call('polyglot', 'canvas.team.create.humanHint'),
      iconName: 'user',
      onClick: (ev, it) => {
        it.call('closeDropdown')
        root?.update({ _createMemberOpen: true, _createMemberError: null })
      }
    }
  }
  rows.NewAgent = {
    extends: 'CanvasCreateMenuItem',
    label: (el) => el.call('polyglot', 'canvas.team.create.agentLabel'),
    hint: (el) => el.call('polyglot', 'canvas.team.create.agentHint'),
    iconName: 'star',
    onClick: (ev, it) => {
      it.call('closeDropdown')
      root?.update({ _createAgentOpen: true, _createAgentError: null })
    }
  }
  // Activate agents — provision the whole seed roster as real, assignable,
  // chainable Mongo agents in one click (idempotent). Hidden once every seed
  // agent is already real (nothing left to provision).
  if ((_agents(root) || []).some((a) => a && a.id && !a.userId)) {
    rows.ActivateAgents = {
      extends: 'CanvasCreateMenuItem',
      label: (el) => el.call('polyglot', 'canvas.team.create.activateLabel'),
      hint: (el) => el.call('polyglot', 'canvas.team.create.activateHint'),
      iconName: 'zap',
      onClick: (ev, it) => {
        it.call('closeDropdown')
        if (root?._agentsProvisioning) return
        root?.update({ _agentsProvisioning: true })
        _provisionSeedAgents(root)
          .then((r) =>
            root?.update({
              _agentsProvisioning: false,
              _tagToast: {
                kind: 'ok',
                text: r.created
                  ? it
                      .call(
                        'polyglot',
                        r.created === 1
                          ? 'canvas.team.toast.activatedOne'
                          : 'canvas.team.toast.activatedMany'
                      )
                      .replace('{count}', r.created)
                  : it.call('polyglot', 'canvas.team.toast.allActive')
              }
            })
          )
          .catch((err) =>
            root?.update({
              _agentsProvisioning: false,
              _tagToast: {
                kind: 'error',
                text: (err && err.message) || 'canvas.team.toast.activationFailed'
              }
            })
          )
      }
    }
  }
  // Run chain — execute the user-connected agents as a Simone-driven chain.
  // Shown only once the user has drawn at least one agent→agent link.
  if (_agentChainEdges(root).length > 0) {
    rows.RunChain = {
      extends: 'CanvasCreateMenuItem',
      label: (el) => el.call('polyglot', 'canvas.team.create.runChainLabel'),
      hint: (el) => el.call('polyglot', 'canvas.team.create.runChainHint'),
      iconName: 'play',
      onClick: (ev, it) => {
        it.call('closeDropdown')
        _runAgentChain(root, it)
      }
    }
  }
  return rows
}

// ─── Constants ────────────────────────────────────────────────

const PERSON_W = 220
const PERSON_H = 96
const AGENT_W = 220
const AGENT_H = 110
const ROW_GAP = 40
const COL_GAP = 56

// Person avatars use the shared built-in Avatar (DiceBear image via
// el.call('avatarUrlFor', email)) — no local color/initials helpers.

// ─── Team category (§unified-perms PR #9) ─────────────────────
//
// Canvas can't import from @symbo-ls/workspace-shell (would create a
// circular dep — shell re-exports canvas), so the small bits of the
// 5-category model needed for color/cluster live inline. Keep these in
// lockstep with workspace/packages/workspace/functions/permissions.js.

const CATEGORIES = ['manager', 'active', 'passive', 'external', 'archived']

const _effectiveCategory = (teams) => {
  if (!teams || !teams.length) return 'none'
  const set = new Set(teams.map((t) => t && t.category).filter(Boolean))
  for (const cat of CATEGORIES) if (set.has(cat)) return cat
  return 'none'
}

// Color map for category-based node coloring. Picks colors from the
// design-system palette so dark/light themes both work. `none` stays
// neutral so unassigned people don't visually compete with team rows.
const _categoryColor = (cat) => {
  if (cat === 'manager') return 'purple'
  if (cat === 'active') return 'blue'
  if (cat === 'passive') return 'gray'
  if (cat === 'external') return 'orange'
  if (cat === 'archived') return 'red'
  return 'line'
}

// Resolve a person's dominant category. `root.teamsByUserId[p.userId]` (id-first)
// or `root.teamsByEmail[email]` (fallback) ships per-row `category` (loadAllTeams
// synthesizes it from root.teams), so no second lookup is needed.
// id-first (resolves for non-mgmt viewers whose rows carry no email), email
// fallback (mgmt viewers + optimistic writes) — tickets/fable.md P0 regression.
const _personCategory = (root, p) => {
  if (!p) return 'none'
  const byId = p.userId != null ? root?.teamsByUserId?.[String(p.userId)] : null
  const memberships =
    (Array.isArray(byId) && byId.length && byId) ||
    (p.email && root?.teamsByEmail?.[String(p.email).toLowerCase()]) ||
    null
  return _effectiveCategory(memberships)
}

// Categories that count as INACTIVE for the /my-agents lens. Everything
// else (active, manager, unassigned) is treated as an active member — so
// the personalized view keeps managers + uncategorized teammates but drops
// the explicitly inactive ones.
const _INACTIVE_CATEGORIES = new Set(['passive', 'external', 'archived'])
const _isActiveMember = (root, p) => !_INACTIVE_CATEGORIES.has(_personCategory(root, p))

// ─── Presence helpers ─────────────────────────────────────────
//
// Person nodes show an online dot when their email appears in
// root.onlineUsers (populated by useOnlinePresence — see
// shared/functions/presence.js). Agent nodes pulse when their slug
// matches a recent root.triage.agentPresence entry (last_heartbeat
// within 5 minutes — same threshold the /tickets dashboard uses).
//
// Both gracefully degrade when their source state isn't loaded yet
// (presence not subscribed, triage not fetched): the indicators just
// don't show.

// The peer's live presence status, read from `root.presenceById` — a per-id
// reactive map (lowercased email → 'online' | 'idle') maintained by
// useOnlinePresence ALONGSIDE the canonical `onlineUsers` array (see
// shared/functions/presence.js). Reading `presenceById[email]` subscribes each
// dot to ITS OWN id's signal, so a presence tick repaints only the node whose
// status changed — not all ~508. Absent key === offline.
//
// This replaced a linear `.find()` over `onlineUsers`: because every dot read
// the one shared array, any user's presence change re-ran every dot's `hide`
// factory (a full-board tick that janked on iPad).
const _personStatus = (root, email) => {
  if (!email) return null
  const byId = root?.presenceById
  if (!byId) return null
  return byId[String(email).toLowerCase()] || null
}

// Online = present AND not flagged idle. A present peer with no status is
// tracked as 'online' by presence.js, so this never regresses to invisible.
const _isPersonOnline = (root, email) => {
  const status = _personStatus(root, email)
  return !!status && status !== 'idle'
}

// Idle = present AND explicitly flagged idle. Mutually exclusive with
// _isPersonOnline; both are false when the peer isn't present at all.
const _isPersonIdle = (root, email) => _personStatus(root, email) === 'idle'

const AGENT_PULSE_MS = 5 * 60 * 1000

const _isAgentPulsing = (root, agentSlug) => {
  if (!agentSlug) return false
  // Two state slots are checked so we don't depend on /tickets having
  // been visited first: the canonical `root.triage.agentPresence` (set
  // by ticketStore on /tickets) or `root._tagAgentPresence` (set by
  // the lightweight standalone fetch below).
  const presence = root?.triage?.agentPresence || root?._tagAgentPresence
  if (!Array.isArray(presence) || presence.length === 0) return false
  const slug = String(agentSlug).toLowerCase()
  const now = Date.now()
  for (const p of presence) {
    if (String(p?.agent_name || '').toLowerCase() !== slug) continue
    if (!p?.last_heartbeat) continue
    const age = now - new Date(p.last_heartbeat).getTime()
    if (age >= 0 && age < AGENT_PULSE_MS) return true
  }
  return false
}

const AGENT_IDLE_MS = 30 * 60 * 1000

// Idle = the heartbeat has gone quiet (past the 5-min online window) but is
// still within 30 min — the agent is present but not actively pulsing. Cards
// show online (green pulse) vs idle (amber dot). (2026-06-19 per Nika "cards
// should show if members are online or idle".)
const _isAgentIdle = (root, agentSlug) => {
  if (!agentSlug) return false
  const presence = root?.triage?.agentPresence || root?._tagAgentPresence
  if (!Array.isArray(presence) || presence.length === 0) return false
  const slug = String(agentSlug).toLowerCase()
  const now = Date.now()
  for (const p of presence) {
    if (String(p?.agent_name || '').toLowerCase() !== slug) continue
    if (!p?.last_heartbeat) continue
    const age = now - new Date(p.last_heartbeat).getTime()
    if (age >= AGENT_PULSE_MS && age < AGENT_IDLE_MS) return true
  }
  return false
}

// Live "working now" check — reads the in-turn `root.activeProcesses[]` populated
// by the Simone chat transport (delegate_tasks / chain_agents → onProcessActive),
// which is on the SAME workspace app root as this canvas. True when a delegated
// sub-agent matching this slug is currently RUNNING, so the canvas lights up the
// agents Simone is actively using THIS turn. Matches by seedSlug (canonical) or
// agentName. Independent of the heartbeat/presence path (dormant — Option B, the
// long-running ticket-work viz, is a separate later phase).
const _isAgentWorking = (root, agentSlug) => {
  if (!agentSlug) return false
  const list = root?.activeProcesses
  if (!Array.isArray(list) || list.length === 0) return false
  const slug = String(agentSlug).toLowerCase()
  for (const p of list) {
    if (p?.status !== 'running') continue
    if (
      String(p?.seedSlug || '').toLowerCase() === slug ||
      String(p?.agentName || '').toLowerCase() === slug
    ) {
      return true
    }
  }
  return false
}

// Lightweight agent-presence poll for /team-and-agents. Mirrors the
// presence-only slice of `fetchGeneralTriage` — same Supabase REST
// endpoint, no ticket fetch — so the canvas can render the pulse
// indicator without paying the full triage round-trip every minute.
// WORKSPACE_ID matches the hard-coded id used by fetchGeneralTriage;
// a multi-tenant refactor is a follow-up shared between both pages.
// Symbols System workspace id — the workspace whose agentic flow used to load
// globally. Now used only to (a) key the Symbols-only epic seed + connections
// and (b) target presence when the Symbols workspace itself is active.
const SYMBOLS_WS_ID = '69e5911201b0ef47b675463f'
// Active workspace id (drift-corrected: prefer the resolved workspace over the
// JWT claim, which lags org switches — mirrors scopedClient.activeWorkspaceId).
const _canvasActiveWsId = (root) =>
  root?.workspace?.id || root?.jwtClaims?.active_workspace_id || null
const _TAG_PRESENCE_POLL_MS = 30 * 1000
// agent_presence — Mongo, via /core/agent-presence (workspace-scoped, flat
// wire shape). Migrated off Supabase REST on 2026-07-03; mirrors the reader
// in tickets/functions/fetchGeneralTriage.js.
const _tagPresenceUrl = (wsId) => `${workspaceApiBase()}/core/agent-presence?workspace=${wsId}`
const _fetchTagAgentPresence = async (root) => {
  if (!root?.update) return
  // Per-workspace presence — query the ACTIVE workspace, not a hard-coded one
  // (2026-06-17 per Nika "agentic flow per workspace, not global").
  const wsId = _canvasActiveWsId(root)
  if (!wsId) return
  try {
    const res = await fetch(_tagPresenceUrl(wsId), { headers: coreHeaders() })
    if (!res.ok) return
    const list = await res.json()
    if (Array.isArray(list)) root.update({ _tagAgentPresence: list })
  } catch (err) {
    /* network blip — try again next tick */
    console.warn('[team-and-agents] presence poll failed', err)
  }
}

// Resolve a team-group's category. `groupId` is `'team:NAME'`. We join
// against `root.teams` by name (the group id IS the team name).
const _teamGroupCategory = (root, groupId) => {
  if (!groupId || !groupId.startsWith('team:')) return 'none'
  const name = groupId.slice('team:'.length)
  const team = ((root && root.teams) || []).find((t) => t && t.name === name)
  return (team && team.category) || 'active'
}

// ─── Agent registry ───────────────────────────────────────────
//
// Canonical set, matching /Users/nikoloza/www/current/skills/agents/.
// Each entry maps to one of the bundled .md keys in
// AGENT_INSTRUCTIONS so the modal renders the full instructions.

// Canonical agent set — mirrors skills/HIERARCHY.md.
//
// Tier 0 — platform (cross-tenant)
//   simone
// Tier 1 — per-tenant brain
//   chuvak
// Tier 2 — siblings under chuvak
//   temo (PM / router), caesar (creative / design ops), watcher (analytics)
// Tier 3 — cross-epic helpers (paired with epic agents)
//   ux, ui-design, qa
// Tier 4 — epic subagents under temo (one per epic scope: sdk, server,
//   smbls, dompiler, extensions, design, infra, papuna, …)
//
// Future: replace this static seed with `sdk.users.list({ type: 'agent' })`
// once Mongo agent-user records land server-side. Until then, the page
// renders every agent in this list as a TeamAgentNode.
// MAIN_AGENTS — the universal default shown in EVERY workspace (per Nika
// 2026-06-17 "leave only main agents for all workspaces"). Caesar is the infra
// agent; Ochopintre is the studio agent. The Symbols-specific epic subagents
// are NOT global — they seed only the Symbols workspace (see below).
const MAIN_AGENTS = [
  // Tier 0 / 1
  { id: 'simone', name: 's1m0ne', caption: 'canvas.team.agentCaption.platform', kind: 'platform' },
  { id: 'chuvak', name: 'Chuvak', caption: 'canvas.team.agentCaption.brain', kind: 'brain' },

  // Tier 2 — siblings under chuvak
  { id: 'temo', name: 'Temo', caption: 'canvas.team.agentCaption.router', kind: 'router' },
  { id: 'caesar', name: 'Caesar', caption: 'canvas.team.agentCaption.infra', kind: 'infra' },
  {
    id: 'watcher',
    name: 'Watcher',
    caption: 'canvas.team.agentCaption.observer',
    kind: 'observer'
  },
  {
    id: 'ochopintre',
    name: 'Ochopintre',
    caption: 'canvas.team.agentCaption.studio',
    kind: 'studio'
  },

  // Tier 3 — cross-epic helpers
  { id: 'ux', name: 'UX', caption: 'canvas.team.agentCaption.helper', kind: 'helper' },
  {
    id: 'ui-design',
    name: 'UI Design',
    caption: 'canvas.team.agentCaption.helper',
    kind: 'helper'
  },
  { id: 'qa', name: 'QA', caption: 'canvas.team.agentCaption.helper', kind: 'helper' }
]

// SYMBOLS_EPIC_AGENTS — per-epic subagents that are Symbols-System-workspace
// data, NOT a global default (infra folded into Caesar). They seed only when
// the Symbols workspace is active; every other workspace shows MAIN_AGENTS
// plus its own real (Mongo) agents.
const SYMBOLS_EPIC_AGENTS = [
  { id: 'sdk', name: 'SDK', caption: 'canvas.team.agentCaption.epicSdk', kind: 'epic' },
  { id: 'server', name: 'Server', caption: 'canvas.team.agentCaption.epicServer', kind: 'epic' },
  { id: 'smbls', name: 'Smbls', caption: 'canvas.team.agentCaption.epicSmbls', kind: 'epic' },
  {
    id: 'dompiler',
    name: 'Dompiler',
    caption: 'canvas.team.agentCaption.epicDompiler',
    kind: 'epic'
  },
  {
    id: 'extensions',
    name: 'Extensions',
    caption: 'canvas.team.agentCaption.epicExtensions',
    kind: 'epic'
  },
  { id: 'design', name: 'Design', caption: 'canvas.team.agentCaption.epicDesign', kind: 'epic' },
  { id: 'papuna', name: 'Papuna', caption: 'canvas.team.agentCaption.epicPapuna', kind: 'epic' },
  {
    id: 'symbolize',
    name: 'Symbolize',
    caption: 'canvas.team.agentCaption.epicSymbolize',
    kind: 'epic'
  },
  { id: 'company', name: 'Company', caption: 'canvas.team.agentCaption.epicCompany', kind: 'epic' }
]

// Effective seed for the active workspace: MAIN everywhere, plus the Symbols
// epics only inside the Symbols workspace. This is what makes the agentic flow
// per-workspace instead of the old global Symbols hierarchy.
// Every seed row is a PLATFORM default until the org holds its own agent row
// (canvasAgentRoster — WL-7: own agents first, platform ones labelled).
const _seedAgents = (root) =>
  canvasAgentRoster(
    _canvasActiveWsId(root) === SYMBOLS_WS_ID
      ? [...MAIN_AGENTS, ...SYMBOLS_EPIC_AGENTS]
      : MAIN_AGENTS
  )

// ─── Agent registry — Mongo-backed (§unified-perms PR #10) ────
//
// SEED_AGENTS above is the canonical hierarchy/topology metadata
// (caption, kind, connection edges) — it's static config, not identity.
// Mongo carries identity for any agent provisioned with `User.kind ===
// 'agent'`; the two compose:
//
//   - `_agents(root)` returns the effective agent list. When Mongo has
//     entries for usernames matching a SEED_AGENTS id, the Mongo
//     identity (id = user._id, name, avatar) overrides SEED defaults
//     while preserving the caption + kind from SEED. New Mongo agents
//     without a SEED entry render with a generic caption.
//   - On cold boot or when listAgents fails, SEED_AGENTS is the
//     fallback so /team-and-agents always renders something.
//
// Boot fetch lives in the page's onCreate hook (one call per session,
// guarded by root._agentsLoaded). Future: re-fetch on revocation event
// once the user-channel emits 'agent.created' / 'agent.removed'.

const _agentsFromMongo = (mongoAgents, root, lang) => {
  const seed = _seedAgents(root)
  if (!Array.isArray(mongoAgents) || mongoAgents.length === 0) return seed
  const seedBySlug = new Map(seed.map((a) => [a.id, a]))
  const seenSlugs = new Set()
  const merged = []
  for (const u of mongoAgents) {
    // Join a Mongo agent back to its client seed on the per-org idempotence key
    // (metadata.seedSlug), NOT the org-namespaced username ('symbols-simone'),
    // which never equals the seed id ('simone') — that mismatch rendered the
    // built-in twice (the 's1m0ne' seed card + the 'Simone' Mongo card). A
    // matched agent keeps the seed's canonical id (so the hierarchy edges in
    // _baseConnections still connect) + its brand name/caption/kind, and carries
    // the Mongo userId/email so it stays assignable + chainable — collapsing the
    // two into ONE card. Falls back to the raw username for custom (non-seed)
    // agents and legacy non-namespaced usernames.
    const seedSlug = String(u.metadata?.seedSlug || '').toLowerCase()
    const rawSlug = String(u.username || u.id || u._id || '').toLowerCase()
    const seedEntry = (seedSlug && seedBySlug.get(seedSlug)) || seedBySlug.get(rawSlug) || null
    const key = seedEntry ? seedEntry.id : rawSlug
    if (!key) continue
    seenSlugs.add(key)
    merged.push({
      id: key,
      // A server-seeded default (the Personal Assistant) carries its name per
      // language on metadata.nameI18n — shown in the active interface language.
      name: seedEntry?.name || canvasAgentDisplayName(u, lang) || key,
      // A seed entry carries a bundle KEY; a custom (non-seed) Mongo agent has
      // no caption of its own, so it carries the generic key and the raw
      // username separately — the render site substitutes {name}. Building
      // 'Agent · ' + username here would be an untranslatable concatenation.
      // A row the platform seeded with no client seed of its own (the
      // Personal Assistant) is a built-in agent: the platform caption, never
      // "Agent · <org-username>".
      caption:
        seedEntry?.caption ||
        (seedSlug ? 'canvas.team.agentCaption.platform' : 'canvas.team.agentCaption.custom'),
      captionName: seedEntry || seedSlug ? null : u.username || key,
      kind: seedEntry?.kind || 'agent',
      avatar: u.avatar || null,
      userId: u.id || u._id || null,
      email: u.email || null,
      // the record's per-language names, kept on the roster row (the
      // contract's shape, shared/_simone/mention.js agentsFromMongo) so the
      // chat's @-mention picker names the agent in the active language too
      nameI18n: u.metadata?.nameI18n || null,
      // the per-org key the platform seeded it under — canvasAgentRoster keeps
      // such a row in the platform block (persona round 5, tomas)
      seedSlug: seedSlug || null
    })
  }
  for (const s of seed) {
    if (!seenSlugs.has(s.id)) merged.push(s)
  }
  // The org's own agents first; a seed with no row here is a platform default
  // (WL-7 — tomas's new workspace listed nine agents he never made).
  return canvasAgentRoster(merged)
}

// The canvas paints only its OWN roster shape (canvasRosterShaped): the lean
// roster the home or the Simone composer wrote under the same key falls back
// to the seed until the canvas's own load lands.
const _agents = (root) => {
  const fromState = root && root._agents
  if (canvasRosterShaped(fromState)) return fromState
  return _seedAgents(root)
}

// One load per session — unless another surface claimed the shared
// `_agentsLoaded` gate with its lean roster (persona round 5, tomas: the canvas
// returned at once on that gate and painted the lean list raw). The canvas's
// own claim is `__canvasAgentsLoading` while its read is out.
const _loadAgents = async (root, lang = root?.lang || null) => {
  if (!root || root.__canvasAgentsLoading) return
  if (root._agentsLoaded && canvasRosterShaped(root._agents)) return
  root._agentsLoaded = true
  root.__canvasAgentsLoading = true
  try {
    const sdk = getSDK()
    if (!sdk || typeof sdk.listAgents !== 'function') return
    // Org-scoped roster (2026-06-06) — agents belong to the active org.
    const raw = await sdk.listAgents(root?.jwtClaims?.workspace_org_id)
    const list = Array.isArray(raw) ? raw : (raw && raw.data) || []
    const merged = _agentsFromMongo(list, root, lang)
    root.update({ _agents: merged })
  } catch (err) {
    root._agentsLoaded = false
    console.warn('[team-and-agents] listAgents failed:', err && err.message)
  } finally {
    root.__canvasAgentsLoading = false
  }
}

// Provision the seed roster into Mongo: for every seed agent that is not yet a
// real (Mongo-backed) agent, create it via the SDK keyed by (org, seedSlug).
// This turns chuvak/temo/ux/qa/… from static topology into assignable,
// delegatable, CHAINABLE identities. Idempotent end-to-end — the server's
// createAgent returns an existing agent for a known (org, seedSlug) instead of
// duplicating, so the "Activate agents" button is safe to click repeatedly.
const _provisionSeedAgents = async (root) => {
  const sdk = getSDK()
  const org = root?.jwtClaims?.workspace_org_id
  if (!sdk || typeof sdk.createAgent !== 'function' || !org) {
    throw new Error('Agent provisioning is unavailable (no SDK or workspace org).')
  }
  const missing = _agents(root).filter((a) => a && a.id && !a.userId)
  const instructions = missing.length ? await _agentInstructions() : {}
  let created = 0
  for (const a of missing) {
    try {
      await sdk.createAgent({
        name: a.name || a.id,
        username: a.id,
        seedSlug: a.id,
        organization: org,
        instructions: instructions[a.id] || '',
        userRole: 'editor'
      })
      created += 1
    } catch (err) {
      console.warn('[team-and-agents] provision ' + a.id + ' failed:', err && err.message)
    }
  }
  // Re-fetch so the now-real agents pick up their Mongo userId + avatar and the
  // nodes flip from seed-only to live (delegatable/chainable).
  root._agentsLoaded = false
  await _loadAgents(root)
  return { created, missing: missing.length }
}

// Topo-order a set of agent→agent edges into a linear execution sequence.
// Returns null on a cycle (no valid order). Pure — no state reads.
const _orderAgentsByConnection = (edges) => {
  if (!Array.isArray(edges) || edges.length === 0) return []
  const out = new Map()
  const indeg = new Map()
  const nodes = new Set()
  for (const e of edges) {
    const from = String((e && e.from) || '').toLowerCase()
    const to = String((e && e.to) || '').toLowerCase()
    if (!from || !to || from === to) continue
    if (!out.has(from)) out.set(from, [])
    out.get(from).push(to)
    indeg.set(to, (indeg.get(to) || 0) + 1)
    nodes.add(from)
    nodes.add(to)
  }
  for (const n of nodes) if (!indeg.has(n)) indeg.set(n, 0)
  const queue = [...nodes].filter((n) => indeg.get(n) === 0)
  const ordered = []
  while (queue.length) {
    const cur = queue.shift()
    ordered.push(cur)
    for (const nxt of out.get(cur) || []) {
      indeg.set(nxt, indeg.get(nxt) - 1)
      if (indeg.get(nxt) === 0) queue.push(nxt)
    }
  }
  return ordered.length === nodes.size ? ordered : null
}

// The user-drawn edges between two AGENTS — the only edges that form a runnable
// chain (the base org hierarchy + agent↔person assignments are not execution
// chains). Shared by the "Run chain" row's visibility guard and the runner.
const _agentChainEdges = (root) => {
  const ids = new Set((_agents(root) || []).map((a) => a && a.id).filter(Boolean))
  const drawn = Array.isArray(root?._tagConnections) ? root._tagConnections : []
  return drawn.filter(
    (e) =>
      e && ids.has(String(e.from || '').toLowerCase()) && ids.has(String(e.to || '').toLowerCase())
  )
}

// Run the connected agents as a Simone-driven chain. Reuses the workspace
// sendPrompt flow end-to-end: opens the assistant drawer and dispatches a
// prompt instructing Simone to call chain_agents on the ordered agents. The
// chain streams in the drawer AND lights up the canvas via the existing
// activeProcesses / onProcessActive plumbing (_isAgentWorking).
const _runAgentChain = (root, el) => {
  const edges = _agentChainEdges(root)
  if (edges.length === 0) {
    root?.update({
      _tagToast: {
        kind: 'error',
        text: 'canvas.team.toast.chainNeedsTwo'
      }
    })
    return
  }
  const ordered = _orderAgentsByConnection(edges)
  if (!ordered || ordered.length < 2) {
    root?.update({
      _tagToast: { kind: 'error', text: 'canvas.team.toast.chainCycle' }
    })
    return
  }
  const byId = new Map((_agents(root) || []).map((a) => [a.id, a]))
  const names = ordered.map((id) => (byId.get(id) && byId.get(id).name) || id)
  const prompt =
    'Run an agent chain with these agents, in this exact order: ' +
    names.join(' → ') +
    ". Use the chain_agents tool — one step per agent, passing each step's output to the next. " +
    'Give each agent a task that fits its role and advances the chain. If you need the objective, ask me.'
  try {
    root?.update({ assistantOpen: true })
    el.call('sendPrompt', prompt)
    root?.update({
      _tagToast: {
        kind: 'ok',
        text: el
          .call('polyglot', 'canvas.team.toast.chainRunning')
          .replace('{names}', names.join(' → '))
      }
    })
  } catch (err) {
    root?.update({
      _tagToast: { kind: 'error', text: (err && err.message) || 'canvas.team.toast.chainFailed' }
    })
  }
}

// ─── Viewer-scoped visibility (the /my-agents lens) ───────────────
//
// /my-agents renders THIS canvas verbatim (it `extends: 'teamAndAgents'`)
// but filters the visible people + agents to the signed-in viewer's
// reach. The lens is gated entirely on `root._canvasScope === 'mine'`,
// which /my-agents sets in onCreate and clears on unmount. When the flag
// is unset — /team-and-agents and the home AgentsWidget embed — every
// accessor below returns the FULL set, so those surfaces are byte-for-
// byte unchanged.
//
// Tiers (Nika 2026-06-04), derived from the same chain the rest of the
// workspace uses (JWT claims, org-member roles, team-maintainer rows):
//   admin   — owner | co-owner | admin org role, `admin.teams` perm, or
//             jwt is_owner → sees EVERYONE (identical to unscoped).
//   manager — leads ≥1 team (team-maintainer in teamsByEmail) → self +
//             the people on teams they lead + every agent assigned to any
//             of them.
//   member  — self + the agents assigned to self. (Anyone who leads
//             people is promoted to manager by the team-chain check.)
const _ADMIN_ROLES = ['owner', 'co-owner', 'admin', 'superadmin']

const _viewerTier = (root) => {
  // Gate-A claim cutover: OFF (default) → root.jwtClaims verbatim; ON → is_owner
  // / permissions from the Mongo-sourced active-org row. See claimResolver.js.
  const claims = canvasResolveClaims(root)
  const role = ((root?.roles && root.roles.byUser) || {})[root?.currentUserId] || null
  const perms = Array.isArray(claims.permissions) ? claims.permissions : []
  // admin.teams is the canonical "can manage the team canvas" permission
  // (same gate as the agent-assignment + team-member mutations).
  if (claims.is_owner || _ADMIN_ROLES.includes(String(role || '')) || perms.includes('admin.teams'))
    return 'admin'
  const email = String(root?.currentUserEmail || '').toLowerCase()
  const myId = root?.currentUserId != null ? String(root.currentUserId) : ''
  // id-first (resolves for non-mgmt viewers whose rows carry no email),
  // email fallback (mgmt viewers + optimistic writes) — tickets/fable.md P0 regression
  const myByIdTeams = myId ? root?.teamsByUserId?.[myId] : null
  const myTeams =
    (Array.isArray(myByIdTeams) && myByIdTeams.length && myByIdTeams) ||
    (email && root?.teamsByEmail?.[email]) ||
    []
  if (myTeams.some((t) => /maintainer|manager|lead/i.test(String(t.role || '')))) return 'manager'
  return 'member'
}

// People on teams the viewer leads (manager tier); [] otherwise.
const _canvasPeopleUnderViewer = (root) => {
  const email = String(root?.currentUserEmail || '').toLowerCase()
  const myId = root?.currentUserId != null ? String(root.currentUserId) : ''
  // id-first (resolves for non-mgmt viewers whose rows carry no email),
  // email fallback (mgmt viewers + optimistic writes) — tickets/fable.md P0 regression
  const myByIdTeams = myId ? root?.teamsByUserId?.[myId] : null
  const myTeams =
    (Array.isArray(myByIdTeams) && myByIdTeams.length && myByIdTeams) ||
    (email && root?.teamsByEmail?.[email]) ||
    []
  const leadIds = myTeams
    .filter((t) => /maintainer|manager|lead/i.test(String(t.role || '')))
    .map((t) => String(t.id))
  if (!leadIds.length) return []
  const byUserId = root?.teamsByUserId || {}
  const byEmail = root?.teamsByEmail || {}
  const out = []
  for (const p of root?.people || []) {
    const pid = p?.userId != null ? String(p.userId) : ''
    const pe = String(p?.email || '').toLowerCase()
    if (pid ? pid === myId : pe === email) continue
    const pByIdTeams = pid ? byUserId[pid] : null
    const pTeams =
      (Array.isArray(pByIdTeams) && pByIdTeams.length && pByIdTeams) || (pe && byEmail[pe]) || []
    if (pTeams.some((t) => leadIds.includes(String(t.id)))) out.push(p)
  }
  return out
}

// Agents assigned to a person (by their User _id), resolved against the
// roster so we get the full {id, name, caption, …} node shape.
const _agentsAssignedToPerson = (root, assigneeUserId) => {
  if (!assigneeUserId) return []
  const rows = (root?.agentAssignmentsByAssignee || {})[assigneeUserId] || []
  const roster = _agents(root)
  const out = []
  for (const r of rows) {
    const a = roster.find((x) => String(x.userId) === String(r.agent))
    if (a) out.push(a)
  }
  return out
}

// Full person roster when unscoped or admin; the viewer's slice otherwise.
const _visiblePeople = (root) => {
  // /agents (agents-only) hides every human node — and, since the team swim-lane
  // frames derive from the visible people, the frames empty out too.
  if (root?._canvasAgentsOnly) return []
  const all = Array.isArray(root?.people) ? root.people : []
  const myId = String(root?.currentUserId || '')
  const myEmail = String(root?.currentUserEmail || '').toLowerCase()
  const isMe = (p) => String(p.userId) === myId || String(p.email || '').toLowerCase() === myEmail
  // Read-only-inactive (`status: 'inactive'`) and departed (`'left'`) members
  // are hidden from the board everywhere — including the full team-and-agents
  // view — while the viewer themselves is always kept.
  const activeStatus = (p) => p.status !== 'left' && p.status !== 'inactive'
  if (root?._canvasScope !== 'mine') return all.filter((p) => isMe(p) || activeStatus(p))
  // /my-agents shows only ACTIVE members (per Nika). The category pill is
  // hidden on this page, so passive/external/archived members would be
  // indistinguishable from active ones — drop them too. The viewer is always
  // kept, regardless of their own category/status.
  const keep = (p) => isMe(p) || (activeStatus(p) && _isActiveMember(root, p))
  const tier = _viewerTier(root)
  if (tier === 'admin') return all.filter(keep)
  const me = all.filter(isMe)
  if (tier !== 'manager') return me
  const seen = new Set()
  const merged = []
  for (const p of [...me, ..._canvasPeopleUnderViewer(root)]) {
    const id = p.userId || p.email
    if (seen.has(id)) continue
    seen.add(id)
    merged.push(p)
  }
  return merged.filter(keep)
}

// The agent ids assigned to the signed-in viewer (the "My agents" set on
// /agents). Resolved directly off the assignment map so it's independent of the
// people roster (which is empty in agents-only mode).
const _myAgents = (root, all) => {
  const mine = _agentsAssignedToPerson(root, root?.currentUserId)
  const ids = new Set(mine.map((a) => a.id))
  return all.filter((a) => ids.has(a.id))
}

// Every agent userId that appears as an assignee's assignment (for the
// assigned/unassigned filter). Built once per call.
const _assignedAgentUserIds = (root) => {
  const byAssignee = root?.agentAssignmentsByAssignee || {}
  const ids = new Set()
  for (const k of Object.keys(byAssignee)) {
    for (const r of byAssignee[k] || []) if (r?.agent != null) ids.add(String(r.agent))
  }
  return ids
}

// Apply the /agents toolbar filters (Status / Type / Assignment) to a set.
const _applyAgentFilters = (root, agents) => {
  let out = agents
  const status = root?._agentsStatus
  if (status === 'active') out = out.filter((a) => _isAgentPulsing(root, a.id))
  else if (status === 'idle') out = out.filter((a) => !_isAgentPulsing(root, a.id))
  const type = root?._agentsType
  if (type && type !== 'all') out = out.filter((a) => String(a.kind || '') === type)
  const assign = root?._agentsAssign
  if (assign === 'assigned' || assign === 'unassigned') {
    const assigned = _assignedAgentUserIds(root)
    out = out.filter((a) => assigned.has(String(a.userId)) === (assign === 'assigned'))
  }
  return out
}

// Full agent roster when unscoped or admin; otherwise the deduped union
// of agents assigned to the viewer's visible people.
const _visibleAgents = (root) => {
  const all = _agents(root)
  // /agents (agents-only): scope by the My/All toolbar, then apply the
  // Status/Type/Assignment filters. /team-and-agents + /my-agents below are
  // unchanged.
  if (root?._canvasAgentsOnly) {
    const base = root?._agentsScope === 'mine' ? _myAgents(root, all) : all
    return _applyAgentFilters(root, base)
  }
  if (root?._canvasScope !== 'mine') return all
  if (_viewerTier(root) === 'admin') return all
  const seen = new Set()
  const out = []
  for (const p of _visiblePeople(root)) {
    for (const a of _agentsAssignedToPerson(root, p.userId)) {
      if (seen.has(a.id)) continue
      seen.add(a.id)
      out.push(a)
    }
  }
  return out
}

// Visible node-id set (people + agents) for edge filtering. Returns null
// when unscoped / admin — meaning "no filter, show every edge".
const _visibleNodeIds = (root) => {
  if (root?._canvasScope !== 'mine' || _viewerTier(root) === 'admin') return null
  const ids = new Set()
  const people = _visiblePeople(root)
  for (let i = 0; i < people.length; i++) ids.add(_personId(people[i], i))
  for (const a of _visibleAgents(root)) ids.add(a.id)
  return ids
}

// ─── Layout helpers ───────────────────────────────────────────
//
// People are grouped by team — each team becomes a horizontal "swim
// lane" with a label header and its members laid out in a grid below.
// People with no team membership land in an "Other" lane at the end.
// Agents get their own lane below the team lanes.
//
// Team grouping reads from `root.teamsByEmail` (populated by
// workspace/functions/teams.js → loadAllTeams). Same selector as
// peopleGroups.js's `groupKeyOf` — keeps the team labels consistent
// with /people and the AI assistant.

// Studios-style grouping: studios uses `root.groupMap[nodeId] = ws`
// to OPT a node INTO a group; most nodes are ungrouped and sit free
// on the canvas. We mirror that here:
//
//   - People with a known team are grouped under that team. A team
//     frame renders only when 2+ members share it (matches studios:
//     `nodesOfGroup.length >= 2`). Solo-team people just sit free.
//   - Agents are NEVER auto-grouped. Each agent is a free node on the
//     canvas — the hierarchy is shown via the SVG connection edges
//     (simone → chuvak → temo → epic agents), not via a frame around
//     a "kind" cluster. This matches studios where unrelated projects
//     don't get auto-wrapped in a frame.
//
// Frame drag-to-rejoin / drag-to-leave behavior from studios.js is a
// follow-up — the structure here matches; the interaction hooks need
// the same `detectDropTarget` + `restackGroup` glue.
//
// Constants
const GROUP_PAD = 24 // padding around bbox
// The platform frame's head (PlatformFrame): the "Platform" label and its one
// line under it (canvas.team.platformNote — persona round 3, W5), in canvas
// units like every frame box here. The block of cards starts below it.
const PLATFORM_HEAD_H = 56
// Room above the platform agents' block for its frame head (PlatformFrame).
const PLATFORM_LABEL_H = PLATFORM_HEAD_H + GROUP_PAD
const GROUP_COLS = 4 // members per row in default layout
const GROUP_GAP_X = 80 // horizontal gap between groups
const GROUP_GAP_Y = 120 // vertical gap between group rows
const GROUPS_PER_ROW = 2 // how many groups stack horizontally
const MIN_GROUP_MEMBERS = 2 // hide frames for solo teams (studios convention)

// Returns the group id for a given person, or null when the person
// should sit ungrouped on the canvas. Agents always return null —
// they ride the connection edges, not a frame.
const _groupFor = (entry, root) => {
  if (_agents(root).some((a) => a.id === entry.id)) return null
  const email = String(entry.email || '').toLowerCase()
  // `entry.id` is userId-first (see _canvasEmailOf: "people use userId-or-email as
  // data-node-id"), so it doubles as the id-first lookup key here — falls
  // through to the email fallback when it's actually an email/synthetic id.
  // id-first (resolves for non-mgmt viewers whose rows carry no email), email
  // fallback (mgmt viewers + optimistic writes) — tickets/fable.md P0 regression
  const byId = entry.id != null ? root?.teamsByUserId?.[String(entry.id)] : null
  const teams = (Array.isArray(byId) && byId.length && byId) || root?.teamsByEmail?.[email] || []
  const name = teams[0]?.name
  if (!name) return null
  return 'team:' + name
}

const _groupLabel = (groupId) => {
  if (!groupId) return ''
  return groupId.slice('team:'.length)
}

// Studios-style group model — rewritten 2026-06-04. Replaces the
// previous two-track (team groups + ad-hoc groups) model with the
// same one /studios uses: a flat `_tagGroupMap[nodeId] = slot` plus
// `_tagGroupNames[slot] = label`, where `slot` is one of the
// pre-allocated `groupN` keys (group1..group50). Frames render via
// `groupN`-indexed children that derive their bbox from member
// positions; persistence rides the workspace-shared canvas-layout
// `groups` field, same as /studios projects.
//
// Team groups are NO LONGER a separate render surface — `teamsByEmail`
// rows seed the group map at hydration time (one slot per team with
// ≥2 members), but from that point forward they're just groups in
// the unified map. Drag-into-frame still triggers the team-membership
// SDK roundtrip when the destination slot was seeded from a team row;
// drag-out triggers the leave-team roundtrip. Drag-collision between
// two free nodes forms a brand-new ad-hoc group.
// Slot-map primitives shared with /studio — canvas/functions/
// slotGroups.js (extracted 2026-06-06 per Nika). Wrappers below bind
// them to this page's `_tag*` state keys.
const _groupMap = (root) =>
  (root && typeof root._tagGroupMap === 'object' && root._tagGroupMap) || {}

const _groupNames = (root) =>
  (root && typeof root._tagGroupNames === 'object' && root._tagGroupNames) || {}

// Which group slot (groupN | null) contains a given node id.
const _currentGroupOf = (root, nodeId) => slotOf(_groupMap(root), nodeId)

// Member node-ids of a given slot. Stable order: people first (by
// row index), then agents (alphabetical) — matches the on-canvas
// layout's natural reading order.
const _membersOfGroup = (root, slot) => {
  const map = _groupMap(root)
  const out = []
  for (const p of _visiblePeople(root)) {
    const id = p.userId || p.email
    if (id && map[id] === slot) out.push(id)
  }
  for (const a of _visibleAgents(root)) {
    if (a.id && map[a.id] === slot) out.push(a.id)
  }
  return out
}

// Pick the next free `groupN` slot.
const _nextGroupSlot = (root) => nextFreeSlot(_groupMap(root))

// Default label for a slot — uses the persisted name if present,
// falls back to "Group N" or the team name when the slot was seeded
// from a team membership row (team:Name → "Name").
const _slotLabel = (root, slot) => {
  const persisted = _groupNames(root)[slot]
  if (persisted) return persisted
  const map = _groupMap(root)
  const members = Object.entries(map)
    .filter(([, v]) => v === slot)
    .map(([k]) => k)
  if (!members.length) return slot
  // If every member shares the same team, label the slot after that
  // team (until the user renames it via the on-frame inline edit).
  const teamGroupId = _groupFor({ id: members[0], email: _canvasEmailOf(root, members[0]) }, root)
  if (teamGroupId) return teamGroupId.slice('team:'.length)
  return 'Group'
}

// Seed the slot map from `root.teamsByEmail` — one slot per team with
// ≥2 visible people. Used by `_hydrateLayoutFromPrefs` when the canvas
// has never been saved before, so members opening a fresh workspace
// land with their team frames already drawn.
// REAL teams only for a person — org-scoped, system permission groups
// (Admin / Members / … — every org member is in Members, which would
// collapse the whole canvas into one giant frame) excluded. Mirrors
// peopleGroups.js's _realTeams.
// id-first (resolves for non-mgmt viewers whose rows carry no email), email
// fallback (mgmt viewers + optimistic writes) — tickets/fable.md P0 regression
const _personRealTeams = (root, p) => {
  const byId = p?.userId != null ? root?.teamsByUserId?.[String(p.userId)] : null
  const rows =
    (Array.isArray(byId) && byId.length && byId) ||
    root?.teamsByEmail?.[String(p?.email || '').toLowerCase()] ||
    []
  const orgId = root?.jwtClaims?.workspace_org_id
  return rows.filter((r) => r?.id && !r.isSystem && (!orgId || String(r.orgId) === String(orgId)))
}

const _seedGroupsFromTeams = (root) => {
  const map = {}
  const names = {}
  const teamCounts = {} // teamId → member count in the visible set
  const teamLabels = {} // teamId → team.name
  for (const p of _visiblePeople(root)) {
    const t = _personRealTeams(root, p)[0]
    if (!t) continue
    teamCounts[t.id] = (teamCounts[t.id] || 0) + 1
    if (t.name && !teamLabels[t.id]) teamLabels[t.id] = t.name
  }
  let slotIdx = 0
  const teamToSlot = {}
  for (const [tid, count] of Object.entries(teamCounts)) {
    if (count < MIN_GROUP_MEMBERS) continue
    const slot = GROUP_SLOTS[slotIdx++] || 'group' + slotIdx
    teamToSlot[tid] = slot
    if (teamLabels[tid]) names[slot] = teamLabels[tid]
  }
  for (const p of _visiblePeople(root)) {
    const tid = _personRealTeams(root, p)[0]?.id
    const slot = tid && teamToSlot[tid]
    if (!slot) continue
    const nodeId = p.userId || p.email
    if (nodeId) map[nodeId] = slot
  }
  return { map, names }
}

// Merge the team seed into an EXISTING group map — saved canvases
// (which skip the fresh-canvas seed) still gain team frames for
// members nobody grouped manually. Manual placement always wins; new
// members land in the slot whose label matches their team name when
// one exists, otherwise in the next free slot. Returns null when
// nothing changed.
const _mergeTeamSeed = (root) => {
  const seeded = _seedGroupsFromTeams(root)
  if (!Object.keys(seeded.map).length) return null
  const map = { ..._groupMap(root) }
  const names = { ..._groupNames(root) }
  const used = new Set(Object.values(map))
  const labelToSlot = {}
  for (const [slot, label] of Object.entries(names)) labelToSlot[label] = slot
  // Bucket unassigned seeded nodes by seeded slot.
  const bySlot = {}
  for (const [id, slot] of Object.entries(seeded.map)) {
    if (map[id]) continue
    if (!bySlot[slot]) bySlot[slot] = []
    bySlot[slot].push(id)
  }
  let changed = false
  for (const [slot, ids] of Object.entries(bySlot)) {
    const label = seeded.names[slot]
    let target = label && labelToSlot[label]
    if (!target) {
      // A brand-new frame needs enough members to render.
      if (ids.length < MIN_GROUP_MEMBERS) continue
      target = GROUP_SLOTS.find((s) => !used.has(s))
      if (!target) break
      used.add(target)
      if (label) {
        names[target] = label
        labelToSlot[label] = target
      }
    }
    for (const id of ids) {
      map[id] = target
      changed = true
    }
  }
  return changed ? { map, names } : null
}

// `[{ id (slot), label, memberIds }]` describing every visible group
// on the canvas. Filtered to slots with ≥2 members (studios convention
// — solo "groups" never render a frame).
const _allGroups = (root) => {
  const slots = new Map()
  const map = _groupMap(root)
  for (const [nodeId, slot] of Object.entries(map)) {
    if (!slot) continue
    if (!slots.has(slot))
      slots.set(slot, { id: slot, memberIds: [], label: _slotLabel(root, slot) })
    slots.get(slot).memberIds.push(nodeId)
  }
  const arr = [...slots.values()].filter((g) => g.memberIds.length >= MIN_GROUP_MEMBERS)
  arr.sort((a, b) => (a.label || a.id).localeCompare(b.label || b.id))
  return arr
}

// People/agents NOT in any group (team or ad-hoc) — laid out as free
// nodes below the group row.
const _ungroupedEntries = (root) => {
  const people = _visiblePeople(root)
  const groups = _allGroups(root)
  const inGroup = new Set()
  for (const g of groups) for (const id of g.memberIds) inGroup.add(id)

  const out = []
  for (let i = 0; i < people.length; i++) {
    const p = people[i]
    const id = p.userId || p.email || 'p-' + i
    if (inGroup.has(id)) continue
    out.push({ id, kind: 'person', w: PERSON_W, h: PERSON_H })
  }
  for (const a of _visibleAgents(root)) {
    if (inGroup.has(a.id)) continue
    out.push({ id: a.id, kind: 'agent', w: AGENT_W, h: AGENT_H })
  }
  return out
}

// Bounding box around a group slot's current member positions.
// Reads from `_tagGroupMap` directly (not via _allGroups → slot
// filter) so this stays O(slot members) instead of O(all slots).
// Returns `null` when the slot has fewer than 2 members on canvas or
// `el` isn't available (pre-mount).
const _groupBbox = (root, slot, el) => {
  if (!el?.call) return null
  const memberIds = _membersOfGroup(root, slot)
  if (memberIds.length < MIN_GROUP_MEMBERS) return null
  // Use the complete merged map (grid defaults overlaid with persisted) so
  // a member that hasn't been dragged/saved yet still contributes to the
  // frame bbox instead of being read as {0,0}.
  const positions = _initialPositions(root)
  return el.call('bboxOfNodeIds', { ids: memberIds, positions, sizeOf: _sizeOfBound(root) })
}

// The frame around the platform agents (WL-7): the bbox of their current
// positions (dragged ones included), null when no platform agent is visible.
const _platformBbox = (root, el) => {
  if (!el?.call) return null
  const ids = canvasPlatformAgents(_visibleAgents(root)).map((a) => a.id)
  if (!ids.length) return null
  return el.call('bboxOfNodeIds', {
    ids,
    positions: _initialPositions(root),
    sizeOf: _sizeOfBound(root)
  })
}

const _initialPositions = (root) => {
  // Always compute the deterministic default grid for the FULL roster so
  // every node has a slot, THEN overlay persisted coords at the end
  // (persisted wins). The old short-circuit returned the persisted map
  // verbatim when non-empty — so any node missing from it (a freshly
  // loaded agent, a changed id, or the agents-vs-positions hydrate race)
  // fell through _posFor to {0,0}, stacking cards in the top-left corner.
  // Computing the grid unconditionally makes the on-load layout
  // deterministic no matter which async source (positions / agents)
  // resolves first — fixes "items not always arranged correctly on load".
  const positions = {}
  const groups = _allGroups(root)

  // 1. Lay team groups out first, 2-up grid, members in GROUP_COLS.
  let rowMaxH = 0
  let gx = 80
  let gy = 80
  let colInRow = 0
  let lastRowBottom = 80

  for (const group of groups) {
    const nodeW = PERSON_W
    const nodeH = PERSON_H
    const cols = Math.min(GROUP_COLS, Math.max(1, group.memberIds.length))
    const rows = Math.ceil(group.memberIds.length / cols)
    const groupW = cols * nodeW + (cols - 1) * COL_GAP
    const groupH = rows * nodeH + (rows - 1) * ROW_GAP

    group.memberIds.forEach((id, i) => {
      const col = i % cols
      const row = Math.floor(i / cols)
      positions[id] = {
        x: gx + col * (nodeW + COL_GAP),
        y: gy + row * (nodeH + ROW_GAP)
      }
    })

    rowMaxH = Math.max(rowMaxH, groupH)
    lastRowBottom = gy + rowMaxH
    colInRow++
    if (colInRow >= GROUPS_PER_ROW) {
      colInRow = 0
      gx = 80
      gy += rowMaxH + GROUP_GAP_Y
      rowMaxH = 0
    } else {
      gx += groupW + GROUP_GAP_X
    }
  }

  // 2. Free nodes (ungrouped people + all agents) below the groups.
  //    Studios convention: free nodes are not auto-laid-out in a grid —
  //    they sit on the canvas where the user placed them. For first
  //    paint though, we need SOME default coords so they don't all
  //    stack at (0,0). Lay them out in a 5-column grid below the last
  //    group row.
  const freeStartY = groups.length ? lastRowBottom + GROUP_GAP_Y * 2 : 80
  const free = _ungroupedEntries(root)
  const FREE_COLS = 5
  // The platform agents (canvasAgentRoster) start their OWN block below the
  // people and the workspace's own agents, with room above it for the
  // "Platform" frame label (PlatformFrame) — WL-7.
  const platformIds = new Set(canvasPlatformAgents(_visibleAgents(root)).map((a) => a.id))
  const ownFree = free.filter((entry) => !platformIds.has(entry.id))
  const platformFree = free.filter((entry) => platformIds.has(entry.id))
  let blockBottom = freeStartY
  ownFree.forEach((entry, i) => {
    const col = i % FREE_COLS
    const row = Math.floor(i / FREE_COLS)
    const y = freeStartY + row * (entry.h + ROW_GAP)
    positions[entry.id] = { x: 80 + col * (entry.w + COL_GAP), y }
    blockBottom = Math.max(blockBottom, y + entry.h)
  })
  const platformStartY = ownFree.length
    ? blockBottom + GROUP_GAP_Y + PLATFORM_LABEL_H
    : freeStartY + PLATFORM_LABEL_H
  platformFree.forEach((entry, i) => {
    const col = i % FREE_COLS
    const row = Math.floor(i / FREE_COLS)
    positions[entry.id] = {
      x: 80 + col * (entry.w + COL_GAP),
      y: platformStartY + row * (entry.h + ROW_GAP)
    }
  })

  // Overlay persisted coords last — a dragged/saved position always wins
  // over the computed grid slot; nodes with no saved coord keep their
  // deterministic grid slot rather than collapsing to {0,0}.
  const persisted = root?._tagPositions
  if (persisted && typeof persisted === 'object') {
    for (const [id, p] of Object.entries(persisted)) {
      if (p && typeof p.x === 'number' && typeof p.y === 'number') positions[id] = p
    }
  }

  // De-conflict default slots against the persisted coords. A node with NO saved
  // position (a newly-added member, or one whose id changed since the layout was
  // saved) gets a deterministic grid slot — but that slot can land EXACTLY on a
  // saved node's persisted coordinate (both are grid-aligned), stacking two cards
  // on top of each other. Any connector drawn to the buried node then reads as
  // pointing at empty space. Saved nodes are authoritative and never move; nudge
  // each colliding unsaved node straight down to the next free grid cell so every
  // node owns a distinct coordinate. (2026-06-29 per Nika — "humans not positioned
  // correctly, beziers connect to empty space".)
  const isSaved = (id) =>
    persisted &&
    typeof persisted === 'object' &&
    persisted[id] &&
    typeof persisted[id].x === 'number' &&
    typeof persisted[id].y === 'number'
  // Bbox de-confliction (not just exact-coordinate): a saved node may sit at an
  // off-grid coordinate, so an unsaved node nudged onto the next grid cell could
  // still visually overlap it. Compare full card boxes and bump straight down by
  // a grid row until the box is clear of every already-placed card.
  const rowStep = PERSON_H + ROW_GAP
  const taken = []
  const collides = (x, y) =>
    taken.some((t) => Math.abs(t.x - x) < PERSON_W && Math.abs(t.y - y) < PERSON_H)
  for (const id of Object.keys(positions)) {
    if (isSaved(id)) taken.push({ x: positions[id].x, y: positions[id].y })
  }
  for (const id of Object.keys(positions)) {
    if (isSaved(id)) continue
    let { x, y } = positions[id]
    let guard = 0
    while (collides(x, y) && guard++ < 500) y += rowStep
    positions[id] = { x, y }
    taken.push({ x, y })
  }

  return positions
}

const _personId = (p, idx) => p?.userId || p?.email || 'p-' + idx

// Resolve a node-id back to a person row. Used by suggest-team selection
// expansion and _onNodeClick; the two-line scan was duplicated.
const _personById = (root, id) => {
  const people = root?.people || []
  for (let i = 0; i < people.length; i++) {
    if (_personId(people[i], i) === id) return people[i]
  }
  return null
}

const _posFor = (root, id) => {
  // Merge persisted positions with freshly-computed defaults so people
  // that landed AFTER the user first dragged something around still
  // get a grid slot — previously the persisted map was treated as
  // authoritative and any id missing from it fell through to (0,0),
  // stacking new cards on top of each other in the corner.
  const persisted = root?._tagPositions
  if (persisted && persisted[id]) return persisted[id]
  const defaults = _initialPositions(root)
  return defaults[id] || { x: 0, y: 0 }
}

// ─── Connections ──────────────────────────────────────────────

const _baseConnections = (root) => {
  // skills/HIERARCHY.md role tree:
  //   simone → chuvak
  //   chuvak → { temo, caesar, watcher }                 (direct siblings only)
  //   temo   → { ux, ui-design, qa }                     (cross-epic helpers
  //                                                       — Temo dispatches
  //                                                       them, so Chuvak
  //                                                       doesn't double-link)
  //   temo   → { sdk, server, smbls, dompiler, extensions,
  //              design, infra, papuna, symbolize, company } (per-epic
  //              subagents)
  const main = [
    { from: 'simone', to: 'chuvak' },

    // chuvak's direct siblings only — chuvak doesn't connect to any
    // agent that Temo is already routing to (avoids visual double-lines
    // and reflects the actual dispatch path).
    { from: 'chuvak', to: 'temo' },
    { from: 'chuvak', to: 'caesar' },
    { from: 'chuvak', to: 'watcher' },
    { from: 'chuvak', to: 'ochopintre' },

    // temo dispatches to the cross-epic helpers (present in every workspace)
    { from: 'temo', to: 'ux' },
    { from: 'temo', to: 'ui-design' },
    { from: 'temo', to: 'qa' }
  ]
  // The per-epic subagent edges are Symbols-workspace-only flow (they used to
  // render in every workspace); temo dispatches to each epic, gated on the
  // active workspace so other workspaces show only the main hierarchy.
  if (_canvasActiveWsId(root) !== SYMBOLS_WS_ID) return main
  return [
    ...main,
    { from: 'temo', to: 'sdk' },
    { from: 'temo', to: 'server' },
    { from: 'temo', to: 'smbls' },
    { from: 'temo', to: 'dompiler' },
    { from: 'temo', to: 'extensions' },
    { from: 'temo', to: 'design' },
    { from: 'temo', to: 'papuna' },
    { from: 'temo', to: 'symbolize' },
    { from: 'temo', to: 'company' }
  ]
}

// (Removed 2026-06-06 per Nika: _teamCoMembershipEdges. It drew an
// edge between EVERY pair of people sharing a team — after the
// Members-system-team backfill every org member shares one team, so
// the canvas rendered a complete graph (~N²/2 lines) that read as a
// rendering mistake. Team relationships are conveyed by the team
// GroupFrames (see _seedGroupsFromTeams/_mergeTeamSeed); edges are
// reserved for the agent hierarchy, agent⇄person assignments and
// user-drawn connections.)

// Example assignment shipped as a default edge: the PM agent (Temo) is
// connected to Gusztav so the canvas demonstrates an agent⇄person
// assignment out of the box (the "connect PM agent to Gusztav" ask).
// Resolved dynamically from root.people so the endpoint matches whatever
// id (_personId) Gusztav's node renders with; if no Gusztav is in the
// roster the edge simply doesn't appear. Removable like any base edge
// (select it → Remove writes to _tagConnectionsRemoved).
const _exampleConnections = (root) => {
  const people = Array.isArray(root?.people) ? root.people : []
  for (let i = 0; i < people.length; i++) {
    const p = people[i]
    const hay = `${p?.name || ''} ${p?.email || ''}`.toLowerCase()
    if (hay.includes('gusztav')) return [{ from: 'temo', to: _personId(p, i) }]
  }
  return []
}

// Server-backed assignment edges (agent⇄person), sourced from
// root.agentAssignmentsByAgent (loaded by loadAllAgentAssignments). Agent
// node id = username slug; person node id = the assignee's User _id (==
// the person's _personId when they have a userId). Carries assignmentId so
// the edit bar can unassign through the server.
const _assignmentConnections = (root) => {
  const byAgent = root?.agentAssignmentsByAgent
  if (!byAgent || typeof byAgent !== 'object') return []
  const agents = _agents(root)
  const out = []
  for (const agentUserId of Object.keys(byAgent)) {
    const agent = agents.find((a) => String(a.userId) === String(agentUserId))
    const slug = agent?.id
    if (!slug) continue
    for (const a of byAgent[agentUserId] || []) {
      if (!a?.assignee) continue
      out.push({
        from: slug,
        to: String(a.assignee),
        assignment: true,
        assignmentId: a.id,
        agentUserId: String(agentUserId),
        assigneeUserId: String(a.assignee),
        relationship: a.relationship || null
      })
    }
  }
  return out
}

const _allConnections = (root) => {
  const base = _baseConnections(root)
  const example = _exampleConnections(root)
  const assignments = _assignmentConnections(root)
  const user = Array.isArray(root?._tagConnections) ? root._tagConnections : []
  const removed = Array.isArray(root?._tagConnectionsRemoved) ? root._tagConnectionsRemoved : []
  const removedSet = new Set(removed.map(_connectionKey))
  const seen = new Map()
  // Merge order matters for which edge "wins" on dedup: base first
  // (agent hierarchy), then user-added.
  // assignments BEFORE example so a real server assignment wins dedup
  // (keeps its assignmentId) over the local example edge for the same pair.
  // (Team co-membership edges removed 2026-06-06 — team frames carry
  // that relationship now.)
  for (const c of [...base, ...assignments, ...example, ...user]) {
    const k = _connectionKey(c)
    if (removedSet.has(k)) continue
    if (seen.has(k)) continue
    seen.set(k, { ...c })
  }
  const merged = [...seen.values()]
  // /agents (agents-only) is a flat grid of agent cards — every edge would
  // dangle to a now-hidden person, so drop them all.
  if (root?._canvasAgentsOnly) return []
  // Under the /my-agents lens, drop edges whose endpoints aren't both
  // visible so no edge dangles to a filtered-out node. null = unscoped /
  // admin → keep every edge (unchanged for /team-and-agents).
  const visible = _visibleNodeIds(root)
  if (!visible) return merged
  return merged.filter((c) => visible.has(c.from) && visible.has(c.to))
}

// Size lookup used by the shared `bezierBetweenNodes` /
// `nodeUnderPoint` / `groupBbox` helpers. Single source of truth so
// every reuse site agrees on which node ids get which dimensions.
// `root` is optional: callers without root context (legacy/early-boot)
// fall back to the SEED list, which is a strict subset of the merged
// Mongo-backed registry — meaning any agent in SEED is always
// classified correctly; only Mongo-only agents that haven't been added
// to SEED yet get person-sized geometry (acceptable visual drift, no
// functional break).
const _canvasIsAgent = (id, root) => {
  const src = root ? _agents(root) : MAIN_AGENTS
  return src.some((a) => a.id === id)
}
const _nodeWidth = (id, root) => (_canvasIsAgent(id, root) ? AGENT_W : PERSON_W)
const _nodeHeight = (id, root) => (_canvasIsAgent(id, root) ? AGENT_H : PERSON_H)
// `root` is optional — without it, _canvasIsAgent falls back to SEED_AGENTS,
// which misclassifies any Mongo-only agent as a person. The two helper
// call sites here both have root available, so they pass the bound
// closure via _sizeOfBound below; the bare _sizeOf is kept for any
// boot-time path that runs before root state is ready.
const _sizeOf = (id, root) => ({ w: _nodeWidth(id, root), h: _nodeHeight(id, root) })
const _sizeOfBound = (root) => (id) => _sizeOf(id, root)

// SVG path from one node center to another — defers to the shared
// `bezierBetweenNodes` helper so the math doesn't drift from studios'
// connection routing. `_posFor` resolves positions identically to
// the bbox helper, then we hand off.
const _linePath = (root, fromId, toId, el) => {
  // Merge persisted + default positions so the helper sees a complete
  // map for both endpoints.
  const positions = {
    ...(root?._tagPositions || {}),
    ...(_initialPositions(root) || {})
  }
  // When `el` is available (DOMQL factory), prefer el.call so the
  // helper resolution goes through the function registry. Otherwise
  // (called from a pure module helper) we'd have to import — but
  // every call site here has el, so the el.call path is always taken.
  return (
    el?.call?.('bezierBetweenNodes', { fromId, toId, positions, sizeOf: _sizeOfBound(root) }) || ''
  )
}

// ─── Node click + drag handlers ───────────────────────────────
//
// Module-level "just dragged" flag — set by the mousedown drag handler
// when the cursor crossed the 3px threshold during a press. The click
// handler checks it and bails so a drag-release doesn't also open the
// modal. Cleared on the next mousedown so successive clicks work.
let _justDragged = false

const _onNodeClick = (e, el, s, kind) => {
  // Suppress click handling immediately after a drag — the DOM still
  // fires `click` after mouseup even if mousemove moved the element,
  // so we use this flag to differentiate "tap" from "drag-release".
  if (_justDragged) {
    _justDragged = false
    e.preventDefault()
    e.stopPropagation()
    return
  }

  const root = s.root
  const id = el.node?.dataset?.nodeId
  if (!id) return

  if (root._tagConnectMode) {
    e.preventDefault()
    e.stopPropagation()
    const first = root._tagConnectFrom
    if (!first) {
      root.update({ _tagConnectFrom: id })
      return
    }
    if (first === id) {
      root.update({ _tagConnectFrom: null })
      return
    }
    const existing = Array.isArray(root._tagConnections) ? root._tagConnections : []
    root.update({
      _tagConnections: [...existing, { from: first, to: id }],
      _tagConnectFrom: null
    })
    return
  }

  // Normal click:
  //  - Agent → open AgentModal (no `/agent-profile` route exists yet).
  //  - Person → open the shared profile modal (same one OnlineMembers /
  //    IntranetRows / avatar chips open). Replaces the prior
  //    PersonDetailModal hop per Nika's 2026-06-03 ask — that modal's
  //    Icons.Close was rendering off-viewport on this canvas and the
  //    avatar-click profile modal is the canonical "view this member"
  //    surface across the workspace.
  if (kind === 'agent') {
    const agent = _agents(root).find((a) => a.id === id)
    if (agent) _openAgentEditor(root, agent)
    return
  }

  const person = _personById(root, id)
  if (!person?.email) return
  el.call('openProfileModal', person.email)
}

// Which NON-self node is the dragged card centered over? Used for
// drag-collision-to-form-group (studios pattern). Delegates the rect
// test to the shared `nodeUnderPoint` helper via el.call, so the
// collision math is the same one every other canvas surface uses.
// Skips agents (they ride connection edges, not group frames).
const _hoveredNode = (el, root, draggedId, draggedW, draggedH) => {
  // Complete map (default grid + persisted overlay) — `_tagPositions`
  // alone is a sparse user-drag map and misses un-dragged nodes.
  const positions = _initialPositions(root)
  const p = positions[draggedId]
  if (!p) return null
  const cx = p.x + draggedW / 2
  const cy = p.y + draggedH / 2
  const candidates = (root?.people || [])
    .map((person) => person.userId || person.email)
    .filter(Boolean)
    .map((id) => ({ id, w: PERSON_W, h: PERSON_H }))
  return (
    el?.call?.('nodeUnderPoint', {
      candidates,
      positions,
      cx,
      cy,
      ignoreId: draggedId
    }) || null
  )
}

// Which team group's frame is the cursor (center of the dragged card)
// currently over? Returns the group id ('team:Name') or null. Skips
// the dragged node's OWN group so the user never gets a "join current
// group" no-op.
const _hoveredGroup = (el, root, draggedId, draggedW, draggedH) => {
  // Complete map (default grid + persisted overlay) — `_tagPositions`
  // alone is a sparse user-drag map and misses un-dragged nodes.
  const positions = _initialPositions(root)
  const p = positions[draggedId]
  if (!p) return null
  const cx = p.x + draggedW / 2
  const cy = p.y + draggedH / 2
  const ownGroup = _groupFor({ id: draggedId, email: _canvasEmailOf(root, draggedId) }, root)
  for (const g of _allGroups(root)) {
    if (g.id === ownGroup) continue
    const bb = _groupBbox(root, g.id, el)
    if (!bb) continue
    // Match the frame's painted rect (includes GROUP_PAD on every side
    // and an extra 28px label gutter above). Defers the rect test to
    // the shared `pointInRect` helper.
    const expanded = {
      x: bb.x - GROUP_PAD,
      y: bb.y - GROUP_PAD - 28,
      w: bb.w + GROUP_PAD * 2,
      h: bb.h + GROUP_PAD * 2 + 28
    }
    if (el?.call?.('pointInRect', cx, cy, expanded)) return g.id
  }
  return null
}

// Email lookup for a person id (people use userId-or-email as data-node-id).
const _canvasEmailOf = (root, id) => {
  const people = root?.people || []
  for (let i = 0; i < people.length; i++) {
    const p = people[i]
    if ((p.userId || p.email || 'p-' + i) === id) return p.email
  }
  return null
}

// Find the SDK Team row matching a frame's group id ('team:NAME').
const _teamForGroupId = (root, groupId) => {
  if (!groupId) return null
  const label = groupId.slice('team:'.length)
  return (root?.teams || []).find((t) => t.name === label) || null
}

// Create or extend a group via drag-collision (studios pattern,
// 2026-06-04 rewrite). Mutates `_tagGroupMap` (the flat
// nodeId → slot map) instead of the previous adhoc-array model.
//   - draggedId currently free + targetId free   → new slot containing both
//   - draggedId free + targetId already in slot  → draggedId joins
//     that existing slot
//   - draggedId already in slot + targetId free  → targetId joins
//     draggedId's slot
//   - both in different slots                    → no-op (would need
//     a merge prompt; studios behaves the same).
const _formGroupOnDrop = (root, draggedId, targetId) => {
  if (!root || !draggedId || !targetId || draggedId === targetId) return
  const draggedSlot = _currentGroupOf(root, draggedId)
  const targetSlot = _currentGroupOf(root, targetId)

  if (draggedSlot && targetSlot && draggedSlot !== targetSlot) return

  const map = { ..._groupMap(root) }
  if (!draggedSlot && targetSlot) {
    map[draggedId] = targetSlot
    root.update({ _tagGroupMap: map })
    return
  }
  if (draggedSlot && !targetSlot) {
    map[targetId] = draggedSlot
    root.update({ _tagGroupMap: map })
    return
  }
  if (!draggedSlot && !targetSlot) {
    const slot = _nextGroupSlot(root)
    map[draggedId] = slot
    map[targetId] = slot
    root.update({ _tagGroupMap: map })
  }
}

// Drop a person card outside every team frame → leave their team.
//
// The team is the one the slot's frame is named after, among the person's
// REAL teams (system groups and other orgs excluded), and only a viewer who
// may manage team members (admin tier — the same gate as the agent
// assignments) gets the leave. null → the drop is layout only (an ad-hoc
// group, a renamed frame, a viewer below admin).
//
// Slot ids are `groupN`, never `team:NAME`: the old `_teamForGroupId(slot)`
// lookup matched no team, so before this the leave never reached the server
// and the frame re-seeded the person on the next load.
const _leavableTeam = (root, personId, slot) => {
  if (!slot || _viewerTier(root) !== 'admin') return null
  const person = _personById(root, personId)
  if (!person?.userId) return null
  const label = _slotLabel(root, slot)
  return _personRealTeams(root, person).find((t) => t.name === label) || null
}

const _personLabel = (el, person) =>
  person?.name ||
  String(person?.email || '').split('@')[0] ||
  el.call('polyglot', 'canvas.team.toast.memberFallback')

// Put the card back where the drag started (Cancel changes nothing).
const _putCardBack = (el, root, personId, start) => {
  root.update({
    _tagPositions: { ...(root._tagPositions || {}), [personId]: { x: start.x, y: start.y } }
  })
  _saveLayoutToPrefs(el, root)
}

const _removeFromSlot = (root, personId) => {
  const map = { ..._groupMap(root) }
  delete map[personId]
  root.update({ _tagGroupMap: map })
}

// The house confirm names the person and the team (CRUD + confirm standard
// rule 4); nothing changes before the verb. Every way out but the verb puts
// the card back.
const _askLeaveTeamOnDrop = (el, root, personId, slot, team, start) => {
  const person = _personById(root, personId)
  el.call('confirmTeamLeave', {
    name: _personLabel(el, person),
    team,
    onConfirm: () => _leaveTeamOnDrop(el, root, personId, slot, team, start),
    onCancel: () => _putCardBack(el, root, personId, start)
  })
}

// Confirmed: the card leaves the frame and the membership follows
// (runTeamLeave: optimistic, SDK, rollback of the membership rows). A failed
// removal puts the card back in its frame and says so (rule 5).
const _leaveTeamOnDrop = async (el, root, personId, slot, team, start) => {
  const person = _personById(root, personId)
  if (!person?.userId) return
  _removeFromSlot(root, personId)
  _saveLayoutToPrefs(el, root)
  const left = await el.call('runTeamLeave', root, {
    team,
    userId: person.userId,
    email: person.email
  })
  if (left === true) return
  if (!_currentGroupOf(root, personId)) {
    root.update({ _tagGroupMap: { ..._groupMap(root), [personId]: slot } })
  }
  _putCardBack(el, root, personId, start)
  root.update({
    _tagToast: {
      kind: 'error',
      text: el
        .call('polyglot', 'canvas.team.toast.leaveFailed')
        .replace('{name}', () => _personLabel(el, person))
        .replace('{team}', () => team.name)
    }
  })
}

// Optimistic teamsByEmail mutation. Studios calls back to the SDK on
// drop; we do the same here via `el.call('addMemberToTeam', …)` so the
// server-side `Team` + `TeamMember` records reflect the new assignment
// across every surface. On SDK failure the optimistic update reverts.
const _joinTeamOnDrop = async (el, root, personId, fromGroupId, toGroupId) => {
  const team = _teamForGroupId(root, toGroupId)
  if (!team) return
  const email = _canvasEmailOf(root, personId)
  if (!email) return
  const person = (root.people || []).find((p) => (p.userId || p.email) === personId)
  const userId = person?.userId
  if (!userId) return

  // Optimistic local merge — add the new team entry for this email so
  // _allGroups / _groupBbox / _ungroupedEntries re-derive immediately.
  const prev = root.teamsByEmail || {}
  const key = String(email).toLowerCase()
  const existing = (prev[key] || []).filter((e) => e.id !== team.id)
  // Carry category + status forward so the optimistic membership row
  // looks identical to one produced by loadAllTeamMembers — without
  // these, TeamPersonNode's category-color border reverts to
  // "unknown" until the next refresh.
  const nextEntries = [
    ...existing,
    {
      id: team.id,
      name: team.name,
      slug: team.slug,
      orgId: team.orgId,
      role: 'member',
      category: team.category || null,
      status: team.category === 'passive' ? 'passive' : 'active'
    }
  ]
  root.update({ teamsByEmail: { ...prev, [key]: nextEntries } })

  try {
    await el.call('addMemberToTeam', root, {
      orgId: team.orgId,
      teamId: team.id,
      userId,
      email
    })
  } catch (err) {
    // Revert the optimistic merge — `addMemberToTeam` itself optimistically
    // writes to teamsByEmail on success, so a failure means we never
    // recorded the entry server-side. Restore the prior shape.
    root.update({ teamsByEmail: prev })
    console.warn('[team-and-agents] join-team drop failed:', err?.message || err)
  }
}

// ─── Persistence (workspace-shared canvas layout) ────────────────
//
// 2026-06-04: migrated from per-user `workspaceProject.userPreferences`
// to the workspace-shared `sdk.canvasLayout` surface (same Mongo-backed
// `/workspaces/:wsId/canvas-layout` endpoint /studios uses for project
// tile positions). Every member sees and updates the same node map —
// drag a card and the new position appears for everyone in the org.
//
// Storage strategy:
//   - `positions` map is namespaced with the `ta:` prefix so it can
//     coexist with /studios' project ids and /my-agents' `ma:` prefix
//     in the same workspace layout document.
//   - Live broadcast: `sdk.subscribeWorkspaceCanvasLayout` fires on
//     every PATCH from any client; the page absorbs incoming positions
//     into `_tagPositions` so drags from another tab/member reflect
//     without a refresh.
//   - Per-user state (`_tagConnections`, `_tagConnectionsRemoved`,
//     `_tagAdHocGroups`) stays in `workspaceProject.userPreferences`
//     for now — the canvas-layout schema is `{positions, groups}`
//     only; promoting those to workspace-shared needs a server-side
//     schema extension (follow-up).

const _TA_PREFIX = 'ta:'
const _PREFS_KEY = 'team_and_agents_layout'

// Resolve the workspace id every canvas-layout call needs. The
// canvas-layout endpoint is keyed by WORKSPACE id (Mongo `_id` of the
// active workspace inside the active org), NOT the org id — verified
// 2026-06-04 by trial: `workspace_org_id` returns "Workspace not
// found", whereas `jwtClaims.active_workspace_id` resolves. Prefer
// the JWT claim (refreshed on every login + switch) with `root.workspace.id`
// as a fallback for SPA paths where state.workspace hydrates before
// the next JWT mint.
const _wsId = (root) => root?.jwtClaims?.active_workspace_id || root?.workspace?.id || null

// Strip the `ta:` prefix from a positions object received from the
// server back into the bare node id space the rest of this page reads.
const _unprefixPositions = (positions) => {
  const out = {}
  if (!positions || typeof positions !== 'object') return out
  for (const [k, v] of Object.entries(positions)) {
    if (!k.startsWith(_TA_PREFIX)) continue
    if (v && typeof v === 'object' && typeof v.x === 'number' && typeof v.y === 'number') {
      out[k.slice(_TA_PREFIX.length)] = { x: v.x, y: v.y }
    }
  }
  return out
}

// Reconstruct the flat group model from canvas-layout's groups field.
// Returns { map: { nodeId: slot }, names: { slot: label } } or null
// if there are no `ta:`-namespaced groups in the document.
const _unprefixGroups = (groups) => {
  if (!groups || typeof groups !== 'object') return null
  const map = {}
  const names = {}
  let touched = false
  for (const [k, v] of Object.entries(groups)) {
    if (!k.startsWith(_TA_PREFIX)) continue
    if (!v || typeof v !== 'object') continue
    // `{ key, name? }` — the server's canvas-layout group-entry contract
    // (WorkspaceCanvasLayout groupSchema). Any other shape is dropped by
    // the PATCH merge server-side, so it can never appear in a stored doc.
    const slot = v.key
    if (slot) {
      map[k.slice(_TA_PREFIX.length)] = slot
      if (v.name) names[slot] = v.name
      touched = true
    }
  }
  return touched ? { map, names } : null
}

// Build the canvas-layout groups payload from the flat _tagGroupMap +
// _tagGroupNames shape. One entry per member node, namespaced with
// the `ta:` prefix.
const _prefixGroups = (groupMap, groupNames) => {
  const out = {}
  if (!groupMap || typeof groupMap !== 'object') return out
  const names = groupNames && typeof groupNames === 'object' ? groupNames : {}
  for (const [nodeId, slot] of Object.entries(groupMap)) {
    if (!slot) continue
    // `{ key, name? }` per the server contract — entries shaped any other
    // way are silently dropped by the PATCH merge (how groups vanished).
    out[_TA_PREFIX + nodeId] = {
      key: slot,
      ...(names[slot] ? { name: names[slot] } : {})
    }
  }
  return out
}

// Imperatively re-apply node positions to the mounted DOM. v3.14
// layout-prop factories can freeze at their mount value (reactive
// layout props don't re-apply); the per-key style{left,top} form the
// nodes use is the working dynamic form, but when the async
// canvas-layout GET lands AFTER the People/Agents children mounted, a
// missed tick leaves already-mounted cards at their default grid
// slots. This loop is the same imperative write pattern the page uses
// for the board transform (applyBoardTransform): scoped to THIS
// board's node, rAF'd, and a no-op for nodes that aren't mounted yet
// (those read the fresh _tagPositions via _posFor at mount anyway).
const _applyPositionsToDom = (root, el) => {
  if (typeof window === 'undefined') return
  const board = el?.AppMain?.Body?.Stage?.Board?.node
  if (!board || !board.querySelectorAll) return
  window.requestAnimationFrame(() => {
    const positions = _initialPositions(root)
    for (const node of board.querySelectorAll('[data-node-id]')) {
      const p = positions[node.dataset.nodeId]
      if (!p || typeof p.x !== 'number' || typeof p.y !== 'number') continue
      node.style.left = p.x + 'px'
      node.style.top = p.y + 'px'
    }
  })
}

// ─── Viewport windowing (render only on-screen nodes) ─────────────
//
// The board holds ~508 person/agent cards. Mounting all of them on first
// paint builds ~508 card subtrees and decodes ~508 avatars — the iPad
// first-paint cost. Windowing renders only the nodes whose board-space AABB
// intersects the panzoom viewport (+ margin), mounting/unmounting as the
// camera pans. The reactive contract is deliberately narrow:
//
//   • The People/Agents children factory filters on `root._tagVisibleIds`
//     (a plain { [id]: 1 } map) and NOTHING else position-related. It must
//     never read `_tagPositions` — that signal is rewritten on every drag
//     pointermove (see _onNodePointerdown), so subscribing the factory to it
//     would unmount+remount every node on every move (a reconcile storm).
//   • `_tagVisibleIds` is (re)computed OUTSIDE any reactive effect — in an
//     rAF driven by the panzoom transform (+ roster / position / fit hooks) —
//     and written via root.update, which re-runs the factory exactly once per
//     membership change (the set-diff gate below drops no-op writes).
//
// Hysteresis (MOUNT_MARGIN < KEEP_MARGIN): a node mounts when it enters the
// inner MOUNT band and stays until it leaves the wider KEEP band, so nodes
// hovering the edge don't thrash mount/unmount on tiny pans.
const _TEAM_WINDOW_MOUNT_MARGIN = 420 // board-px past the viewport edge to MOUNT
const _TEAM_WINDOW_KEEP_MARGIN = 900 // board-px past the viewport edge to KEEP mounted
// Uniform windowing box: person + agent cards share width (220); heights are
// 96 / 110, so the max (110) over-includes a person's box by 14px — which only
// ever errs toward MOUNTING (never wrongly unmounts a visible node) and keeps
// the intersection test O(n) with no per-node agent lookup.
const _WIN_NODE_W = PERSON_W
const _WIN_NODE_H = Math.max(PERSON_H, AGENT_H)

// Node ids pinned into the visible set regardless of viewport — the node being
// position-dragged and the link-drag source. Module-level, matching
// _justDragged / _activeDrag: only one drag runs at a time globally, and a
// pinned id with no matching node in a given board is just an inert map entry.
const _pinnedIds = new Set()

// Resolve the page root store from any element defensively (mirrors the
// onCreate/onRemove derivation — `state` isn't always threaded through the
// home-embed extend chain).
const _tagRootOf = (el) => {
  const state = el?.state || (typeof el?.getRoot === 'function' ? el.getRoot().state : null)
  return state?.root || null
}

// Walk up from any node / link-handle element to the teamAndAgents PAGE
// element — the one that owns __tagPzInstance + the windowing recompute state.
// Follows THIS element's own parent chain (el.parent is already used at the
// LinkHandle), so a second embedded board is never cross-driven.
const _tagPageElOf = (el) => {
  let n = el
  let guard = 0
  while (n && guard++ < 16) {
    if (n.__tagPzInstance) return n
    n = n.parent
  }
  return null
}

// Invert the panzoom matrix (screen = board*scale + translate) to the visible
// board-space rect, padded by `margin` board-px on each side.
const _boardRectFromCamera = (t, vw, vh, margin) => {
  const s = t.scale || 1
  return {
    left: -t.x / s - margin,
    top: -t.y / s - margin,
    right: (vw - t.x) / s + margin,
    bottom: (vh - t.y) / s + margin
  }
}

// Ids whose uniform card box overlaps `rect`. Pure O(n) — no reactive reads,
// no per-node size lookup (see _WIN_NODE_*). `snap` is an id→{x,y} map.
const _teamIdsIntersectingRect = (snap, rect) => {
  const out = new Set()
  for (const id in snap) {
    const p = snap[id]
    // Fail-safe: a node with no known coords (never positioned → empty {} in
    // _tagPositions) has no box to test for overlap. NEVER drop it — always
    // include it, otherwise windowing permanently unmounts un-positioned cards
    // (they render at their computed layout slot but vanish once the recompute
    // takes over). Correctness over the tiny cost of always-mounting the few
    // cards that lack saved coords.
    if (!p || typeof p.x !== 'number' || typeof p.y !== 'number') {
      out.add(id)
      continue
    }
    if (
      p.x <= rect.right &&
      p.x + _WIN_NODE_W >= rect.left &&
      p.y <= rect.bottom &&
      p.y + _WIN_NODE_H >= rect.top
    ) {
      out.add(id)
    }
  }
  return out
}

// Cached position snapshot for the recompute. `_initialPositions` is O(n²)
// (bbox de-confliction), so it must NOT run per pan frame — rebuild only when
// the _tagPositions object identity OR the roster length changes. During a
// board pan _tagPositions is stable (only node drags rewrite it, and a node
// drag doesn't pan), so the snapshot stays cached across every pan frame.
// Stored on the page el.
const _windowPosSnapshot = (root, el) => {
  const posId = root?._tagPositions
  const rosterLen = (_visiblePeople(root)?.length || 0) + (_visibleAgents(root)?.length || 0)
  const cache = el.__tagPosSnapshot
  if (cache && cache.posId === posId && cache.rosterLen === rosterLen) return cache.snap
  const base = _initialPositions(root)
  // Every node in _tagPositions MUST be represented in the snap, or it's invisible
  // to _idsIntersectingRect and gets permanently dropped once windowing engages.
  // _initialPositions omits never-positioned nodes (empty {} coords), so union any
  // missing raw entry in; _idsIntersectingRect fail-safe-includes coord-less nodes
  // so they always render. Copy (don't mutate _initialPositions' return).
  const snap = base ? { ...base } : {}
  const rawPos = root?._tagPositions
  if (rawPos) for (const id in rawPos) if (!(id in snap)) snap[id] = rawPos[id] || {}
  el.__tagPosSnapshot = { posId, rosterLen, snap }
  return snap
}

// Steady-state window gate — reads ONLY `_tagVisibleIds` (never
// `_tagPositions`). Fail-open before the first recompute exists; the factory's
// first-paint path supplies a real seed set so we don't actually mount all 508
// in that window.
const _inWindow = (root, id) => {
  const vis = root?._tagVisibleIds
  return vis ? !!vis[id] : true
}

// First-paint seed: before any recompute has written `_tagVisibleIds`, window
// against the seed-camera rect ({ x:40, y:40, scale:1 } — the panzoom seed;
// the Stage isn't laid out yet so use window.inner*). Positions are read inside
// an UNTRACKED region so the factory effect never subscribes to `_tagPositions`
// (a subscription would remount every node per drag move). Cached on the
// container el and keyed by roster length, so it recomputes when nodes arrive
// (no "0 nodes" flash on cold load) but never per frame.
const _teamEnsureInitialWindow = (root, el) => {
  if (typeof window === 'undefined') return null
  const prev = getObserver()
  setObserver(null)
  try {
    const rosterLen = (_visiblePeople(root)?.length || 0) + (_visibleAgents(root)?.length || 0)
    if (el.__tagInitialWindow && el.__tagInitialWindowLen === rosterLen) {
      return el.__tagInitialWindow
    }
    const snap = _initialPositions(root)
    const rect = _boardRectFromCamera(
      { x: 40, y: 40, scale: 1 },
      window.innerWidth || 1280,
      window.innerHeight || 800,
      _TEAM_WINDOW_MOUNT_MARGIN
    )
    const set = _teamIdsIntersectingRect(snap, rect)
    el.__tagInitialWindow = set
    el.__tagInitialWindowLen = rosterLen
    return set
  } finally {
    setObserver(prev)
  }
}

// Filter mapped children to the viewport window. Reads `_tagVisibleIds` once
// (tracked → the factory re-runs when the recompute rewrites it); on the very
// first paint (no recompute yet) it windows against the untracked seed set
// instead of failing open to the whole roster.
const _teamWindowFilter = (root, el, nodes, idOf) => {
  // Steady state: the recompute has written `_tagVisibleIds` → gate on it
  // (reads ONLY that signal, never `_tagPositions`).
  if (root?._tagVisibleIds) return nodes.filter((n) => _inWindow(root, idOf(n)))
  // First paint (no recompute yet): window against the untracked seed set
  // instead of failing open to the whole roster.
  const seed = _teamEnsureInitialWindow(root, el)
  return seed ? nodes.filter((n) => seed.has(idOf(n))) : nodes
}

// rAF-coalesced recompute scheduler. Multiple triggers in one frame (pan tick +
// roster tick + drag release) collapse to a single run.
const scheduleTeamWindowRecompute = (el, root) => {
  if (!el || !root || typeof window === 'undefined') return
  if (el.__tagVizRaf) return
  el.__tagVizRaf = window.requestAnimationFrame(() => {
    el.__tagVizRaf = null
    _teamRunWindowRecompute(el, root)
  })
}

// Compute the new visible set from the live camera and, ONLY when membership
// actually changed, write it to `_tagVisibleIds`. The set-diff gate is what
// stops a fast pan from firing a root.update — and thus a reconcile — every
// frame. Always runs in an rAF (outside any reactive effect), so its reactive
// reads subscribe nothing.
const _teamRunWindowRecompute = (el, root) => {
  if (!el || !root || typeof window === 'undefined') return
  const pz = el.__tagPzInstance
  const stage = el.AppMain?.Body?.Stage?.node
  if (!pz || !stage || typeof pz.getTransform !== 'function') return
  let t
  try {
    t = pz.getTransform()
  } catch (_) {
    return
  }
  if (!t) return
  const vw = stage.clientWidth || stage.getBoundingClientRect().width
  const vh = stage.clientHeight || stage.getBoundingClientRect().height
  if (!vw || !vh) return

  const snap = _windowPosSnapshot(root, el)
  const newSet = _teamIdsIntersectingRect(
    snap,
    _boardRectFromCamera(t, vw, vh, _TEAM_WINDOW_MOUNT_MARGIN)
  )
  // Hysteresis — keep previously-visible nodes that still fall inside the wider
  // KEEP band so edge nodes don't thrash mount/unmount on small pans.
  const prev = el.__tagLastVisibleSet
  if (prev && prev.size) {
    const keepSet = _teamIdsIntersectingRect(
      snap,
      _boardRectFromCamera(t, vw, vh, _TEAM_WINDOW_KEEP_MARGIN)
    )
    for (const id of prev) if (keepSet.has(id)) newSet.add(id)
  }
  // Never unmount a node being dragged / linked from.
  for (const id of _pinnedIds) newSet.add(id)

  // Set-diff gate: identical membership → no root.update, no reconcile.
  const key = newSet.size ? [...newSet].sort().join('|') : ''
  if (key === el.__tagLastVisibleKey) return
  el.__tagLastVisibleKey = key
  el.__tagLastVisibleSet = newSet
  const map = {}
  for (const id of newSet) map[id] = 1
  root.update({ _tagVisibleIds: map })
  // Freshly-mounted nodes read their saved coords via _posFor at mount, but a
  // v3.14 reactive-layout freeze can leave them at the default grid slot;
  // re-apply positions imperatively (same pattern the snap poll uses).
  _applyPositionsToDom(root, el)
}

// Hydrate _tagPositions from the workspace-shared canvas layout AND
// the per-user prefs for the still-per-user fields. Idempotent: an
// empty server response leaves whatever the user already has on root.
const _hydrateLayoutFromPrefs = async (root, el) => {
  // Serialize hydrate passes — onCreate, the prefs-landed re-run AND
  // the wsId-landed re-run (both in onUpdate) can each kick this;
  // overlapping GETs would race each other's _tagPositions writes.
  if (el?._tagHydrateInFlight) return
  if (el) el._tagHydrateInFlight = true
  // 1) Per-user connection state from userPreferences — same shape as
  //    pre-migration. Read here so a legacy `positions` blob inside
  //    user prefs still seeds the canvas on the first load after the
  //    migration ships (treated as fallback only).
  const prefs = root?.userPrefs || {}
  const legacy = prefs[_PREFS_KEY] || {}
  const userOnly = {}
  if (Array.isArray(legacy.connections)) userOnly._tagConnections = legacy.connections
  if (Array.isArray(legacy.connectionsRemoved))
    userOnly._tagConnectionsRemoved = legacy.connectionsRemoved
  // Migrate legacy adhoc arrays into the studios-style flat map on
  // first hydration after the 2026-06-04 refactor — discarded after
  // the next save commits the new shape to canvas-layout.groups.
  if (Array.isArray(legacy.adHocGroups) && legacy.adHocGroups.length) {
    const map = {}
    const names = {}
    let slotIdx = 0
    for (const g of legacy.adHocGroups) {
      const memberIds = Array.isArray(g?.memberIds) ? g.memberIds : []
      if (memberIds.length < MIN_GROUP_MEMBERS) continue
      const slot = GROUP_SLOTS[slotIdx++] || 'group' + slotIdx
      for (const id of memberIds) map[id] = slot
      if (g.label) names[slot] = g.label
    }
    if (Object.keys(map).length) {
      userOnly._tagGroupMap = map
      if (Object.keys(names).length) userOnly._tagGroupNames = names
    }
  }
  if (Object.keys(userOnly).length) root?.update?.(userOnly)

  // 2) Workspace-shared positions from sdk.canvasLayout. Fall back to
  //    the legacy per-user positions on miss/error so a tenant whose
  //    server worker hasn't deployed the workspace-shared schema yet
  //    still sees their prior drags.
  const wsId = _wsId(root)
  const sdk = getSDK()
  let serverPositions = null
  let serverGroups = null
  let attempted = false
  let getSucceeded = false
  if (sdk?.getCanvasLayout && wsId) {
    attempted = true
    try {
      const layout = await sdk.getCanvasLayout(wsId)
      getSucceeded = true
      const strippedPos = _unprefixPositions(layout?.positions)
      if (Object.keys(strippedPos).length) serverPositions = strippedPos
      // Groups shape on canvas-layout: { [prefixedNodeId]:
      // { key, name? } }. Reconstruct the flat
      // `_tagGroupMap` + `_tagGroupNames` shape from it.
      const strippedGroups = _unprefixGroups(layout?.groups)
      if (strippedGroups) serverGroups = strippedGroups
      if (layout?.version != null) root.__tagLayoutVersion = layout.version
    } catch (err) {
      console.warn('[team-and-agents] getCanvasLayout failed:', err?.message || err)
    }
  }
  // Remember which workspace this pass ran for — onUpdate re-runs the
  // hydrate when the resolvable wsId differs (the onCreate pass can
  // fire before jwtClaims/workspace land, skipping the GET entirely;
  // without this re-run the saved layout never loads and the canvas
  // stays on the default grid for the whole session). Recorded even
  // when the GET path is unavailable so onUpdate doesn't re-kick on
  // every tick.
  if (el && wsId) el._tagLayoutWsLoaded = wsId
  if (serverPositions) {
    // Merge UNDER any in-session local drags — `_tagPositions` only
    // ever holds persisted coords + this session's user drags now (the
    // drag handler no longer bakes the default grid in), so local keys
    // are always fresher user intent than the GET snapshot. A plain
    // replace here clobbered a drag made while the GET was in flight.
    root?.update?.({ _tagPositions: { ...serverPositions, ...(root._tagPositions || {}) } })
  } else if (legacy.positions && typeof legacy.positions === 'object') {
    root?.update?.({ _tagPositions: { ...legacy.positions, ...(root._tagPositions || {}) } })
  }
  if (serverGroups) {
    root?.update?.({
      _tagGroupMap: serverGroups.map,
      _tagGroupNames: serverGroups.names
    })
  }
  // Team grouping (2026-06-06, per Nika): humans are grouped by their
  // REAL teams on every hydrate — fresh canvases get the full seed,
  // and saved canvases gain frames for team members nobody grouped
  // manually (_mergeTeamSeed never moves a node out of a slot the
  // user placed it in). Subsequent drags persist the result back to
  // canvas-layout for everyone.
  const teamMerged = _mergeTeamSeed(root)
  if (teamMerged) {
    root?.update?.({
      _tagGroupMap: teamMerged.map,
      _tagGroupNames: teamMerged.names
    })
  }
  // Flag so the onUpdate save-gate can arm itself off this signal
  // (the legacy gate keyed on userPrefs which may never land for a
  // workspace whose only persistence path is canvas-layout). Without
  // this the save NEVER fires — verified 2026-06-04: 71 default
  // positions in _tagPositions but workspace layout stayed empty.
  //
  // Only set once the canvas-layout GET was actually ATTEMPTED (wsId
  // resolved) or there is genuinely nothing to load through (no SDK
  // method). A pass that skipped the GET because wsId hadn't landed
  // yet must NOT arm the save-gate — saves fired before the first
  // real load completes are exactly the race that persisted default
  // positions over the saved layout.
  //
  // Arm the save-gate ONLY after a SUCCESSFUL load (or when there's genuinely
  // no GET path) — NEVER after a FAILED GET (e.g. the backend was restarting,
  // which is exactly how "humans positions failed to load" happens). Arming
  // on a failed attempt let the canvas persist its fallback/default coords
  // over the real, still-unloaded layout.
  if (el && (getSucceeded || !sdk?.getCanvasLayout)) el._tagLayoutHydrated = true
  // A transient backend outage makes the GET fail; the wsId-gate in onUpdate
  // stays satisfied (we recorded _tagLayoutWsLoaded above), so without an
  // explicit retry the canvas would sit on fallback coords for the WHOLE
  // session — saved positions never load even after the server recovers.
  // Schedule a backoff retry so the layout self-heals (capped to avoid a
  // storm; reset on success). The in-flight guard at the top of this fn
  // serializes it against any onUpdate-driven pass.
  if (el && attempted && !getSucceeded) {
    el._tagLayoutRetries = (el._tagLayoutRetries || 0) + 1
    if (el._tagLayoutRetries <= 6 && typeof window !== 'undefined') {
      const delay = Math.min(10000, Math.round(800 * Math.pow(1.7, el._tagLayoutRetries - 1)))
      clearTimeout(el._tagLayoutRetryTimer)
      el._tagLayoutRetryTimer = window.setTimeout(() => {
        _hydrateLayoutFromPrefs(root, el)
      }, delay)
    }
  } else if (getSucceeded) {
    el._tagLayoutRetries = 0
  }
  // Snap already-mounted nodes to the freshly-resolved coordinates (see
  // _applyPositionsToDom — covers the mount-before-load ordering for
  // positions AND the default-grid reshuffle a group seed/merge causes).
  if (el) _applyPositionsToDom(root, el)
  if (el) el._tagHydrateInFlight = false
}

// Debounced workspace-layout patch. Drag ticks rapidly mutate
// _tagPositions; coalesce 600ms of writes into one PATCH so the
// canvas-layout endpoint sees O(1) calls per gesture instead of N.
const _canvasSaveTimers = new WeakMap()
// Tracks the active document-level drag listeners keyed off root so the
// page's onRemove can unwind them when the user navigates away mid-drag.
// onUp clears its own entry on natural release; only the unmount path
// needs this WeakMap.
const _activeDrag = new WeakMap()

const _prefixPositions = (positions) => {
  const out = {}
  if (!positions || typeof positions !== 'object') return out
  for (const [k, v] of Object.entries(positions)) {
    if (v && typeof v === 'object' && typeof v.x === 'number' && typeof v.y === 'number') {
      out[_TA_PREFIX + k] = { x: v.x, y: v.y }
    }
  }
  return out
}

const _saveLayoutToPrefs = (el, root) => {
  if (!root) return
  clearTimeout(_canvasSaveTimers.get(root))
  _canvasSaveTimers.set(
    root,
    setTimeout(async () => {
      const wsId = _wsId(root)
      const sdk = getSDK()

      // Per-user connection state still rides userPreferences. Empty
      // arrays write through so members can also REMOVE a base edge and
      // have the removal persist across reloads.
      const prefs = root.userPrefs || {}
      const layoutBag = {
        // positions: kept in legacy slot too — graceful fallback for
        // any client that hasn't shipped the canvas-layout migration
        // (older builds reading user prefs still see the positions).
        positions: root._tagPositions || {},
        connections: Array.isArray(root._tagConnections) ? root._tagConnections : [],
        connectionsRemoved: Array.isArray(root._tagConnectionsRemoved)
          ? root._tagConnectionsRemoved
          : []
        // adHocGroups field retired 2026-06-04 — groups live in the
        // workspace-shared canvas-layout document now (studios shape:
        // groups[nodeId] = { key, name? }).
      }
      root.update({ userPrefs: { ...prefs, [_PREFS_KEY]: layoutBag } })
      try {
        if (sdk?.execute) {
          // PER-PATH PATCH — names only `team_and_agents_layout`.  `user_id`
          // is server-stamped and never client-sent: a PATCH REFUSES a
          // reserved key outright (reserved_pref_key), where the old whole-
          // row PUT silently stripped it (CORE-USERPREFS-PATCH-PER-PATH-1).
          await sdk.execute('workspaceProject.userPreferences', 'patch', {
            patch: { set: { [_PREFS_KEY]: layoutBag } }
          })
        }
      } catch (err) {
        console.warn('[team-and-agents] user-prefs save failed:', err?.message || err)
      }

      // Workspace-shared positions + groups. Send only the namespaced
      // subset so /studios + /my-agents project/agent keys in the same
      // document aren't touched. expectedVersion guards concurrent
      // edits — 409 re-queues the next debounce write with the freshest
      // local map.
      if (sdk?.patchCanvasLayout && wsId) {
        const partial = {
          positions: _prefixPositions(root._tagPositions || {}),
          groups: _prefixGroups(root._tagGroupMap, root._tagGroupNames)
        }
        if (root.__tagLayoutVersion != null) partial.expectedVersion = root.__tagLayoutVersion
        try {
          const result = await sdk.patchCanvasLayout(wsId, partial)
          if (result?.version != null) root.__tagLayoutVersion = result.version
        } catch (err) {
          // 409 conflict → drop the stale version so the next debounce
          // doesn't include it; the live socket sub will pull in the
          // peer's writes between now and then.
          if (String(err?.message || '').includes('409')) root.__tagLayoutVersion = null
          console.warn('[team-and-agents] patchCanvasLayout failed:', err?.message || err)
        }
      }
    }, 600)
  )
}

// Subscribe to live canvas-layout broadcasts so a drag in one tab /
// member fans out to every other member's canvas without a refresh.
// Returns a Promise<unsubscribe>; the caller is responsible for
// awaiting + storing the resolved function so onRemove can release
// the socket cleanly.
//
// Why the Promise:
// `getSDK()` returns a Proxy that defers EVERY method to
// `initPromise.then(() => value.apply(target, args))` (see
// shared/functions/sdk.js wrapWithInitAwait). So the immediate return
// of `sdk.subscribeWorkspaceCanvasLayout(...)` is a Promise wrapping
// the actual unsub. Without awaiting we'd store a Promise as the
// unsub and onRemove's `if (typeof fn === 'function')` would skip
// teardown — the socket leaks for the lifetime of the tab, and a
// re-mount would open a second one.
const _subscribeCanvasLayout = async (root, el) => {
  const wsId = _wsId(root)
  if (!wsId) return null
  const sdk = getSDK()
  if (!sdk?.subscribeWorkspaceCanvasLayout) return null
  return sdk.subscribeWorkspaceCanvasLayout(wsId, (payload) => {
    const next = {}
    if (payload?.positions) {
      const incomingPos = _unprefixPositions(payload.positions)
      if (Object.keys(incomingPos).length) {
        next._tagPositions = { ...(root._tagPositions || {}), ...incomingPos }
      }
    }
    if (payload?.groups) {
      const incomingGroups = _unprefixGroups(payload.groups)
      if (incomingGroups) {
        // Replace, don't merge — a member dragged OUT of a group on
        // another tab shouldn't come back via stale local map state.
        next._tagGroupMap = incomingGroups.map
        next._tagGroupNames = incomingGroups.names
      }
    }
    if (Object.keys(next).length) {
      root?.update?.(next)
      // Same mount-vs-update freeze guard as the hydrate path — snap
      // mounted nodes to the incoming coords imperatively.
      if (el) _applyPositionsToDom(root, el)
    }
    if (payload?.version != null) root.__tagLayoutVersion = payload.version
  })
}

// ─── Connection click-to-remove ─────────────────────────────────
//
// Default + user-added edges are merged in `_baseConnections` /
// `_allConnections`. Removing a default edge writes a negative entry
// into `root._tagConnectionsRemoved` so the merge keeps it out across
// reloads.

const _connectionKey = (c) => `${c?.from || ''}→${c?.to || ''}`

const _isUserConnection = (root, c) => {
  const user = Array.isArray(root?._tagConnections) ? root._tagConnections : []
  return user.some((u) => _connectionKey(u) === _connectionKey(c))
}

const _removeConnection = (root, c) => {
  if (!root || !c) return
  const key = _connectionKey(c)
  const user = Array.isArray(root._tagConnections) ? root._tagConnections : []
  const removed = Array.isArray(root._tagConnectionsRemoved) ? root._tagConnectionsRemoved : []
  // User-added edge → just drop from the user list.
  if (user.some((u) => _connectionKey(u) === key)) {
    root.update({
      _tagConnections: user.filter((u) => _connectionKey(u) !== key),
      _tagSelectedConnection: null
    })
    return
  }
  // Default edge → record a removal entry so `_allConnections` filters
  // it out on every render.
  if (!removed.some((r) => _connectionKey(r) === key)) {
    root.update({
      _tagConnectionsRemoved: [...removed, { from: c.from, to: c.to }],
      _tagSelectedConnection: null
    })
  }
}

const _onNodePointerdown = (e, el, s) => {
  // Reset the drag-suppress flag at the START of every press so an
  // earlier drag doesn't permanently swallow clicks on subsequent
  // taps. Click handler will set it back to true via the moved-guard
  // below if the user actually drags.
  _justDragged = false

  // Drag — only kicks in when the user isn't in connect-mode. POINTER events
  // (not mouse), so a finger drags a node on iPad too. Panzoom's beforeMouseDown
  // gates the desktop mouse pan on `data-node-id`, and initPanzoomBoard's touch
  // guard pauses panzoom on a single-finger node touch, so the board never pans
  // while we drag a node with either input.
  if (s.root._tagConnectMode) return
  const id = el.node?.dataset?.nodeId
  if (!id) return
  e.stopPropagation()
  // Pin the dragged node into the viewport window so a pan-driven (or the
  // release) recompute never unmounts it mid-drag. Released in onUp.
  _pinnedIds.add(id)
  // Track the initiating pointer so a second finger landing mid-drag can't
  // hijack the move (mouse always reports pointerId 1).
  const pointerId = e.pointerId

  const root = s.root
  // Read view: _initialPositions is the COMPLETE map (default grid with
  // persisted coords overlaid) — never the sparse persisted map alone,
  // which misses ids that haven't been dragged yet (start = {0,0} bug).
  const positions = _initialPositions(root)
  const start = positions[id] || { x: 0, y: 0 }
  const startX = e.clientX
  const startY = e.clientY
  const scale = (typeof window !== 'undefined' && window.__tagPzScale) || 1
  let moved = false

  // People (not agents) can join team groups by dropping onto a frame.
  // Track which frame the cursor is currently over so the frame can
  // visually highlight (`root._tagHoverGroup`) and so onUp can mutate
  // the team membership on drop.
  const isAgent = _canvasIsAgent(id, root)
  const nodeW = isAgent ? AGENT_W : PERSON_W
  const nodeH = isAgent ? AGENT_H : PERSON_H

  const onMove = (ev) => {
    if (ev.pointerId !== pointerId) return
    const dx = (ev.clientX - startX) / scale
    const dy = (ev.clientY - startY) / scale
    if (Math.abs(dx) + Math.abs(dy) > 3) moved = true
    // Write ONLY the dragged node's coordinate into the persisted map.
    // The old `{ ...positions, [id]: … }` spread baked the ENTIRE
    // default grid into _tagPositions on the first drag of a session —
    // and because _saveLayoutToPrefs PATCHes every key it holds (the
    // server partial-merges per node id), one early drag (before the
    // async canvas-layout GET resolved) permanently overwrote every
    // member's SAVED position with the default grid slot. That is the
    // root cause of "humans render at default positions on first load":
    // the saved layout itself had been replaced by defaults. Un-dragged
    // nodes keep resolving through _posFor → _initialPositions, so
    // nothing visual changes — only what gets persisted does.
    const next = { ...(root._tagPositions || {}), [id]: { x: start.x + dx, y: start.y + dy } }
    root.update({ _tagPositions: next })
    if (!isAgent) {
      const hover = _hoveredGroup(el, root, id, nodeW, nodeH)
      if (hover !== root._tagHoverGroup) root.update({ _tagHoverGroup: hover })
      // Node-over-node detection (studios collision = new group).
      // Only when no team-frame is hovered — frame join takes
      // precedence so users dragging INTO a team frame don't
      // accidentally form an ad-hoc group with the closest member.
      const hoverNode = hover ? null : _hoveredNode(el, root, id, nodeW, nodeH)
      if (hoverNode !== root._tagHoverNode) root.update({ _tagHoverNode: hoverNode })
    }
  }
  const entry = {}
  const onUp = (ev) => {
    if (ev && ev.pointerId !== pointerId) return
    document.removeEventListener('pointermove', onMove)
    document.removeEventListener('pointerup', onUp)
    document.removeEventListener('pointercancel', onUp)
    if (_activeDrag.get(root) === entry) _activeDrag.delete(root)
    // Drag settled — release the pin and re-window against the final camera +
    // positions (the dragged node may have moved out of / into the viewport).
    _pinnedIds.delete(id)
    const pageEl = _tagPageElOf(el)
    if (pageEl) scheduleTeamWindowRecompute(pageEl, root)
    const hoverGroup = root._tagHoverGroup
    const hoverNode = root._tagHoverNode
    if (hoverGroup) root.update({ _tagHoverGroup: null })
    if (hoverNode) root.update({ _tagHoverNode: null })
    if (moved) {
      _justDragged = true
      _saveLayoutToPrefs(el, root)
      if (!isAgent) {
        const fromGroupId = _groupFor({ id, email: _canvasEmailOf(root, id) }, root)
        // Priority: drop on a frame → join that slot. Drop on another
        // free node → form (or extend) a slot. Drop in empty space
        // while currently in a slot → leave that slot. The studios
        // model is purely visual — slot membership lives in
        // `_tagGroupMap`. Team-membership SDK round-trips piggyback
        // off the slot transitions when the slot was seeded from a
        // team row (looked up via _teamForSlot / _teamForGroupId).
        const currentSlot = _currentGroupOf(root, id)
        if (hoverGroup && currentSlot !== hoverGroup) {
          const map = { ..._groupMap(root), [id]: hoverGroup }
          root.update({ _tagGroupMap: map })
          // Team membership side-effect — only when both slots resolve
          // back to teams the SDK knows about.
          if (!isAgent) {
            const toTeamId = _teamForGroupId(
              root,
              currentSlot ? `team:${_slotLabel(root, hoverGroup)}` : null
            )
            if (toTeamId) _joinTeamOnDrop(el, root, id, currentSlot, hoverGroup)
          }
        } else if (hoverNode) {
          _formGroupOnDrop(root, id, hoverNode)
        } else if (!hoverGroup && currentSlot) {
          // Out of a team's frame → that team loses the member, after the
          // house confirm (Cancel puts the card back). Any other slot is
          // layout only and leaves at once.
          const team = _leavableTeam(root, id, currentSlot)
          if (team) _askLeaveTeamOnDrop(el, root, id, currentSlot, team, start)
          else _removeFromSlot(root, id)
        }
      }
    }
  }
  document.addEventListener('pointermove', onMove)
  document.addEventListener('pointerup', onUp)
  document.addEventListener('pointercancel', onUp)
  entry.onMove = onMove
  entry.onUp = onUp
  _activeDrag.set(root, entry)
}

// ─── Drag-to-assign (studios-style link handle) ───────────────
//
// Mirrors canvas/pages/studios.js `startLink`: press the small handle on
// a node's right edge, drag onto another node, release to add a
// { from, to } edge into root._tagConnections (the assignment graph that
// _allConnections renders + _saveLayoutToPrefs persists). This is the
// primary "assign items to each other" gesture now that the Suggest-team
// panel is gone — the same affordance /studios uses to wire projects to
// integrations.

// Center point (board coords) of the link-drag source node — feeds the
// LinkGhost path while a drag is in flight.
const _linkGhostFrom = (root) => {
  const lk = root?._tagLinking
  if (!lk?.fromId) return { x: 0, y: 0 }
  const positions = { ...(root?._tagPositions || {}), ...(_initialPositions(root) || {}) }
  const p = positions[lk.fromId]
  if (!p) return { x: 0, y: 0 }
  const sz = _sizeOf(lk.fromId, root)
  return { x: p.x + sz.w / 2, y: p.y + sz.h / 2 }
}

// Topmost node under the cursor (excluding the drag source).
// elementsFromPoint walks the z-stack so the pointerEvents:none ghost /
// dots layers don't mask the card beneath.
const _nodeIdAtPoint = (clientX, clientY, fromId) => {
  if (typeof document === 'undefined') return null
  const stack = document.elementsFromPoint(clientX, clientY)
  for (const node of stack) {
    const host = node.closest && node.closest('[data-node-id]')
    if (host && host.dataset.nodeId && host.dataset.nodeId !== fromId) {
      return host.dataset.nodeId
    }
  }
  return null
}

const _startLink = (e, el, root, fromId) => {
  if (e.button !== 0 || !fromId) return
  // Pin the link source so a recompute can't unmount it while we drag a
  // connection from it. Released in onUp.
  _pinnedIds.add(fromId)
  // Swallow the event so the node's own mousedown (position drag) and the
  // panzoom pan don't also fire.
  e.stopPropagation()
  e.preventDefault()

  // Resolve THIS board's rect + scale so the ghost tracks the cursor in
  // board coordinates. el.node.closest finds the correct Board even when
  // the home dashboard embeds a second canvas instance.
  const board = el.node && el.node.closest('[data-tag-board]')
  const toBoardPoint = (clientX, clientY) => {
    if (!board) return { x: 0, y: 0 }
    const r = board.getBoundingClientRect()
    const scale = (typeof window !== 'undefined' && window.__tagPzScale) || 1
    return { x: (clientX - r.left) / scale, y: (clientY - r.top) / scale }
  }

  const seed = toBoardPoint(e.clientX, e.clientY)
  root.update({
    _tagLinking: { fromId, toId: null, x: seed.x, y: seed.y },
    _tagConnectFrom: fromId
  })

  const onMove = (ev) => {
    const p = toBoardPoint(ev.clientX, ev.clientY)
    const toId = _nodeIdAtPoint(ev.clientX, ev.clientY, fromId)
    root.update({
      _tagLinking: { fromId, toId, x: p.x, y: p.y },
      _tagHoverNode: toId || null
    })
  }
  const onUp = (ev) => {
    document.removeEventListener('mousemove', onMove)
    document.removeEventListener('mouseup', onUp)
    // Release the link-source pin and re-window (a node may have entered /
    // left the viewport, and the source no longer needs to stay mounted).
    _pinnedIds.delete(fromId)
    const pageEl = _tagPageElOf(el)
    if (pageEl) scheduleTeamWindowRecompute(pageEl, root)
    const toId = _nodeIdAtPoint(ev.clientX, ev.clientY, fromId)
    if (toId && toId !== fromId) {
      const key = _connectionKey({ from: fromId, to: toId })
      const revKey = _connectionKey({ from: toId, to: fromId })
      const already = _allConnections(root).some((c) => {
        const k = _connectionKey(c)
        return k === key || k === revKey
      })
      if (!already) {
        // Agent⇄person pair → a real, server-backed assignment. Resolve
        // the agent's User _id (from the roster) + the person's User _id
        // (from root.people). When both resolve, write the assignment
        // through the SDK; the edge then renders from _assignmentConnections.
        const fromIsAgent = _canvasIsAgent(fromId, root)
        const toIsAgent = _canvasIsAgent(toId, root)
        const agentNode = fromIsAgent ? fromId : toIsAgent ? toId : null
        const personNode = fromIsAgent ? (toIsAgent ? null : toId) : toIsAgent ? fromId : null
        const agent = agentNode && _agents(root).find((a) => a.id === agentNode)
        const person = personNode && _personById(root, personNode)
        const agentUserId = agent && agent.userId
        const assigneeUserId = person && person.userId
        if (agentNode && personNode && agentUserId && assigneeUserId) {
          const orgId =
            root?.jwtClaims?.workspace_org_id ||
            (root?.jwtClaims?.orgs && root.jwtClaims.orgs[0]?.id) ||
            null
          const who =
            person.name ||
            (person.email || '').split('@')[0] ||
            el.call('polyglot', 'canvas.team.toast.memberFallback')
          el.call('assignAgentToUser', root, { orgId, agentUserId, assigneeUserId })
            .then(() =>
              root.update({
                _tagToast: {
                  kind: 'ok',
                  text: el
                    .call('polyglot', 'canvas.team.toast.assigned')
                    .replace('{agent}', agent.name || agentNode)
                    .replace('{person}', who)
                }
              })
            )
            .catch((err) =>
              root.update({
                _tagToast: {
                  kind: 'error',
                  text: (err && err.message) || 'canvas.team.toast.assignFailed'
                }
              })
            )
          root.update({ _tagLinking: null, _tagConnectFrom: null, _tagHoverNode: null })
          return
        }
        // Fallback: local visual edge (agent⇄agent, person⇄person, or an
        // endpoint with no Mongo user id to assign server-side).
        const user = Array.isArray(root._tagConnections) ? root._tagConnections : []
        const removed = Array.isArray(root._tagConnectionsRemoved)
          ? root._tagConnectionsRemoved
          : []
        // Drop any prior removal of this pair so the freshly-drawn edge sticks.
        const nextRemoved = removed.filter((r) => {
          const k = _connectionKey(r)
          return k !== key && k !== revKey
        })
        root.update({
          _tagConnections: [...user, { from: fromId, to: toId }],
          _tagConnectionsRemoved: nextRemoved
        })
        _saveLayoutToPrefs(el, root)
      }
    }
    root.update({ _tagLinking: null, _tagConnectFrom: null, _tagHoverNode: null })
  }
  document.addEventListener('mousemove', onMove)
  document.addEventListener('mouseup', onUp)
}

// Shared link handle added to every node (person + agent). Hidden until
// the card is hovered — the reveal rule lives on each node
// (':hover > [data-tag-linkhandle]'). Factory (not a shared object) so the
// two nodes don't collide on one DOMQL element reference.
const _linkHandle = () => ({
  attr: { 'data-tag-linkhandle': '' },
  position: 'absolute',
  right: '-9px',
  top: '50%',
  marginTop: '-9px',
  width: '18px',
  height: '18px',
  round: '50%',
  background: 'white',
  borderWidth: '2px',
  borderStyle: 'solid',
  borderColor: 'document',
  cursor: 'crosshair',
  opacity: '0',
  transform: 'scale(0.6)',
  transition: 'A opacity defaultBezier, A transform defaultBezier',
  zIndex: '4',
  title: (el) => el.call('polyglot', 'canvas.team.linkHandle.title'),
  ':hover': { transform: 'scale(1.3)' },
  // Node drag starts on pointerdown (_onNodePointerdown); stop it bubbling to
  // the node so pressing the link handle draws a connection (mouse-only)
  // instead of ALSO starting a node-reposition drag. Without this guard the
  // two drags race — the card follows the cursor and the link never lands
  // (the studios link handle ships the same guard; see studios.js LinkHandle).
  onPointerdown: (e) => e.stopPropagation(),
  onMousedown: (e, el, s) => _startLink(e, el, s.root, el.parent?.node?.dataset?.nodeId),
  // A click on the handle shouldn't open the node's detail modal.
  onClick: (e) => {
    e.preventDefault()
    e.stopPropagation()
  }
})

// ─── Fit-to-visible (the /my-agents zoom-in + home embed) ─────
//
// The canvas's node positions are the SHARED full-org-map coordinates and
// the camera only ever seeds the top-left origin. Two surfaces need that
// framed instead of left at the origin:
//   • /my-agents (non-admin) — the viewer's filtered subset keeps its
//     full-map coords, so the handful of nodes land scattered, usually
//     fully off-screen (the "blank /my-agents" a team member saw).
//   • the home AgentsWidget embed — the full roster rendered into the
//     small widget showed only the top-left cards; the rest overflowed.
// `_fitMode` decides which (the dedicated /team-and-agents route opts out,
// keeping its free pan). We frame the relevant node set: its bbox via the
// shared `bboxOfNodeIds` helper, then a pan/zoom that centres it. The
// MIN_SCALE floor means the wide+short home widget lands on a readable,
// centred band of the roster (pan for the rest) rather than a postage-stamp
// fit-everything. Driven by the bounded poll in onCreate (the reactive
// onUpdate does NOT reliably tick for this page element); one-shot per mount
// (`el.__tagFitted`) so a later drag doesn't yank the camera away.
const _FIT_PAD = 140 // scene-px breathing room around the set
const _FIT_MAX_SCALE = 1 // never zoom past 100% (e.g. a lone self-node)
const _FIT_MIN_SCALE = 0.25 // floor: keep cards readable; pan for the rest

// Which surface (if any) wants an auto-fit, and over which node set:
//   'subset' — /my-agents non-admin → the viewer's filtered nodes
//   'all'    — the home embed (canvas mounted off its dedicated routes)
//   null     — /team-and-agents (+ admin /my-agents): free pan, no fit
const _fitMode = (root) => {
  if (!root) return null
  // /agents — fit the camera to the (filtered) agent set.
  if (root._canvasAgentsOnly) return 'subset'
  if (root._canvasScope === 'mine') return _viewerTier(root) === 'admin' ? null : 'subset'
  const path = root.currentPath || root.route || ''
  if (path !== '/team-and-agents' && path !== '/my-agents') return 'all'
  return null
}

// The node ids to frame. `_visiblePeople` / `_visibleAgents` already return
// the filtered subset under `_canvasScope === 'mine'` and the full roster
// otherwise, so this is the right set for BOTH the subset and 'all' modes.
const _fitNodeIds = (root) => {
  const ids = new Set()
  const people = _visiblePeople(root)
  for (let i = 0; i < people.length; i++) ids.add(_personId(people[i], i))
  for (const a of _visibleAgents(root)) ids.add(a.id)
  return ids
}

const _fitToVisible = (root, el) => {
  if (!root || el.__tagFitted) return
  if (!_fitMode(root)) return
  const ids = _fitNodeIds(root)
  if (!ids || !ids.size) return
  const scene = el.AppMain?.Body?.Stage?.Board?.node
  const stage = el.AppMain?.Body?.Stage?.node
  // el.__tagPzInstance is the live instance initPanzoomBoard returned.
  // NB: window.__canvasGlobalScope.panzoomInstance can be a STALE instance
  // left by a prior canvas page (its transform doesn't drive this Board),
  // so never reach for it here. Not mounted yet → bail; the poll retries.
  const pz = el.__tagPzInstance || scene?.__tagPanzoomAttached
  if (!pz || !scene || !stage) return
  const positions = { ...(_initialPositions(root) || {}), ...(root._tagPositions || {}) }
  const bbox = el.call('bboxOfNodeIds', { ids: [...ids], positions, sizeOf: _sizeOfBound(root) })
  if (!bbox || !bbox.w || !bbox.h) return
  const vw = stage.clientWidth || stage.getBoundingClientRect().width
  const vh = stage.clientHeight || stage.getBoundingClientRect().height
  if (!vw || !vh) return
  const scale = Math.max(
    _FIT_MIN_SCALE,
    Math.min(_FIT_MAX_SCALE, vw / (bbox.w + _FIT_PAD * 2), vh / (bbox.h + _FIT_PAD * 2))
  )
  // panzoom maps scene→screen as `scene * scale + translate`; solve the
  // translate that lands the bbox centre on the viewport centre. Same
  // zoomAbs(0,0) + moveTo pairing initPanzoomBoard uses to seed the view.
  const cx = bbox.x + bbox.w / 2
  const cy = bbox.y + bbox.h / 2
  try {
    pz.zoomAbs(0, 0, scale)
    pz.moveTo(vw / 2 - cx * scale, vh / 2 - cy * scale)
    // Force the transform to the DOM — the home embed's panzoom doesn't fire
    // the `transform` event that normally flushes it, so moveTo alone updates
    // the instance but leaves the board pinned at the seed (see
    // initPanzoomBoard). Redundant-but-harmless on the routes.
    if (typeof pz.applyBoardTransform === 'function') pz.applyBoardTransform()
  } catch (_) {
    return
  }
  el.__tagFitted = true
  // Window to the freshly-fitted camera. On the home embed the panzoom
  // `transform` event never fires, so this (and the applyBoardTransform →
  // onTransform bridge in initPanzoomBoard) are the reliable recompute
  // triggers there.
  scheduleTeamWindowRecompute(el, root)
}

const _scheduleTeamPanzoomBoot = (el) => {
  if (typeof window === 'undefined' || el.__tagBooted || el.__tagPzRaf) return
  const attempts = (el.__tagPzAttempts = (el.__tagPzAttempts || 0) + 1)
  if (attempts > 120) {
    console.warn('[team-and-agents] panzoom boot gave up waiting for Board')
    return
  }
  el.__tagPzRaf = requestAnimationFrame(() => {
    el.__tagPzRaf = null
    _bootTeamPanzoom(el)
  })
}

const _bootTeamPanzoom = (el) => {
  if (el.__tagBooted || typeof window === 'undefined') return
  const scene = el.AppMain?.Body?.Stage?.Board?.node
  const dots = el.AppMain?.Body?.Stage?.Dots?.node
  if (!scene || !dots) {
    _scheduleTeamPanzoomBoot(el)
    return
  }

  el.__tagBooted = true
  // Reuse the shared panzoom boot helper via the function registry
  // (NOT a direct import — same reuse model the rest of the canvas
  // pages follow). Cancel token lets onRemove halt the RAF retry
  // chain if the page unmounts before the scene mounts. The returned
  // pz instance is captured for dispose-on-unmount.
  el.__tagPzCancel = { canceled: false }
  el.__tagPzInstance = el.call('initPanzoomBoard', {
    getScene: () => scene,
    getDots: () => dots,
    initial: { x: 40, y: 40, scale: 1 },
    attachKey: '__tagPanzoomAttached',
    cancelToken: el.__tagPzCancel,
    onTransform: (t) => {
      if (typeof window === 'undefined') return
      window.__tagPzScale = t.scale
      // Mirror into the shared canvas globalScope so the shell Zoombar
      // can read/drive this canvas — same pattern as /tickets (#32).
      const gs = (window.__canvasGlobalScope = window.__canvasGlobalScope || {})
      gs.positions = gs.positions || { x: 0, y: 0 }
      gs.positions.x = t.x
      gs.positions.y = t.y
      gs.scale = t.scale
      // Viewport windowing: the camera moved → recompute which nodes intersect
      // it. rAF-coalesced + set-diff-gated inside, so a fast pan costs at most
      // one root.update per membership change and none when it's unchanged.
      const winRoot = _tagRootOf(el)
      if (winRoot) scheduleTeamWindowRecompute(el, winRoot)
    }
  })
  if (!el.__tagPzInstance) {
    el.__tagBooted = false
    _scheduleTeamPanzoomBoot(el)
    return
  }
  el.__tagPzAttempts = 0
  const gs = (window.__canvasGlobalScope = window.__canvasGlobalScope || {})
  gs.panzoomInstance = el.__tagPzInstance
  gs.positions = gs.positions || { x: 0, y: 0 }
  // Seed the initial viewport window now the camera exists — hands the
  // first-paint seed set off to the reactive _tagVisibleIds without waiting
  // for the user's first pan.
  const bootRoot = _tagRootOf(el)
  if (bootRoot) scheduleTeamWindowRecompute(el, bootRoot)
}

// ─── /agents filter toolbar helpers ───────────────────────────
// The toolbar is inline in the Stage (not a registered component) to dodge the
// canvas childExtends-bridge requirement; these module helpers back it.
// `label` on every option below is a bundle KEY, resolved through polyglot at
// each render site — a `{{ … }}` template cannot be used because the value is
// also returned from a `text:` FUNCTION (`_agentOptLabel`), where a template
// string would render its own braces.
const _AGENT_STATUS_OPTS = [
  { key: 'all', label: 'canvas.team.filter.statusAll' },
  { key: 'active', label: 'canvas.team.filter.statusActive' },
  { key: 'idle', label: 'canvas.team.filter.statusIdle' }
]
const _AGENT_ASSIGN_OPTS = [
  { key: 'all', label: 'canvas.team.filter.assignAll' },
  { key: 'assigned', label: 'canvas.team.filter.assignAssigned' },
  { key: 'unassigned', label: 'canvas.team.filter.assignUnassigned' }
]
// Type options are derived from the kinds actually present in the roster.
const _agentTypeOpts = (root) => {
  const kinds = [
    ...new Set(
      _agents(root)
        .map((a) => a.kind)
        .filter(Boolean)
    )
  ].sort()
  return [
    { key: 'all', label: 'canvas.team.filter.typeAll' },
    // Derived from roster `kind` slugs — data, not copy. polyglot returns a
    // non-key verbatim, so these pass straight through.
    ...kinds.map((k) => ({ key: k, label: k.charAt(0).toUpperCase() + k.slice(1) }))
  ]
}
const _agentOptLabel = (opts, key) => (opts.find((o) => o.key === (key || 'all')) || opts[0]).label
// The option list a filter row names by its scope key ('status' / 'type' /
// 'assign'): a top-level helper, the path `_agentOptLabel` takes through frank.
const _agentFilterOpts = (key, root) =>
  key === 'type' ? _agentTypeOpts(root) : key === 'assign' ? _AGENT_ASSIGN_OPTS : _AGENT_STATUS_OPTS

// A full-width sidebar filter row ("<Title> … <value> ⌄") — opens a MenuPanel
// of options that write `stateKey` on root. The value label is a reactive text
// factory (content props update on state change; only paint/layout freeze).
const _agentsFilterRow = (title, stateKey, optsKey) => ({
  // The factory args ride on `scope`: frank serializes each handler on its
  // own, without this closure, so a bare `stateKey` read published as a read
  // off the shared globalScope. The option list rides as a KEY, never a
  // closure: a scope-bag function with free names cannot cross the transport,
  // so on a pushed canvas the rows lost their options (canvas case 4,
  // 2026-11-01). `_agentFilterOpts` resolves the key at each read.
  scope: { title, stateKey, optsKey },
  tag: 'button',
  type: 'button',
  flow: 'x',
  align: 'center space-between',
  gap: 'V',
  width: '100%',
  padding: 'Z A',
  round: 'radiusPill',
  borderWidth: '0',
  background: 'transparent',
  color: 'subtitle',
  cursor: 'pointer',
  fontSize: 'Z2',
  ':hover': {
    background: 'hover',
    color: 'title'
  },
  Title: { tag: 'span', color: 'caption', text: title },
  Val: {
    flow: 'x',
    align: 'center center',
    gap: 'V',
    Current: {
      tag: 'span',
      color: 'title',
      fontWeight: '700',
      text: (el, s) =>
        el.call(
          'polyglot',
          _agentOptLabel(_agentFilterOpts(el.scope.optsKey, s.root), s.root?.[el.scope.stateKey])
        )
    },
    Caret: { extends: 'Icon', name: 'arrowAngleDown', boxSize: 'Y2', color: 'caption' }
  },
  onClick: (e, el, s) => {
    e.stopPropagation()
    const root = s.root
    const opts = _agentFilterOpts(el.scope.optsKey, root)
    const body = { extends: 'MenuPanel', aria: { label: el.scope.title } }
    opts.forEach((o, i) => {
      body['Opt_' + i] = {
        extends: 'MenuPanelItem',
        text: (el2) => el2.call('polyglot', o.label),
        isActive: (el2, s2) => (s2.root?.[el.scope.stateKey] || 'all') === o.key,
        onClick: (ev, it) => {
          it.call('closeDropdown', 'agentsFilter')
          root?.update({ [el.scope.stateKey]: o.key })
        }
      }
    })
    el.call('openDropdown', {
      id: 'agentsFilter',
      anchorEl: el,
      placement: 'bottom-start',
      content: body
    })
  }
})

// The /agents filter card — lives in the left CanvasViewRail (below the VIEWS
// nav), shown only on /agents. subscribeTo the filter keys so this page-root
// subtree re-renders its active states + value labels on change (page-root
// children otherwise freeze); the Stage AgentNodes re-render via _visibleAgents
// independently. Replaces the old floating AgentsFilterBar.
const _agentsFilterCard = () => ({
  hide: (el, s) => !s.root?._canvasAgentsOnly,
  subscribeTo: [
    '_canvasAgentsOnly',
    '_agentsScope',
    '_agentsStatus',
    '_agentsType',
    '_agentsAssign'
  ],
  // Its OWN glass card below the VIEWS card (2026-06-19 per Nika "split the
  // /agents sidebar into 2 different cards") — the rail's sidebar card
  // (CanvasViewRail childExtends AppSidebarCard, Nika 2026-10-30 "A: navbar
  // glass"), so it carries no surface of its own; in the phone sheet it sits
  // on the sheet unboxed, as the VIEWS group above it does.
  flexShrink: '0',
  flow: 'y',
  gap: 'V',

  Label: {
    tag: 'span',
    text: '{{ canvas.team.rail.agents | polyglot }}',
    fontSize: 'Y2',
    fontWeight: '700',
    color: 'caption',
    textTransform: 'uppercase',
    letterSpacing: '0.05em',
    padding: 'Y A'
  },

  // All agents / My agents — full-width rows, active = frosted wash (VIEWS idiom).
  Scope: {
    flow: 'y',
    gap: 'V',
    childrenAs: 'state',
    children: () => [
      { key: 'all', label: 'canvas.team.scope.all' },
      { key: 'mine', label: 'canvas.team.scope.mine' }
    ],
    childExtends: {
      tag: 'button',
      type: 'button',
      flow: 'x',
      align: 'center left',
      width: '100%',
      padding: 'Z A',
      round: 'radiusPill',
      borderWidth: '0',
      background: 'transparent',
      color: 'subtitle',
      cursor: 'pointer',
      fontSize: 'Z2',
      fontWeight: '700',
      isActive: (el, s) => (s.root?._agentsScope || 'all') === s.key,
      // Same white-wash bug as the /files switcher: theme-invariant, so the
      // ACTIVE tab had no visible background on the light shell. (:hover below
      // already had its @light counterpart — the active state was missed.)
      '.isActive': {
        background: 'selected',
        color: 'title'
      },
      ':hover': {
        background: 'hover',
        color: 'title'
      },
      // `label` carries a bundle KEY (see the `Scope.children` list above).
      text: (el, s) => el.call('polyglot', s.label),
      onClick: (e, el, s) => {
        e.stopPropagation()
        s.root?.update({ _agentsScope: s.key })
      }
    }
  },

  // Status / Type / Assignment — dropdown rows.
  Filters: {
    flow: 'y',
    gap: 'V',
    marginTop: 'V',
    StatusRow: _agentsFilterRow(
      (el) => el.call('polyglot', 'canvas.team.filter.titleStatus'),
      '_agentsStatus',
      'status'
    ),
    TypeRow: _agentsFilterRow(
      (el) => el.call('polyglot', 'canvas.team.filter.titleType'),
      '_agentsType',
      'type'
    ),
    AssignRow: _agentsFilterRow(
      (el) => el.call('polyglot', 'canvas.team.filter.titleAssignment'),
      '_agentsAssign',
      'assign'
    )
  }
})

// ─── Page ─────────────────────────────────────────────────────

export const teamAndAgents = {
  tag: 'main',
  id: 'main-content',
  extends: 'AppPage',
  // Fill the shell grid's content row (2026-06-08). The shell grid is
  // `<header-row> <1fr-content-row>`; on this canvas surface the header
  // floats (position:absolute), so its grid row collapses to ~6px. With
  // no explicit placement the <main> AUTO-PLACED into that collapsed
  // ~6px row 1 instead of the 1fr content row 2 — the whole canvas
  // (viewport → plane → nodes) was then clipped to 6px tall and the page
  // rendered blank even though every node was in the DOM at the right
  // position. The shell grid is a single `1fr` row now (AppHeader is an
  // absolute overlay, 2026-06-09), so `gridRow: '1'` lands the page in that
  // content track and #main-content fills the viewport; the legacy
  // `gridRow: '2'` stranded it in an implicit 0-height row 2 (canvas
  // invisible). Inert in the home AgentsWidget embed — that mounts
  // CanvasEmbed in a flex widget body (not a grid), where its own
  // height:100%/flex govern.
  gridRow: '1',
  gridColumn: '1 / -1',
  minHeight: '0',
  metadata: (el) => ({ title: el.call('pageTitle', 'canvas.page.teamAndAgents.title') }),

  // Persistent left rail — the shared Frontend/Infra/Tasks/Agents view switcher
  // (this is the Agents view). 2026-06-18 per Nika "add this sidebar to
  // /team-and-agents page".
  // The board shows no title, so this is its one `h1`: announced, never seen
  // (workspace design-direction.md §3). /agents and /my-agents extend this page
  // and name themselves by overriding the text.
  PageSrTitle: { text: '{{ canvas.page.teamAndAgents.title | polyglot }}' },

  CanvasViewRail: {
    '@mobileL': { display: 'none' },
    AgentsFilterCard: _agentsFilterCard()
  },
  CanvasControlsSheet: { Card: { Content: { AgentsFilterCard: _agentsFilterCard() } } },

  // Init panzoom on the Board via the shared helper
  // (canvas/functions/initPanzoomBoard.js). The helper handles:
  //   - panzoom + beforeMouseDown gate on [data-node-id]
  //   - wheel pan + ⌘/Ctrl pinch zoom
  //   - dots layer position + size on every transform
  //   - --canvas-scale CSS var on the scene
  //   - idempotent attach
  // What stays here: drag-position scale lookup (window.__tagPzScale)
  // and the initial offset that matches the page's coordinate system.
  onCreate: (el, s) => {
    // `s` may be undefined when teamAndAgents is embedded into another
    // surface (the home's AgentsWidget uses `extends: 'teamAndAgents'`)
    // — DOMQL doesn't always thread the state argument through the
    // extend chain. Same fallback shape as onRemove below; bail
    // silently if root is unreachable rather than throwing.
    const state = s || el?.state || (typeof el?.getRoot === 'function' ? el.getRoot().state : null)
    const root = state?.root
    if (!root) return
    // The lens a page that extends this one names on its scope (/agents:
    // 'agents', /my-agents: 'mine'), applied HERE, before the boot below reads
    // it (`_fitMode`, the agents-only node set). Those pages carry no hook of
    // their own and never read this page OBJECT: a function that read it made
    // frank copy the whole page into globalScope (canvas case 3, 2026-11-01).
    const lens = el.scope?.canvasLens
    if (lens === 'agents') {
      root._canvasAgentsOnly = true
      if (root._agentsScope == null) root._agentsScope = 'all'
      if (root._agentsStatus == null) root._agentsStatus = 'all'
      if (root._agentsType == null) root._agentsType = 'all'
      if (root._agentsAssign == null) root._agentsAssign = 'all'
    } else if (lens === 'mine') root._canvasScope = 'mine'
    // Hydrate persisted layout: workspace-shared positions from
    // sdk.canvasLayout, per-user connection state from user prefs.
    // The call is async but we don't await — the canvas renders with
    // default positions immediately and snaps to the server's map as
    // soon as the GET resolves. userPrefs may be `null` on cold mount;
    // the onUpdate hook below re-runs once loadPrivateData fills it.
    _hydrateLayoutFromPrefs(root, el)
    if (!el._tagHydrateAttempted) el._tagHydrateAttempted = true

    // Live workspace-canvas-layout subscription — a drag on another
    // tab or by another member fans the new positions through this
    // socket and merges them into _tagPositions. Stash the unsubscribe
    // on `el` so onRemove tears the socket down.
    if (!el.__tagLayoutUnsub) {
      // Mark eagerly so a re-entrant onCreate (smbls fires the hook
      // multiple times during teardown chains) doesn't open a second
      // socket while the first one's promise is still resolving.
      el.__tagLayoutUnsub = true
      _subscribeCanvasLayout(root, el).then((unsub) => {
        if (typeof unsub === 'function') el.__tagLayoutUnsub = unsub
        else el.__tagLayoutUnsub = null
      })
    }
    // §unified-perms PR #10 — fetch the Mongo-backed agent roster
    // once per session. _loadAgents is idempotent (root._agentsLoaded
    // guard); failure silently falls back to SEED_AGENTS, so the
    // canvas always renders. The active interface language names the
    // server-seeded defaults (agentDisplayName).
    _loadAgents(root, el.call('getActiveLang'))
    // Load server-backed agent↔person assignments once per session
    // (feature-detected — a no-op until the assignment API is live).
    if (!root._agentAssignmentsLoaded) {
      root._agentAssignmentsLoaded = true
      try {
        el.call('loadAllAgentAssignments', root)
      } catch (_) {
        /* fn not in scope */
      }
    }
    // `root.onlineUsers` (canonical array) and `root.presenceById` (per-id
    // reactive map) are both populated by the shell-level useOnlinePresence
    // subscription in app.js — no per-page subscription needed here. Person
    // nodes' OnlineDot/IdleDot read `presenceById[email]` (via _personStatus),
    // so a presence tick repaints only the changed node's dot, not the board.
    //
    // Lightweight agent-presence poll: first hit now, then every 30s
    // while the page is mounted. Cleaned up by onRemove. Keeps the
    // pulse indicator live without needing /tickets to be the
    // canonical loader.
    if (!el._tagPresencePollStarted) {
      el._tagPresencePollStarted = true
      _fetchTagAgentPresence(root)
      el._tagPresencePoll = setInterval(() => _fetchTagAgentPresence(root), _TAG_PRESENCE_POLL_MS)
    }
    // Re-snap nodes to their SAVED coords as they mount AND as positions
    // hydrate. People load async AFTER this onCreate's first hydrate, and the
    // reactive style.left/top freezes at the mount-time default grid (v3.14
    // reactive-layout freeze) while this page's onUpdate doesn't reliably tick
    // — so without this, humans render on the default grid and never move to
    // their saved positions ("cards don't populate to correct positions on
    // initial render"; lines drawn between them then point at empty space).
    // The existing fit-poll only runs for _fitMode (my-agents/embed), NOT
    // /team-and-agents — so this page had nothing re-applying. Bounded poll on
    // the (node-count + position-count + layout-version) signature; re-applies
    // imperatively on any change, settles ~1.2s after it stops growing (or a
    // 20s ceiling). Cleared in onRemove.
    if (!el.__tagSnapPoll) {
      let snapSig = ''
      let snapStable = 0
      let snapTicks = 0
      el.__tagSnapPoll = setInterval(() => {
        snapTicks++
        const board = el?.AppMain?.Body?.Stage?.Board?.node
        const n = board ? board.querySelectorAll('[data-node-id]').length : 0
        const sig =
          n +
          '|' +
          Object.keys(root._tagPositions || {}).length +
          '|' +
          (root.__tagLayoutVersion == null ? '' : root.__tagLayoutVersion)
        if (sig !== snapSig) {
          snapSig = sig
          snapStable = 0
          _applyPositionsToDom(root, el)
        } else {
          snapStable++
        }
        // Don't settle until the layout GET has actually resolved
        // (`_tagLayoutHydrated`). On SPA nav from home the people are ALREADY
        // loaded, so the node count stabilises in ~1.2s — BEFORE the fresh
        // getCanvasLayout GET returns. Without this gate the poll cleared on
        // that early stability and the saved positions (arriving moments later)
        // never re-applied, leaving humans on the default grid (the "wrong
        // positions on navbar nav" bug). On a hard refresh everything loads
        // slowly together, so the count kept changing and the poll naturally
        // stayed alive — which is why hard refresh worked but SPA nav didn't.
        // The snapTicks ceiling still bounds the poll if the GET never lands.
        if ((n > 0 && snapStable >= 6 && el._tagLayoutHydrated) || snapTicks >= 100) {
          clearInterval(el.__tagSnapPoll)
          el.__tagSnapPoll = null
          _applyPositionsToDom(root, el)
        }
      }, 200)
    }
    //
    // Inject the `@keyframes ttag-pulse` rule once per document so
    // TeamAgentNode.PulseDot can pulse. Cheap (idempotent guard via
    // element id) and avoids needing a design-system keyframe entry.
    if (typeof document !== 'undefined' && !document.getElementById('ttag-pulse-css')) {
      const style = document.createElement('style')
      style.id = 'ttag-pulse-css'
      style.textContent =
        '@keyframes ttag-pulse{0%{box-shadow:0 0 0 0 rgba(80,200,120,0.7),0 0 0 2px var(--color-card);}70%{box-shadow:0 0 0 10px rgba(80,200,120,0),0 0 0 2px var(--color-card);}100%{box-shadow:0 0 0 0 rgba(80,200,120,0),0 0 0 2px var(--color-card);}}'
      document.head.appendChild(style)
    }
    // /my-agents + the home embed: poll until the node set has loaded and
    // settled, then frame it (see _fitToVisible / _fitMode). Driven by a
    // bounded poll rather than the reactive onUpdate so the camera move
    // doesn't depend on a state tick landing after data + panzoom are ready.
    // No-op on /team-and-agents (and admin /my-agents).
    if (_fitMode(root) && !el.__tagFitPoll) {
      let lastSig = null
      el.__tagFitPoll = setInterval(() => {
        if (el.__tagFitted || (el.__tagFitPollN = (el.__tagFitPollN || 0) + 1) > 60) {
          clearInterval(el.__tagFitPoll)
          el.__tagFitPoll = null
          return
        }
        const ids = _fitNodeIds(root)
        const sig = ids && ids.size ? [...ids].sort().join(',') : ''
        // Fit once the set is non-empty AND unchanged since the previous
        // tick — the staged loads (people, then assigned agents) have
        // stopped growing it.
        if (sig && sig === lastSig) _fitToVisible(root, el)
        lastSig = sig
      }, 180)
    }
    _bootTeamPanzoom(el)
  },
  onUpdate: (el, s) => {
    _bootTeamPanzoom(el)
    // Same defensive derivation as onCreate / onRemove — `s` is
    // undefined along the embedded-page teardown / update paths.
    const state = s || el?.state || (typeof el?.getRoot === 'function' ? el.getRoot().state : null)
    const root = state?.root
    if (!root) return
    // Re-hydrate once user prefs lands (cold mount path). Skipped while
    // another hydrate pass is in flight (it would be swallowed by the
    // in-flight guard and the flag would block the retry) — a later
    // onUpdate tick picks it up.
    if (
      !el._tagPrefsHydrated &&
      !el._tagHydrateInFlight &&
      root?.userPrefs &&
      Object.keys(root.userPrefs).length > 0
    ) {
      el._tagPrefsHydrated = true
      _hydrateLayoutFromPrefs(root, el)
    }
    // Re-hydrate when the workspace id becomes resolvable (or changes).
    // The onCreate hydrate can run before jwtClaims/workspace land —
    // wsId is null then and the canvas-layout GET is skipped entirely,
    // leaving the canvas on the default grid with no other trigger to
    // load the saved layout. _tagLayoutWsLoaded records which wsId the
    // last pass actually attempted; the in-flight guard inside
    // _hydrateLayoutFromPrefs serializes overlapping kicks.
    const wsIdNow = _wsId(root)
    if (wsIdNow && el._tagLayoutWsLoaded !== wsIdNow) {
      _hydrateLayoutFromPrefs(root, el)
    }
    // Save when the user-mutated keys change. Diff-gated so we don't
    // PATCH on every reactive cycle. The debounce inside
    // _saveLayoutToPrefs absorbs rapid sequential updates.
    const posSig = root?._tagPositions
    const conSig = root?._tagConnections
    const remSig = root?._tagConnectionsRemoved
    const grpSig = root?._tagGroupMap
    const namSig = root?._tagGroupNames
    if (
      el._tagLastSavedPos !== posSig ||
      el._tagLastSavedCon !== conSig ||
      el._tagLastSavedRem !== remSig ||
      el._tagLastSavedGrp !== grpSig ||
      el._tagLastSavedNam !== namSig
    ) {
      // Arm ONLY off the canvas-layout hydration flag — set when a
      // hydrate pass that could actually reach the server completed
      // (_hydrateLayoutFromPrefs sets it after the awaited GET). The
      // old gate also armed off `_tagPrefsHydrated`, which onUpdate
      // sets SYNCHRONOUSLY before its async hydrate pass resolves —
      // so saves could fire while the saved layout was still in
      // flight, persisting pre-load state over it (the first-load
      // default-positions race).
      if (el._tagLayoutHydrated) {
        if (el._tagSaveArmed) _saveLayoutToPrefs(el, root)
        el._tagSaveArmed = true
      }
      el._tagLastSavedPos = posSig
      el._tagLastSavedCon = conSig
      el._tagLastSavedRem = remSig
      el._tagLastSavedGrp = grpSig
      el._tagLastSavedNam = namSig
    }
    // ── Viewport windowing re-triggers ────────────────────────────
    // Recompute the visible set when the roster changed (late-arriving nodes
    // should appear in-viewport without a manual pan) or when node positions
    // changed from a NON-drag source (layout hydrate, live collab move). Both
    // signature-gated; the position trigger is SKIPPED while a local drag is
    // in flight — the dragged node is pinned and onUp recomputes on release,
    // so per-pointermove _tagPositions writes never rebuild the window (the
    // reconcile storm this windowing exists to avoid).
    const winRosterSig =
      (_visiblePeople(root)?.length || 0) + ':' + (_visibleAgents(root)?.length || 0)
    const winPosId = root?._tagPositions
    const winDragging = !!_activeDrag.get(root)
    let winNeed = false
    if (el.__tagWinRosterSig !== winRosterSig) {
      el.__tagWinRosterSig = winRosterSig
      winNeed = true
    }
    if (!winDragging && el.__tagWinPosId !== winPosId) {
      el.__tagWinPosId = winPosId
      winNeed = true
    }
    if (winNeed) scheduleTeamWindowRecompute(el, root)
  },

  onRender: (el, s) => {
    _bootTeamPanzoom(el)
    // The empty-area right-click menu used to be a RAW
    // `node.addEventListener('contextmenu', …)` armed here behind a
    // `__tagCtxBound` node flag, removed nowhere (FA503). It is DECLARED as
    // `onContextmenu` on this page root now, so DOMQL registers it with the
    // node and tears it down in dispose().
    //
    // The comment that justified the raw call — "DOMQL doesn't delegate
    // onContextmenu" — was STALE: `contextmenu` is a member of smbls's
    // DELEGATED_EVENTS set (element/src/registry.js). See
    // `__tests__/globalListenerLifecycle.test.js` for the receipt.
    //
    // The long-press shim still arms here: it is a one-shot synthesiser, not
    // a listener this page owns, and it needs the node to exist.
    //
    // iPad: synthesize the missing long-press contextmenu at the stage root;
    // it bubbles from the touched tile, so one arm covers every person/agent
    // node (tagNodeContextmenu) + the empty-canvas menu below. Panzoom is
    // unaffected — pointer travel past the shim's tolerance cancels the press.
    //
    // NO latch of our own — see the same call in pages/studios.js: the shim is
    // idempotent per NODE, and an element-level flag outlives a node swap.
    el.call('armLongPress')
  },

  // Empty-area right-click → the shared cascade context menu ("New ▸" → the
  // create flyout). Mirrors the /studio background menu.
  onContextmenu: (ev, el, s) => {
    if (
      ev.target.closest?.('input, textarea, [data-smbls-dropdown="panel"], [data-key="ModalRoot"]')
    )
      return
    // Empty canvas only — not a node tile, the sidebar, the + island, or a control.
    if (
      ev.target.closest?.(
        '[data-node-id], button, a, [data-key="AppSidebar"], [data-key="TagCreateNav"]'
      )
    )
      return
    ev.preventDefault()
    el.call('openDropdown', {
      id: 'tagContext',
      anchorRect: {
        left: ev.clientX,
        top: ev.clientY,
        right: ev.clientX,
        bottom: ev.clientY,
        width: 0,
        height: 0
      },
      placement: 'bottom-start',
      content: cascadeMenu([
        {
          label: (el) => el.call('polyglot', 'canvas.boardMenu.new.label'),
          icon: 'plus',
          ariaLabel: (el) => el.call('polyglot', 'canvas.team.create.aria'),
          submenuBody: tagCreateRows(s.root)
        }
      ])
    })
  },

  // Tear down everything the page leaked onto root, document, window,
  // and the scene node so back-and-forth nav doesn't drag "old stuff"
  // into the next mount of either this page or studios. Mirrors the
  // five leak surfaces flagged in the code review (#13):
  //   1. pending save debounce (_canvasSaveTimers WeakMap)
  //   2. in-flight panzoom RAF retry (cancelToken)
  //   3. panzoom instance event listeners (pz.dispose)
  //   4. mid-drag document mousemove/mouseup (_activeDrag WeakMap)
  //   5. page-scoped root state (hover/connect/selection)
  onRemove: (el, s) => {
    // `s` is undefined when this page's onRemove fires during a teardown
    // triggered by an EMBEDDING surface (the home's AgentsWidget embeds
    // teamAndAgents via `extends`, and DOMQL's dispose walker enters this
    // hook with `(el, undefined)` along that path). Falling back to
    // `el.state` / `el.getRoot()` keeps cleanup working both when the
    // user navigates AWAY from `/team-and-agents` (s passed normally) and
    // when the page is being detached as a child of another element
    // (s undefined). Without this, every navigation off the home that
    // tears down the embedded canvas logs a smbls onRemove TypeError and
    // skips the rest of cleanup — leaving panzoom listeners, drag
    // handlers, and root state slots leaked into the next mount.
    const state = s || el?.state || (typeof el?.getRoot === 'function' ? el.getRoot().state : null)
    const root = state?.root
    if (root) {
      clearTimeout(_canvasSaveTimers.get(root))
      _canvasSaveTimers.delete(root)
    }
    if (el._tagPresencePoll) {
      clearInterval(el._tagPresencePoll)
      el._tagPresencePoll = null
      el._tagPresencePollStarted = false
    }
    // Stop the fit poll so it can't fire against a disposed panzoom after
    // unmount; reset the one-shot so a re-mount re-frames.
    if (el.__tagFitPoll) {
      clearInterval(el.__tagFitPoll)
      el.__tagFitPoll = null
    }
    el.__tagFitted = false
    el.__tagFitPollN = 0
    // Stop the position re-snap poll on unmount.
    if (el.__tagSnapPoll) {
      clearInterval(el.__tagSnapPoll)
      el.__tagSnapPoll = null
    }
    // Stop + reset viewport-windowing state so a re-mount recomputes cleanly.
    if (el.__tagVizRaf) {
      cancelAnimationFrame(el.__tagVizRaf)
      el.__tagVizRaf = null
    }
    el.__tagLastVisibleKey = null
    el.__tagLastVisibleSet = null
    el.__tagPosSnapshot = null
    el.__tagWinRosterSig = null
    el.__tagWinPosId = null
    _pinnedIds.clear()
    if (el.__tagPzRaf) {
      cancelAnimationFrame(el.__tagPzRaf)
      el.__tagPzRaf = null
    }
    // Release the workspace-canvas-layout socket — leaks otherwise on
    // every back-and-forth between this page and another.
    if (typeof el.__tagLayoutUnsub === 'function') {
      try {
        el.__tagLayoutUnsub()
      } catch (_) {}
      el.__tagLayoutUnsub = null
    }
    if (el.__tagPzCancel) el.__tagPzCancel.canceled = true
    const scene = el.AppMain?.Body?.Stage?.Board?.node
    const pz = el.__tagPzInstance || scene?.__tagPanzoomAttached
    if (pz && typeof pz.dispose === 'function') {
      try {
        pz.dispose()
      } catch (_) {
        /* idempotent */
      }
    }
    if (scene && scene.__tagPanzoomAttached) delete scene.__tagPanzoomAttached
    el.__tagPzInstance = null
    // Release the shared Zoombar mirror so it doesn't point at a disposed pz.
    if (
      typeof window !== 'undefined' &&
      window.__canvasGlobalScope &&
      window.__canvasGlobalScope.panzoomInstance === pz
    ) {
      window.__canvasGlobalScope.panzoomInstance = null
    }
    if (root) {
      const drag = _activeDrag.get(root)
      if (drag) {
        if (drag.onMove) document.removeEventListener('pointermove', drag.onMove)
        if (drag.onUp) {
          document.removeEventListener('pointerup', drag.onUp)
          document.removeEventListener('pointercancel', drag.onUp)
        }
        _activeDrag.delete(root)
      }
      if (
        root._tagHoverGroup ||
        root._tagHoverNode ||
        root._tagSelectedConnection ||
        root._tagConnectMode ||
        root._tagConnectFrom ||
        root._tagLinking ||
        root._tagVisibleIds
      ) {
        root.update({
          _tagHoverGroup: null,
          _tagHoverNode: null,
          _tagSelectedConnection: null,
          _tagConnectMode: false,
          _tagConnectFrom: null,
          _tagLinking: null,
          // Clear the window set so a re-mount's first paint takes the fresh
          // seed path rather than filtering against a stale set.
          _tagVisibleIds: null
        })
      }
      // Drop the lens a page that extends this one named (see onCreate), so
      // /team-and-agents and the home embed, which share this root, show the
      // full set again.
      const lens = el.scope?.canvasLens
      if (lens === 'agents') root._canvasAgentsOnly = null
      else if (lens === 'mine') root._canvasScope = null
    }
    if (typeof window !== 'undefined') window.__tagPzScale = 1
  },

  // Sidebar removed — the canvas fills the whole content area. The
  // help text moves into a floating HelpBox anchored bottom-right of
  // the canvas (see AppMain.Body.Canvas.HelpBox below).
  AppSidebar: null,

  AppMain: {
    flex: '1 1 0',
    flow: 'y',
    minHeight: '0',
    // Page-level zero padding — the canvas + sidebar already occupy
    // the whole content area, no AppMain padding needed around them.
    padding: '0',
    // Full-bleed square canvas — AppMain extends CommonCard (ships margin +
    // round); reset both like tickets/files/infra so the board isn't clipped
    // into rounded corners. (2026-06-19 per Nika "remove canvas border radius".)
    margin: '0',
    round: '0',

    Body: {
      flex: '1 1 0',
      flow: 'y',
      padding: '0',
      overflow: 'hidden',

      // Viewport: panzoom mounts on the inner Board; this is the
      // overflow-hidden clipper that contains the transformed scene.
      Stage: {
        position: 'relative',
        flex: '1 1 0',
        width: '100%',
        height: '100%',
        minHeight: '70vh',
        overflow: 'hidden',
        // Canvas surface matches /tickets (+ the home dashboard): `gray5`
        // (rgb(69,69,75)) — the `--canvas-surface-bg` @dark chain resolved to
        // near-black, diverging from /tickets. The Zoombar's `--canvas-bg` pick
        // still overrides when set. (2026-06-17 per Nika "same background on
        // canvas as /tickets" — extended to /team-and-agents.)
        style: {
          background: 'var(--canvas-bg, var(--stage-bg, var(--color-stage)))'
        },
        cursor: (el, s) => (s.root?._tagConnectMode ? 'crosshair' : 'default'),

        // Floating "+" create-island — the shared CanvasCreateIsland (the
        // /studio StudioNavbar chrome) with the page's two actions. The
        // menu opens through the dropdown plugin (placement, outside-click,
        // ESC) — the old state-toggled CreateMenu + TagCreateBackdrop are
        // gone. Viewport-anchored (in Stage, not the Board) so it stays put
        // while the canvas pans/zooms.
        TagCreateNav: {
          // The one solid-ink create FAB (DATE-FMT-1-REMAINDERS-1) — same
          // look as /studio, the project canvas and the tickets board.
          extends: 'CanvasPrimaryCreateIsland',
          AddBtn: {
            title: (el) => el.call('polyglot', 'canvas.team.create.addTitle'),
            aria: { label: (el) => el.call('polyglot', 'canvas.team.create.aria') },
            // Content is built HERE (the opener) with `root` captured, the
            // same pattern as /tickets — the dropdown portal doesn't inherit
            // page state (cf. dropdown-panel-content-reactivity), and the
            // island-prop indirection didn't resolve for inline (non-
            // registered) islands.
            onClick: (e, el, s) => {
              e.stopPropagation()
              el.call('openDropdown', {
                id: 'tagCreate',
                anchorEl: el,
                placement: 'left-start',
                content: {
                  extends: 'CanvasCreateMenuBody',
                  aria: { label: (el) => el.call('polyglot', 'canvas.team.create.menuAria') },
                  ...tagCreateRows(s.root)
                }
              })
            }
          }
        },

        // (The /agents filters moved from a floating Stage bar into a card in
        // the left CanvasViewRail — see _agentsFilterCard / CanvasViewRail.)

        // Floating help pill — bottom-right corner of the canvas. The
        // chrome (card surface, hairline border, radius, padding, the
        // dismiss ×) is the shared CanvasMessage; this consumer supplies
        // only the content: the shared `Lottie` (the same swipe-right
        // animation `DragParagraph` uses inside the studio insert panel,
        // shared/components/DragParagraph.js) plus the three help lines
        // that used to live in the sidebar. `dismissKey` wires the ×: a
        // click sets `_tagHelpDismissed` on root, persisted across SPA nav
        // back to this page. CanvasMessage already defaults to the
        // bottom-right corner, so no positioning override is needed.
        HelpBox: {
          // CanvasHelpCard = the glassy, lifted help-card chrome (was the flat
          // CanvasMessage). This consumer supplies only the content below —
          // the swipe Lottie now ships with the base card.
          extends: 'CanvasHelpCard',
          dismissKey: '_tagHelpDismissed',

          Line1: { tag: 'span', text: '{{ canvas.team.help.drag | polyglot }}' },
          Line2: { tag: 'span', text: (el) => el.call('panZoomHint') },
          Line3: { tag: 'span', text: '{{ canvas.team.help.click | polyglot }}' }
        },

        // (Suggest-team panel removed — node-to-node assignment is now a
        // studios-style drag from each card's link handle; see _startLink
        // + the LinkHandle child on TeamPersonNode / TeamAgentNode.)

        // Toast — transient feedback for assign / unassign (+ errors).
        // Viewport-anchored: lives in Stage, not the transformed Board.
        Toast: {
          hide: (el, s) => !s.root?._tagToast,
          position: 'absolute',
          left: '50%',
          bottom: 'B',
          transform: 'translateX(-50%)',
          zIndex: '6',
          padding: 'V A',
          round: 'C',
          fontSize: 'Z2',
          fontWeight: '700',
          color: 'white',
          borderWidth: 'hairline',
          borderStyle: 'solid',
          borderColor: 'line',
          shadow: 'overlay',
          background: (el, s) =>
            s.root?._tagToast?.kind === 'error' ? 'red' : 'rgba(38, 40, 47, 0.96)',
          // Key-in-state: the carrier holds a bundle KEY (or an already-composed
          // sentence / raw SDK message, which polyglot returns verbatim on a miss).
          text: (el, s) =>
            s.root?._tagToast?.text ? el.call('polyglot', s.root._tagToast.text) : '',
          // Auto-dismiss ~3.2s after the toast last changed.
          onUpdate: (el, s) => {
            const t = s.root?._tagToast
            if (!t || el.__toastFor === t) return
            el.__toastFor = t
            if (el.__toastTimer) clearTimeout(el.__toastTimer)
            el.__toastTimer = setTimeout(() => s.root?.update?.({ _tagToast: null }), 3200)
          }
        },

        // Dots layer — shared CanvasDots grid, re-positioned on every
        // panzoom transform tick (initPanzoomBoard's updateDots).
        Dots: { extends: 'CanvasDots' },

        // Board is the transformed scene panzoom owns. Inner Connections
        // SVG + nodes ride this transform.
        Board: {
          position: 'absolute',
          top: '0',
          left: '0',
          width: '6000px',
          height: '4000px',
          transformOrigin: '0 0',
          // Marker so _startLink resolves THIS board's rect + scale via
          // el.node.closest — works for the dedicated page AND the home
          // embed, which each mount their own Board.
          attr: { 'data-tag-board': '' },

          // Live "ghost" line shown while dragging a connection from a
          // node's link handle (_startLink writes root._tagLinking). One
          // dashed segment from the source node's center to the cursor,
          // in board coordinates. pointerEvents:none so it never eats the
          // drop hit-test.
          LinkGhost: {
            tag: 'svg',
            position: 'absolute',
            inset: '0',
            width: '6000px',
            height: '4000px',
            overflow: 'visible',
            zIndex: '1',
            style: { pointerEvents: 'none' },
            attr: { viewBox: '0 0 6000 4000', preserveAspectRatio: 'none' },
            hide: (el, s) => !s.root?._tagLinking,
            GhostLine: {
              tag: 'path',
              attr: {
                d: (el, s) => {
                  const f = _linkGhostFrom(s.root)
                  const lk = s.root?._tagLinking
                  const x2 = lk?.x ?? f.x
                  const y2 = lk?.y ?? f.y
                  return `M ${f.x} ${f.y} L ${x2} ${y2}`
                },
                fill: 'none',
                stroke: 'rgba(140, 170, 255, 0.95)',
                'stroke-width': '2',
                'stroke-dasharray': '6 5',
                'stroke-linecap': 'round'
              }
            }
          },

          // Connections SVG — covers the same coordinate space as the
          // Board, sits underneath the cards. `overflow: visible` matches
          // studios.js (buildConnectionsSVG ~line 1724): SVG elements
          // default to overflow:hidden in CSS, which clips any bezier
          // arc whose control points fall outside the viewBox bounds.
          // Connections layer — uses the shared `CanvasConnection`
          // component (canvas/components/CanvasConnection.js) for the
          // per-edge HitArea + Visible <g>. Page-specific styling +
          // event handlers are passed as state via `getPath` /
          // `getStroke` / etc. callbacks so the renderer stays
          // page-agnostic.
          Connections: {
            tag: 'svg',
            position: 'absolute',
            inset: '0',
            width: '6000px',
            height: '4000px',
            overflow: 'visible',
            style: { pointerEvents: 'none' },
            attr: { viewBox: '0 0 6000 4000', preserveAspectRatio: 'none' },
            childrenAs: 'state',
            children: (el, s) => {
              const root = s.root
              const conns = _allConnections(root)
              // Inject the per-page callbacks each shared
              // CanvasConnection child consumes from its state.
              return conns.map((c) => ({
                ...c,
                getPath: (root, from, to, e) => _linePath(root, from, to, e),
                getStroke: (root, st) => {
                  // Live: an edge to/from an agent currently running a delegated
                  // task glows blue — lighting the Simone→agent→Temo flow as it
                  // runs (reads root.activeProcesses via _isAgentWorking).
                  if (_isAgentWorking(root, st.to) || _isAgentWorking(root, st.from)) {
                    return 'rgba(120, 150, 255, 0.95)'
                  }
                  const key = _connectionKey({ from: st.from, to: st.to })
                  if (
                    root?._tagSelectedConnection &&
                    _connectionKey(root._tagSelectedConnection) === key
                  ) {
                    return 'rgba(255,120,120,0.95)'
                  }
                  if (root?._tagHoverConnection === key) {
                    return 'rgba(255,255,255,0.95)'
                  }
                  if (st.from === root?._tagConnectFrom || st.to === root?._tagConnectFrom) {
                    return 'rgba(180,120,216,0.85)'
                  }
                  return 'rgba(255,255,255,0.5)'
                },
                getStrokeWidth: (root, st) => {
                  if (_isAgentWorking(root, st.to) || _isAgentWorking(root, st.from)) return 2.5
                  const key = _connectionKey({ from: st.from, to: st.to })
                  if (
                    root?._tagSelectedConnection &&
                    _connectionKey(root._tagSelectedConnection) === key
                  ) {
                    return 2.5
                  }
                  const sharedCount = Array.isArray(st.shared) ? st.shared.length : 0
                  if (sharedCount >= 3) return 2.5
                  if (sharedCount === 2) return 2.0
                  if (sharedCount === 1) return 1.5
                  return 1.5
                },
                onSelect: (e, el, st) => {
                  e.preventDefault()
                  e.stopPropagation()
                  if (st.root?._tagConnectMode) return
                  // Studios-style direct removal — a click on a
                  // connection drops it immediately, no intermediate
                  // EditBar. Mirrors /studios' click-to-remove pattern
                  // (canvas/pages/studios.js:6841 — `path.closest
                  // [data-connection-id]` handler that filters the
                  // edge out of root.connections + persists). Three
                  // edge classes are handled here:
                  //   1. Server-backed assignment edge → SDK unassign.
                  //   2. User-added edge → drop from _tagConnections.
                  //   3. Base/co-membership edge → record removal in
                  //      _tagConnectionsRemoved so re-renders keep it
                  //      hidden across reloads.
                  // (Relationship picker that previously lived in the
                  // ConnectionEditBar moved to the agent profile modal
                  // — track via the agent's _selectedAgent flow.)
                  const c = {
                    from: st.from,
                    to: st.to,
                    assignment: st.assignment,
                    assignmentId: st.assignmentId,
                    agentUserId: st.agentUserId,
                    assigneeUserId: st.assigneeUserId,
                    relationship: st.relationship || null
                  }
                  if (c.assignment && c.assignmentId) {
                    // A server-backed assignment: the same gate as the agent
                    // editor's Remove, and the house confirm first (CRUD +
                    // confirm standard rule 4) — the unassign runs only on
                    // the verb.
                    const root = st.root
                    if (!_canManageAgents(root)) return
                    const orgId =
                      root?.jwtClaims?.workspace_org_id ||
                      (root?.jwtClaims?.orgs && root.jwtClaims.orgs[0]?.id) ||
                      null
                    const agent = _agents(root).find((a) => a.id === c.from)
                    el.call('confirmAgentUnassign', {
                      agent: (agent && agent.name) || c.from,
                      person:
                        _personNameByUserId(root, c.assigneeUserId) ||
                        el.call('polyglot', 'canvas.team.assignee.fallback'),
                      onConfirm: () =>
                        Promise.resolve(
                          el.call('unassignAgentFromUser', root, {
                            orgId,
                            assignmentId: c.assignmentId,
                            agentUserId: c.agentUserId,
                            assigneeUserId: c.assigneeUserId
                          })
                        )
                          .then(() =>
                            root?.update?.({
                              _tagToast: {
                                kind: 'ok',
                                text: 'canvas.team.toast.assignmentRemoved'
                              }
                            })
                          )
                          .catch((err) =>
                            root?.update?.({
                              _tagToast: {
                                kind: 'error',
                                text: (err && err.message) || 'canvas.team.toast.removeFailed'
                              }
                            })
                          )
                    })
                    return
                  }
                  _removeConnection(st.root, c)
                },
                onHover: (e, el, st, entering) => {
                  const key = _connectionKey({ from: st.from, to: st.to })
                  if (entering) {
                    st.root?.update?.({ _tagHoverConnection: key })
                  } else if (st.root?._tagHoverConnection === key) {
                    st.root?.update?.({ _tagHoverConnection: null })
                  }
                }
              }))
            },
            // Bare-key — DOMQL auto-extends the shared component
            // registered by canvas/components/CanvasConnection.js.
            childExtends: 'CanvasConnection'
          },

          // ConnectionEditBar removed 2026-06-04 — clicks on the
          // connection lines now fire removal directly (studios-style,
          // see CanvasConnection.onSelect above). The relationship
          // picker that lived here moved into the AgentModal's
          // assignment row so it's accessible from the agent's full
          // detail surface instead of an ephemeral floating toolbar.

          // Studios-style group frames — one absolute-positioned rect
          // per `groupN` slot (matches /studios `GroupFrame_<ws>`
          // children). Pre-allocates frames for all 50 slots; only
          // slots with ≥2 members render visible (`display:block`).
          // Sits BEHIND the nodes (zIndex 1; nodes are zIndex 2) so the
          // person/agent cards stack on top of the frame chrome.
          //
          // Drag the frame body → moves every member (delegates to the
          // node mousedown of the first member, same trick studios uses
          // — onMousedown finds the first slot member and pretends the
          // drag started on its node).
          // Double-click the label → editable; blur/Enter commits the
          // new name via the canvas-layout groups field so every member
          // sees it.
          GroupFrames: {
            position: 'absolute',
            inset: '0',
            pointerEvents: 'none',
            zIndex: '1',
            childrenAs: 'state',
            children: () => GROUP_SLOTS.map((slot) => ({ id: slot })),
            childExtends: {
              attr: { 'data-group-id': (el, s) => s.id },
              position: 'absolute',
              pointerEvents: 'auto',
              cursor: 'grab',
              display: (el, s) => {
                const bb = _groupBbox(s.root, s.id, el)
                return bb && _membersOfGroup(s.root, s.id).length >= MIN_GROUP_MEMBERS
                  ? 'block'
                  : 'none'
              },
              style: {
                left: (el, s) => {
                  const bb = _groupBbox(s.root, s.id, el)
                  return bb ? bb.x - GROUP_PAD + 'px' : '-9999px'
                },
                top: (el, s) => {
                  const bb = _groupBbox(s.root, s.id, el)
                  return bb ? bb.y - GROUP_PAD - PLATFORM_HEAD_H + 'px' : '-9999px'
                },
                width: (el, s) => {
                  const bb = _groupBbox(s.root, s.id, el)
                  return bb ? bb.w + GROUP_PAD * 2 + 'px' : '0px'
                },
                height: (el, s) => {
                  const bb = _groupBbox(s.root, s.id, el)
                  return bb ? bb.h + GROUP_PAD * 2 + PLATFORM_HEAD_H + 'px' : '0px'
                }
              },
              round: 'radiusControl',
              borderWidth: (el, s) => (s.root?._tagHoverGroup === s.id ? '2px' : '1px'),
              borderStyle: 'solid',
              borderColor: (el, s) =>
                s.root?._tagHoverGroup === s.id
                  ? 'rgba(255,255,255,0.8)'
                  : 'rgba(255,255,255,0.15)',
              background: (el, s) =>
                s.root?._tagHoverGroup === s.id
                  ? 'rgba(255,255,255,0.08)'
                  : 'rgba(255,255,255,0.03)',
              transition: 'A background defaultBezier, A border defaultBezier',

              // Group label — sits above the frame's top edge.
              // Double-click to rename; blur or Enter commits.
              Label: {
                tag: 'span',
                position: 'absolute',
                top: '-28px',
                left: '4px',
                padding: 'V Z',
                round: 'radiusPill',
                fontSize: 'Y2',
                fontWeight: '700',
                letterSpacing: '.08em',
                textTransform: 'uppercase',
                color: 'caption',
                cursor: 'text',
                pointerEvents: 'auto',
                title: (el) => el.call('polyglot', 'canvas.team.group.renameTitle'),
                style: { outline: 'none' },
                ':focus-visible': { shadow: 'none' },
                // Group-label hover wash. Was a fixed white alpha on the FLIPPING stage
                // (milk light / gray5 dark), so it painted white-on-milk — invisible — in
                // light. `hover` is the §4.3 wash pair (black.05 light / white.07 dark).
                ':hover': { background: 'hover' },
                text: (el, s) => {
                  if (el._editing) {
                    return el.node?.textContent ?? _slotLabel(s.root, s.id)
                  }
                  return _slotLabel(s.root, s.id)
                },
                onMousedown: (e) => e.stopPropagation(),
                onClick: (e) => e.stopPropagation(),
                onDblclick: (e, el) => {
                  e.stopPropagation()
                  e.preventDefault()
                  el._editing = true
                  if (!el.node) return
                  el.node.contentEditable = 'true'
                  el.node.focus()
                  const doc = el.context?.document || document
                  const win = el.context?.window || window
                  const range = doc.createRange()
                  range.selectNodeContents(el.node)
                  const sel = win.getSelection()
                  sel.removeAllRanges()
                  sel.addRange(range)
                },
                onBlur: (e, el, s) => {
                  if (!el._editing) return
                  el._editing = false
                  if (!el.node) return
                  el.node.contentEditable = 'false'
                  const name = (el.node.textContent || '').trim()
                  const slot = s.id
                  const next = { ...(s.root?._tagGroupNames || {}) }
                  if (name) next[slot] = name
                  else delete next[slot]
                  s.root?.update?.({ _tagGroupNames: next })
                },
                onKeydown: (e, el) => {
                  if (e.key === 'Enter' || e.key === 'Escape') {
                    e.preventDefault()
                    el.node?.blur()
                  }
                }
              }
            }
          },

          // The platform agents' frame (WL-7; Nika 2026-10-25 "Own agents,
          // platform ones labeled"): the seed agents with no row in this org
          // sit in their own block under a fixed "Platform" label, after the
          // workspace's own agents. Display chrome only — it follows the
          // cards (bbox of their positions) and takes no pointer, so drags
          // and clicks reach the cards and the board as before.
          PlatformFrame: {
            dataPlatformFrame: true,
            position: 'absolute',
            pointerEvents: 'none',
            zIndex: '1',
            display: (el, s) => (_platformBbox(s.root, el) ? 'block' : 'none'),
            left: (el, s) => {
              const bb = _platformBbox(s.root, el)
              return bb ? bb.x - GROUP_PAD + 'px' : '-9999px'
            },
            top: (el, s) => {
              const bb = _platformBbox(s.root, el)
              return bb ? bb.y - GROUP_PAD - PLATFORM_HEAD_H + 'px' : '-9999px'
            },
            width: (el, s) => {
              const bb = _platformBbox(s.root, el)
              return bb ? bb.w + GROUP_PAD * 2 + 'px' : '0px'
            },
            height: (el, s) => {
              const bb = _platformBbox(s.root, el)
              return bb ? bb.h + GROUP_PAD * 2 + PLATFORM_HEAD_H + 'px' : '0px'
            },
            round: 'radiusControl',
            borderWidth: '1px',
            borderStyle: 'dashed',
            borderColor: 'line',
            // The label and, under it, what these agents are: Symbols' own,
            // in every workspace (persona round 3, W5) — one line, cut with
            // an ellipsis where the block is narrower than the words.
            PlatformHead: {
              position: 'absolute',
              top: 'Z',
              left: 'Z',
              right: 'Z',
              flow: 'y',
              gap: 'X',
              minWidth: '0',
              Label: {
                tag: 'span',
                fontSize: 'Y2',
                fontWeight: '700',
                letterSpacing: '.08em',
                textTransform: 'uppercase',
                color: 'caption',
                text: '{{ canvas.tileFrame.platform | polyglot }}'
              },
              PlatformNote: {
                tag: 'span',
                fontSize: 'Z',
                lineHeight: '1.4',
                color: 'caption',
                whiteSpace: 'nowrap',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                text: '{{ canvas.team.platformNote | polyglot }}'
              }
            }
          },

          // People nodes. `_visiblePeople` is the full roster on
          // /team-and-agents (no scope flag) and the viewer's slice under
          // the /my-agents lens.
          People: {
            childrenAs: 'state',
            // #2315 — inject a STABLE per-person id into each child's state.
            // TeamPersonNode previously computed its id via (s._nodeId || _personId(s, 0))
            // (hardcoded idx=0), so every person lacking a userId collapsed to
            // 'p-0' — colliding positions/highlights and rendering the wrong
            // info. Carry the correctly-indexed id as _nodeId instead.
            children: (el, s) =>
              _teamWindowFilter(
                s.root,
                el,
                _visiblePeople(s.root).map((p, i) => ({
                  ...p,
                  _nodeId: _personId(p, i),
                  // Stable reconciler key. Without it the reconciler keys array
                  // children by POSITIONAL INDEX (create.js:2360), so windowing
                  // the array would patch the WRONG node's state. _personId uses
                  // the FULL-roster index i, computed here BEFORE the window
                  // filter so a windowed-out person never shifts anyone's id.
                  key: _personId(p, i)
                })),
                (x) => x._nodeId
              ),
            childExtends: 'TeamPersonNode'
          },

          // Agent nodes — sourced from Mongo via sdk.listAgents (§unified-perms
          // PR #10), with SEED_AGENTS as the boot/offline fallback.
          // `_visibleAgents` filters to the viewer's assigned agents under
          // the /my-agents lens; full roster otherwise.
          Agents: {
            childrenAs: 'state',
            children: (el, s) =>
              _teamWindowFilter(
                s.root,
                el,
                // Stable reconciler key by agent id (see People above — agents
                // carry `.id` but had no `.key`, so the reconciler fell back to
                // positional index).
                _visibleAgents(s.root).map((a) => ({ ...a, key: a.id })),
                (x) => x.id
              ),
            childExtends: 'TeamAgentNode'
          }
        }
      }
    }
  },

  // Modals — mount at the page level so they can overlay the canvas
  // unconstrained. Hidden until their `*` state key is truthy.
  TeamMemberModal: {},
  AgentModal: {},

  // Create modal for the "+" menu's New human. Page-level so it overlays the
  // canvas unconstrained; hidden until `_createMemberOpen` is truthy. (New
  // agent's CreateAgentModal is the shell-root single instance —
  // workspace/app.js — since 2026-09-26: this page opens as a window over
  // home, whose own mount made one "+ → Agent" open two dialogs.)
  CreateMemberModal: {}
}

// ─── Card menu trigger (⋯) ───────────────────────────────────
//
// Three-dot menu button positioned in the top-right corner of every
// node card — mirrors the project-tile MenuButton in /studios.
// Clicking opens the same detail modal as a body click (so it serves
// as an explicit affordance for "what does this node do?").

// ─── Person node ──────────────────────────────────────────────

export const TeamPersonNode = {
  position: 'absolute',
  width: PERSON_W + 'px',
  flow: 'y',
  gap: 'V',
  padding: 'A',
  round: 'radiusCard',
  // Borderless blue surface (2026-06-12 per Nika "make human background
  // blue, agent gray, remove borders"). The border used to carry the
  // connect/hover affordances — those move to a box-shadow ring so the
  // function survives without permanent card chrome. (Category border
  // coloring retired with the border itself.)
  background: 'document',
  backdropFilter: 'var(--shadow-float)',
  borderWidth: '0',
  boxShadow: (el, s) => {
    const id = s._nodeId || _personId(s, 0)
    // Link-drag source: the node you started dragging a connection from.
    if (s.root?._tagConnectFrom === id) return '0 0 0 2px var(--color-purple)'
    // Drag-collision / link-target: an in-flight link or card is over it.
    if (s.root?._tagHoverNode === id) return '0 0 0 2px var(--color-cyan)'
    return 'var(--shadow-float)'
  },
  cursor: (el, s) => (s.root?._tagConnectMode ? 'pointer' : 'grab'),
  userSelect: 'var(--shadow-float)',
  zIndex: '2',
  shadow: 'float',
  transition: 'transform B defaultBezier',
  ':hover': { transform: 'translateY(-2px)' },
  ':hover > [data-tag-linkhandle]': { opacity: '1', transform: 'scale(1)' },

  attr: {
    'data-node-id': (el, s) => s._nodeId || _personId(s, 0),
    role: 'button',
    tabindex: '0'
  },
  // FLAT prop, FUNCTION form — an attribute inside `attr:` never re-resolves
  // a polyglot lookup (docs/I18N-CONVENTION.md §1).
  'aria-label': (el, s) =>
    el
      .call('polyglot', 'canvas.team.node.openAria')
      .replace(
        '{name}',
        s.name || s.title || el.call('polyglot', 'canvas.team.node.personFallback')
      ),
  style: {
    left: (el, s) => _posFor(s.root, s._nodeId || _personId(s, 0)).x + 'px',
    top: (el, s) => _posFor(s.root, s._nodeId || _personId(s, 0)).y + 'px'
  },
  onPointerdown: _onNodePointerdown,
  onClick: (e, el, s) => _onNodeClick(e, el, s, 'person'),
  onContextmenu: (e, el, s) => el.call('tagNodeContextmenu', e, s, 'person'),
  onKeydown: (e, el, s) => {
    if (e.target !== el.node || e.defaultPrevented) return
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault()
      _onNodeClick(e, el, s, 'person')
    }
  },

  // Drag-to-assign handle (studios-style) — reveals on hover; drag onto
  // another card to draw a connection/assignment edge.
  LinkHandle: _linkHandle(),

  // Online indicator (green dot, top-right) — visible when the user's
  // email is in root.onlineUsers (Supabase presence subscription, see
  // shared/functions/presence.js).
  OnlineDot: {
    position: 'absolute',
    top: 'V',
    right: 'V',
    width: 'Z',
    height: 'Z',
    round: '50%',
    background: 'green',
    boxShadow: '0 0 0 2px var(--color-card)',
    zIndex: '3',
    pointerEvents: 'var(--shadow-float)',
    hide: (el, s) => !_isPersonOnline(s.root, s.email)
  },

  // Idle indicator (amber, static) when the user is present but inactive
  // (5 min no activity / tab hidden — see idleState.js). Mirrors the agent
  // IdleDot; mutually exclusive with the green OnlineDot. Both hide when the
  // person isn't present at all. (2026-06-19 per Nika "online or idle".)
  IdleDot: {
    position: 'absolute',
    top: 'V',
    right: 'V',
    width: 'Z',
    height: 'Z',
    round: '50%',
    background: 'orange',
    boxShadow: '0 0 0 2px var(--color-card)',
    zIndex: '3',
    pointerEvents: 'var(--shadow-float)',
    hide: (el, s) => !_isPersonIdle(s.root, s.email)
  },

  Head: {
    flow: 'x',
    align: 'center left',
    gap: 'Z',

    Avatar: {
      boxSize: 'B',
      round: '50%',
      align: 'center center',
      flexShrink: '0',
      src: (el, s) => el.call('avatarUrlFor', s.email || s.name)
    },

    Info: {
      flow: 'y',
      gap: '0',
      flex: '1',
      minWidth: '0',
      Name: {
        tag: 'span',
        fontSize: 'Z2',
        fontWeight: '700',
        color: 'title',
        overflow: 'hidden',
        textOverflow: 'ellipsis',
        whiteSpace: 'nowrap',
        text: (el, s) => s.name || (s.email || '').split('@')[0] || 'Member'
      },
      Role: {
        tag: 'span',
        fontSize: 'Y2',
        color: 'caption',
        overflow: 'hidden',
        textOverflow: 'ellipsis',
        whiteSpace: 'nowrap',
        text: (el, s) => s.position || s.role || (s.email || '').split('@')[0] || ''
      }
    }
  }

  // CategoryPill (the category/status badge — blue "Active", purple
  // "Manager", etc.) removed 2026-06-10 per Nika "remove badges from
  // cards". The category signal still reads through the node's
  // category-colored border (_categoryColor on borderColor above);
  // OnlineDot (green presence indicator) is unrelated and stays.
}

// ─── Agent node ───────────────────────────────────────────────

export const TeamAgentNode = {
  position: 'absolute',
  width: AGENT_W + 'px',
  flow: 'y',
  gap: 'V',
  padding: 'A',
  round: 'radiusCard',
  // Borderless neutral surface (2026-06-12 per Nika — humans blue, agents
  // neutral, no borders); connect affordance moves to a box-shadow ring.
  // §8.5: `gray4` was STATIC, so on the light canvas every adaptive ink on
  // the card collapsed — agent names 1.66:1, role captions 1.25:1 (2026-08-08
  // walk; dark measured clean, the signature of a static ground). The
  // `overlayStrong` pair keeps the same neutral-against-canvas reading on
  // both themes.
  background: 'document',
  borderWidth: '0',
  boxShadow: (el, s) =>
    _isAgentWorking(s.root, s.id)
      ? '0 0 0 2px var(--color-blue), var(--shadow-float)'
      : s.root?._tagConnectFrom === s.id
        ? '0 0 0 2px var(--color-purple)'
        : 'var(--shadow-float)',
  cursor: (el, s) => (s.root?._tagConnectMode ? 'pointer' : 'grab'),
  userSelect: 'var(--shadow-float)',
  zIndex: '2',
  shadow: 'float',
  transition: 'transform B defaultBezier',
  ':hover': { transform: 'translateY(-2px)' },
  ':hover > [data-tag-linkhandle]': { opacity: '1', transform: 'scale(1)' },

  attr: {
    'data-node-id': (el, s) => s.id,
    role: 'button',
    tabindex: '0'
  },
  // FLAT prop, FUNCTION form — see TeamPersonNode.
  'aria-label': (el, s) =>
    el
      .call('polyglot', 'canvas.team.node.openAria')
      .replace(
        '{name}',
        s.name || s.title || el.call('polyglot', 'canvas.team.node.agentFallback')
      ),
  style: {
    left: (el, s) => _posFor(s.root, s.id).x + 'px',
    top: (el, s) => _posFor(s.root, s.id).y + 'px'
  },
  onPointerdown: _onNodePointerdown,
  onClick: (e, el, s) => _onNodeClick(e, el, s, 'agent'),
  onContextmenu: (e, el, s) => el.call('tagNodeContextmenu', e, s, 'agent'),
  onKeydown: (e, el, s) => {
    if (e.target !== el.node || e.defaultPrevented) return
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault()
      _onNodeClick(e, el, s, 'agent')
    }
  },

  // Drag-to-assign handle (studios-style) — reveals on hover; drag onto
  // another card to draw a connection/assignment edge.
  LinkHandle: _linkHandle(),

  // Pulsing indicator (green dot + ring, top-right) — visible when
  // the agent has a recent heartbeat in root.triage.agentPresence
  // (last 5 min). Animation lives in the `ttag-pulse` keyframe
  // injected by the page's onCreate.
  PulseDot: {
    position: 'absolute',
    top: 'V',
    right: 'V',
    width: 'Z',
    height: 'Z',
    round: '50%',
    background: 'green',
    boxShadow: '0 0 0 2px var(--color-card)',
    zIndex: '3',
    pointerEvents: 'var(--shadow-float)',
    style: { animation: 'ttag-pulse 1.6s ease-out infinite' },
    hide: (el, s) => !_isAgentPulsing(s.root, s.id)
  },

  // Idle indicator (amber, static — no pulse) when the agent heartbeat has gone
  // quiet but is still recent. Mutually exclusive with the green online pulse.
  // (2026-06-19 per Nika "cards should show online or idle".)
  IdleDot: {
    position: 'absolute',
    top: 'V',
    right: 'V',
    width: 'Z',
    height: 'Z',
    round: '50%',
    background: 'orange',
    boxShadow: '0 0 0 2px var(--color-card)',
    zIndex: '3',
    pointerEvents: 'var(--shadow-float)',
    hide: (el, s) => !_isAgentIdle(s.root, s.id)
  },

  // Live "working now" — a blue pulsing dot (top-LEFT, so it never collides with
  // the green/amber heartbeat dots top-right) shown while Simone is running a
  // delegated/chained task on this agent THIS turn (root.activeProcesses). Reuses
  // the ttag-pulse keyframe; independent of the dormant heartbeat presence.
  WorkingDot: {
    position: 'absolute',
    top: 'V',
    left: 'V',
    width: 'Z',
    height: 'Z',
    round: '50%',
    background: 'blue',
    boxShadow: '0 0 0 2px var(--color-card)',
    zIndex: '3',
    pointerEvents: 'var(--shadow-float)',
    style: { animation: 'ttag-pulse 1.2s ease-out infinite' },
    hide: (el, s) => !_isAgentWorking(s.root, s.id)
  },

  Head: {
    flow: 'x',
    align: 'center left',
    gap: 'Z',
    Glyph: {
      width: 'B',
      height: 'B',
      round: 'radiusControl',
      align: 'center center',
      flexShrink: '0',
      // §4.4 pale/deep — `purple.3` composites to a PALE plate (#C0B2D3 on
      // light), so white ink on it measured 1.99:1 (2026-08-08 walk). A pale
      // hue plate takes dark ink; the hue still reads as the identity.
      background: 'overlayStrong',
      color: 'title',
      fontSize: 'A',
      fontWeight: '700',
      text: '◆'
    },
    Info: {
      flow: 'y',
      gap: '0',
      flex: '1',
      minWidth: '0',
      Name: {
        tag: 'span',
        fontSize: 'Z2',
        fontWeight: '700',
        color: 'title',
        text: (el, s) => s.name || s.id
      },
      Caption: {
        tag: 'span',
        fontSize: 'Y2',
        color: 'caption',
        // KEY-IN-STATE: `s.caption` holds a bundle key for a seeded agent and a
        // server-supplied string for a custom one. polyglot returns a non-key
        // verbatim, so both pass through the same call.
        text: (el, s) =>
          el
            .call('polyglot', s.caption || 'canvas.team.agent.fallback')
            .replace('{name}', s.captionName || '')
      }
    }
  }

  // KindPill (epic/helper/router/brain/observer/platform badge) removed
  // 2026-06-10 per Nika "remove badges from cards". The agent kind still
  // surfaces in the AgentModal detail view.
}

// ─── Modals ───────────────────────────────────────────────────
//
// Modal usage notes: the shared Modal (workspace/packages/shared/
// components/Modal.js) renders a `Window` child via the brand's
// `ModalWindow` shell, and that shell ALWAYS has a `ModalHeader`
// (Hgroup with H + P + Icons.Close). To populate the header without
// rendering a duplicate dialog beside it, set H.text + P.text on the
// inherited ModalHeader.Hgroup. All page-specific content lives inside
// `Window` as additional named children — siblings of ModalHeader.
//
// `closeModal` is registered as a workspace function and is invoked by
// ModalHeader.Icons.Close via `el.call('closeModal')` — it clears the
// closest modal element's stateKey on root.

export const TeamMemberModal = {
  extends: 'Modal',
  stateKey: '_selectedTeamMember',
  pointerEvents: (el, s) => (s.root?._selectedTeamMember ? 'auto' : 'none'),

  Window: {
    width: 'I',
    maxWidth: '92vw',
    gap: 'A',

    ModalHeader: {
      Hgroup: {
        H: {
          text: (el, s) => {
            const m = s.root?._selectedTeamMember
            return m?.name || (m?.email || '').split('@')[0] || 'Member'
          }
        },
        P: {
          text: (el, s) => {
            const m = s.root?._selectedTeamMember
            return m?.position || m?.role || m?.email || ''
          }
        }
      }
    },

    HeaderAvatar: {
      extends: 'Avatar',
      boxSize: 'C',
      round: '50%',
      align: 'center center',
      src: (el, s) =>
        el.call(
          'avatarUrlFor',
          s.root?._selectedTeamMember?.email || s.root?._selectedTeamMember?.name
        )
    },

    Detail: {
      flow: 'y',
      gap: 'V',
      fontSize: 'Z1',
      color: 'title',
      Email: {
        tag: 'p',
        margin: '0',
        text: (el, s) =>
          el
            .call('polyglot', 'canvas.team.member.email')
            .replace('{value}', s.root?._selectedTeamMember?.email || '—')
      },
      Location: {
        tag: 'p',
        margin: '0',
        text: (el, s) =>
          el
            .call('polyglot', 'canvas.team.member.location')
            .replace('{value}', s.root?._selectedTeamMember?.location || '—')
      },
      Timezone: {
        tag: 'p',
        margin: '0',
        text: (el, s) =>
          el
            .call('polyglot', 'canvas.team.member.timezone')
            .replace('{value}', s.root?._selectedTeamMember?.timezone || '—')
      }
    }
  }
}

// ─── Agent settings editor (the /my-agents modal) ─────────────────
//
// The agent modal is a 4-tab editor over the agent's DB record:
//   Instructions — system prompt / persona (metadata.instructions)
//   Permissions  — access preset + permission set (metadata.userRole /
//                  metadata.permissions)
//   Tools        — runtime capability allowlist (metadata.capabilities),
//                  rendered from the live capability/tool catalog
//                  (sdk.getAgentToolCatalog → agent-core)
//   Assignment   — the people this agent is assigned to (AgentAssignment)
//
// _selectedAgent carries only the lean roster fields; opening the modal
// fetches the full record (sdk.getAgent) into _agentDetail and seeds the
// editable _agentDraft. Save PATCHes via sdk.updateAgent. Assignment edits
// route through the workspace agentAssignments functions via el.call.

const AGENT_ACCESS_PRESETS = [
  {
    value: 'guest',
    label: 'canvas.team.agentAccess.guestLabel',
    permissions: ['read'],
    hint: 'canvas.team.agentAccess.guestHint'
  },
  {
    value: 'editor',
    label: 'canvas.team.agentAccess.editorLabel',
    permissions: ['read', 'write'],
    hint: 'canvas.team.agentAccess.editorHint'
  },
  {
    value: 'admin',
    label: 'canvas.team.agentAccess.adminLabel',
    permissions: ['read', 'write', 'manage'],
    hint: 'canvas.team.agentAccess.adminHint'
  }
]
const _accessPreset = (v) =>
  AGENT_ACCESS_PRESETS.find((p) => p.value === v) || AGENT_ACCESS_PRESETS[1]

const _agentToolCatalog = (root) =>
  Array.isArray(root?._agentToolCatalog) ? root._agentToolCatalog : []

// Fetch the capability/tool catalog once per session and cache on root.
const _loadAgentToolCatalog = async (root) => {
  if (!root || root._agentToolCatalogLoaded) return
  root._agentToolCatalogLoaded = true
  try {
    const sdk = getSDK()
    if (!sdk || typeof sdk.getAgentToolCatalog !== 'function') {
      root._agentToolCatalogLoaded = false
      return
    }
    const res = await sdk.getAgentToolCatalog()
    const data = (res && res.data) || res || {}
    root.update({ _agentToolCatalog: Array.isArray(data.catalog) ? data.catalog : [] })
  } catch (err) {
    root._agentToolCatalogLoaded = false
    console.warn('[agent-editor] tool catalog failed:', err && err.message)
  }
}

// Open the editor for an agent: reset tab/draft state, fetch the full
// record + tool catalog, seed the editable draft. Falls back to the
// bundled persona for instructions when the DB record has none (so seeded
// agents present their canonical persona as the editable starting point).
const _openAgentEditor = (root, agent) => {
  if (!root || !agent) return
  root.update({
    _selectedAgent: agent,
    _agentTab: 'instructions',
    _agentDetail: null,
    _agentDetailFor: agent.id,
    _agentDraft: null,
    _agentSavePending: false,
    _agentSaveError: null,
    _agentSaveSavedAt: null
  })
  _loadAgentToolCatalog(root)

  const seed = (personas) => ({
    instructions: personas[agent.id] || '',
    userRole: 'editor',
    permissions: ['read', 'write'],
    capabilities: [],
    capabilitiesRestricted: false
  })

  // Seed-only agent (no Mongo record yet) — show the bundled persona;
  // saving is gated off until the agent is provisioned.
  if (!agent.userId) {
    _agentInstructions().then(
      (personas) => {
        if (root._agentDetailFor !== agent.id) return
        root.update({ _agentDetail: { _seedOnly: true }, _agentDraft: seed(personas) })
      },
      (err) => {
        console.warn('[agent-editor] persona load failed:', err && err.message)
        if (root._agentDetailFor !== agent.id) return
        root.update({ _agentDetail: { _seedOnly: true }, _agentDraft: seed({}) })
      }
    )
    return
  }

  ;(async () => {
    const personas = await _agentInstructions().catch(() => ({}))
    try {
      const sdk = getSDK()
      const detail = await sdk.getAgent(agent.userId)
      const d = (detail && detail.data) || detail || {}
      // Guard against a fast modal switch — only seed if still this agent.
      if (root._agentDetailFor !== agent.id) return
      root.update({
        _agentDetail: d,
        _agentDraft: {
          instructions: d.instructions || personas[agent.id] || '',
          userRole: d.userRole || 'editor',
          permissions:
            Array.isArray(d.permissions) && d.permissions.length
              ? d.permissions
              : _accessPreset(d.userRole).permissions,
          capabilities: Array.isArray(d.capabilities) ? d.capabilities : [],
          capabilitiesRestricted: d.capabilitiesRestricted === true
        }
      })
    } catch (err) {
      console.warn('[agent-editor] getAgent failed:', err && err.message)
      if (root._agentDetailFor !== agent.id) return
      root.update({ _agentDetail: { _error: true }, _agentDraft: seed(personas) })
    }
  })()
}

const _patchAgentDraft = (root, patch) => {
  const draft = (root && root._agentDraft) || {}
  root.update({ _agentDraft: { ...draft, ...patch }, _agentSaveSavedAt: null })
}

const _setAgentAccess = (root, value) => {
  const preset = _accessPreset(value)
  _patchAgentDraft(root, { userRole: preset.value, permissions: preset.permissions })
}

const _agentHasCap = (root, cap) => {
  const caps = root && root._agentDraft && root._agentDraft.capabilities
  return Array.isArray(caps) && caps.includes(cap)
}

const _toggleAgentCapability = (root, cap) => {
  const caps =
    root && root._agentDraft && Array.isArray(root._agentDraft.capabilities)
      ? root._agentDraft.capabilities
      : []
  _patchAgentDraft(root, {
    capabilities: caps.includes(cap) ? caps.filter((c) => c !== cap) : [...caps, cap]
  })
}

// "Restrict to selected tools" master switch. OFF (default) = the agent is
// unrestricted (inherits the runtime default) and the per-capability toggles
// below are just a saved pre-selection; ON = the agent's runs are capped to
// exactly the selected capabilities (empty selection = deny-all). This keeps
// enforcement opt-in, so checking a capability never silently locks an agent.
const _agentRestricted = (root) =>
  !!(root && root._agentDraft && root._agentDraft.capabilitiesRestricted)

const _toggleAgentRestricted = (root) =>
  _patchAgentDraft(root, { capabilitiesRestricted: !_agentRestricted(root) })

// Persist Instructions + Permissions + Tools via sdk.updateAgent. On
// success patches the local detail; surfaces pending / saved / error in
// the footer.
const _saveAgentSettings = async (root) => {
  const agent = root && root._selectedAgent
  const draft = root && root._agentDraft
  if (!agent || !draft) return
  if (!agent.userId) {
    root.update({
      _agentSaveError: 'canvas.team.agentModal.errorBuiltIn'
    })
    return
  }
  root.update({ _agentSavePending: true, _agentSaveError: null })
  try {
    const sdk = getSDK()
    const updated = await sdk.updateAgent(agent.userId, {
      instructions: draft.instructions || '',
      userRole: draft.userRole || 'editor',
      permissions: Array.isArray(draft.permissions) ? draft.permissions : [],
      capabilities: Array.isArray(draft.capabilities) ? draft.capabilities : [],
      capabilitiesRestricted: !!draft.capabilitiesRestricted
    })
    const d = (updated && updated.data) || updated || {}
    root.update({
      _agentSavePending: false,
      _agentSaveSavedAt: Date.now(),
      _agentDetail: d && d.id ? d : root._agentDetail
    })
  } catch (err) {
    root.update({
      _agentSavePending: false,
      _agentSaveError: (err && err.message) || 'canvas.team.agentModal.errorSaveFailed'
    })
  }
}

// ─ Assignment helpers ─ read the org-scoped AgentAssignment maps the
// workspace boot already populates (root.agentAssignmentsByAgent), and
// route mutations through the workspace agentAssignments functions via
// el.call (optimistic local update + SDK write). Gated on the admin tier
// (the server requires admin.teams for assignment writes).
const _agentAssignees = (root) => {
  const uid = root && root._selectedAgent && root._selectedAgent.userId
  if (!uid) return []
  const rows = root.agentAssignmentsByAgent && root.agentAssignmentsByAgent[uid]
  return Array.isArray(rows) ? rows : []
}

const _canManageAgents = (root) => _viewerTier(root) === 'admin'

const _personNameByUserId = (root, uid) => {
  if (!uid) return null
  const p = (Array.isArray(root && root.people) ? root.people : []).find(
    (x) => String(x.userId) === String(uid)
  )
  return (p && (p.name || p.email)) || null
}

// The person an assignment row names (the row's text and the unassign ask).
const _assigneeLabel = (el, s) =>
  s.assigneeName ||
  _personNameByUserId(s.root, s.assignee) ||
  s.assignee ||
  el.call('polyglot', 'canvas.team.assignee.fallback')

const _assignablePeople = (root) => {
  const people = Array.isArray(root && root.people) ? root.people : []
  const taken = new Set(_agentAssignees(root).map((r) => String(r.assignee)))
  return people.filter((p) => p && p.userId && !taken.has(String(p.userId)))
}

const _orgIdForAssign = (root) =>
  (root && root.jwtClaims && root.jwtClaims.workspace_org_id) ||
  (root &&
    root.jwtClaims &&
    Array.isArray(root.jwtClaims.orgs) &&
    root.jwtClaims.orgs[0] &&
    root.jwtClaims.orgs[0].id) ||
  (root && root.workspace && root.workspace.id) ||
  null

// Tab + access button factories (called at modal-literal eval time, so
// they must be defined above the AgentModal export). Active state uses the
// reactive `.isActive` conditional-class (createEffect-wrapped) rather than
// a reactive `background` factory, which freezes at mount in v3.14.
const agentTabButton = (key, label) => ({
  scope: { key },
  tag: 'button',
  type: 'button',
  text: label,
  minHeight: 'controlHCompact',
  padding: '0 A',
  round: 'radiusPill',
  border: 'none',
  cursor: 'pointer',
  fontSize: 'Z2',
  fontWeight: '700',
  background: 'transparent',
  color: 'caption',
  isActive: (el, s) => ((s.root && s.root._agentTab) || 'instructions') === el.scope.key,
  '.isActive': { background: 'selected', color: 'title' },
  onClick: (e, el, s) => {
    e.preventDefault()
    s.root.update({ _agentTab: el.scope.key })
  }
})

const agentAccessButton = (value, label) => ({
  scope: { value },
  tag: 'button',
  type: 'button',
  text: label,
  flex: '1 1 0',
  minHeight: 'controlHCompact',
  padding: '0 A',
  round: 'radiusPill',
  border: 'none',
  cursor: 'pointer',
  fontSize: 'Z2',
  fontWeight: '700',
  background: 'overlay',
  color: 'title',
  isActive: (el, s) =>
    ((s.root && s.root._agentDraft && s.root._agentDraft.userRole) || 'editor') === el.scope.value,
  '.isActive': { background: 'blue', color: 'white' },
  onClick: (e, el, s) => {
    e.preventDefault()
    _setAgentAccess(s.root, el.scope.value)
  }
})

export const AgentModal = {
  extends: 'Modal',
  stateKey: '_selectedAgent',
  pointerEvents: (el, s) => (s.root?._selectedAgent ? 'auto' : 'none'),
  // Clear editor drafts when the modal closes (backdrop / Escape). The
  // shared Modal merges extraClose into its close update.
  extraClose: {
    _agentTab: 'instructions',
    _agentDetail: null,
    _agentDetailFor: null,
    _agentDraft: null,
    _agentSavePending: false,
    _agentSaveError: null,
    _agentSaveSavedAt: null
  },

  Window: {
    width: 'I',
    maxWidth: '94vw',
    height: '82dvh',
    maxHeight: '92dvh',
    flow: 'y',
    gap: 'V',

    ModalHeader: {
      Hgroup: {
        H: {
          text: (el, s) =>
            (s.root?._selectedAgent && (s.root._selectedAgent.name || s.root._selectedAgent.id)) ||
            el.call('polyglot', 'canvas.team.agent.fallback')
        },
        P: {
          text: (el, s) => {
            const agent = s.root?._selectedAgent
            if (!agent?.caption) return ''
            return el.call('polyglot', agent.caption).replace('{name}', agent.captionName || '')
          }
        }
      }
    },

    // ─ Tab bar ─
    TabBar: {
      flexShrink: '0',
      flow: 'x',
      gap: 'V',
      align: 'center',
      TabInstructions: agentTabButton('instructions', 'Instructions'),
      TabPermissions: agentTabButton('permissions', 'Permissions'),
      TabTools: agentTabButton('tools', 'Tools'),
      TabAssignment: agentTabButton('assignment', 'Assignment')
    },

    // ─ Instructions ─
    InstructionsPanel: {
      hide: (el, s) => ((s.root && s.root._agentTab) || 'instructions') !== 'instructions',
      flow: 'y',
      gap: 'V',
      flex: '1 1 0',
      minHeight: '0',
      Label: {
        tag: 'span',
        fontSize: 'Y',
        fontWeight: '700',
        color: 'caption',
        text: '{{ canvas.team.agentModal.instructionsLabel | polyglot }}'
      },
      InstructionsArea: {
        tag: 'textarea',
        name: 'agent-instructions',
        placeholder: (el) => el.call('polyglot', 'canvas.team.agentModal.instructionsPlaceholder'),
        flex: '1 1 0',
        minHeight: '0',
        width: '100%',
        padding: 'A',
        round: 'radiusControl',
        theme: 'document',
        color: 'title',
        fontSize: 'Z1',
        lineHeight: '1.5',
        border: 'none',
        resize: 'none',
        fontFamily: 'inherit',
        value: (el, s) => (s.root && s.root._agentDraft && s.root._agentDraft.instructions) || '',
        onInput: (e, el, s) => {
          _patchAgentDraft(s.root, { instructions: el.node.value })
        }
      }
    },

    // ─ Permissions ─
    PermissionsPanel: {
      hide: (el, s) => ((s.root && s.root._agentTab) || 'instructions') !== 'permissions',
      flow: 'y',
      gap: 'A',
      flex: '1 1 0',
      minHeight: '0',
      overflow: 'auto',
      AccessLabel: {
        tag: 'span',
        fontSize: 'Y',
        fontWeight: '700',
        color: 'caption',
        text: '{{ canvas.team.agentModal.accessLabel | polyglot }}'
      },
      AccessRow: {
        flow: 'x',
        gap: 'V',
        AccessGuest: agentAccessButton(
          'guest',
          '{{ canvas.team.agentAccess.guestLabel | polyglot }}'
        ),
        AccessEditor: agentAccessButton(
          'editor',
          '{{ canvas.team.agentAccess.editorLabel | polyglot }}'
        ),
        AccessAdmin: agentAccessButton(
          'admin',
          '{{ canvas.team.agentAccess.adminLabel | polyglot }}'
        )
      },
      AccessHint: {
        tag: 'p',
        margin: '0',
        fontSize: 'Y',
        lineHeight: '1.4',
        color: 'caption',
        // `hint` on the preset holds a bundle KEY — resolved here so a language
        // switch re-renders it.
        text: (el, s) =>
          el.call(
            'polyglot',
            _accessPreset(s.root && s.root._agentDraft && s.root._agentDraft.userRole).hint
          )
      },
      PermsLabel: {
        tag: 'span',
        fontSize: 'Y',
        fontWeight: '700',
        color: 'caption',
        text: '{{ canvas.team.agentModal.permsLabel | polyglot }}'
      },
      PermsChips: {
        flow: 'x',
        gap: 'V',
        flexWrap: 'wrap',
        childrenAs: 'state',
        children: (el, s) =>
          ((s.root && s.root._agentDraft && s.root._agentDraft.permissions) || []).map((p) => ({
            perm: p
          })),
        childExtends: 'AgentPermChip'
      }
    },

    // ─ Tools ─
    ToolsPanel: {
      hide: (el, s) => ((s.root && s.root._agentTab) || 'instructions') !== 'tools',
      flow: 'y',
      gap: 'V',
      flex: '1 1 0',
      minHeight: '0',
      overflow: 'auto',
      // Master switch — enforcement is opt-in. OFF = unrestricted (the toggles
      // below are a saved pre-selection); ON = runs capped to the selection.
      RestrictRow: {
        flexShrink: '0',
        flow: 'x',
        align: 'center',
        justifyContent: 'space-between',
        gap: 'V',
        padding: 'V',
        round: 'radiusControl',
        theme: 'card',
        Meta: {
          flow: 'y',
          gap: 'V',
          Title: {
            tag: 'span',
            fontSize: 'Z2',
            fontWeight: '700',
            color: 'title',
            text: '{{ canvas.team.agentModal.restrictTitle | polyglot }}'
          },
          Desc: {
            tag: 'span',
            fontSize: 'Y',
            lineHeight: '1.4',
            color: 'caption',
            text: (el, s) =>
              el.call(
                'polyglot',
                _agentRestricted(s.root)
                  ? 'canvas.team.agentModal.restrictDescOn'
                  : 'canvas.team.agentModal.restrictDescOff'
              )
          }
        },
        RestrictToggle: {
          tag: 'button',
          type: 'button',
          flexShrink: '0',
          minHeight: 'controlHCompact',
          padding: '0 A',
          round: 'radiusPill',
          border: 'none',
          cursor: 'pointer',
          fontSize: 'Y',
          fontWeight: '700',
          background: 'overlay',
          color: 'caption',
          isOn: (el, s) => _agentRestricted(s.root),
          '.isOn': { background: 'blue', color: 'white' },
          text: (el, s) =>
            el.call(
              'polyglot',
              _agentRestricted(s.root)
                ? 'canvas.team.agentModal.toggleOn'
                : 'canvas.team.agentModal.toggleOff'
            ),
          onClick: (e, el, s) => {
            e.preventDefault()
            _toggleAgentRestricted(s.root)
          }
        }
      },
      Intro: {
        tag: 'p',
        margin: '0',
        fontSize: 'Y',
        lineHeight: '1.4',
        color: 'caption',
        text: (el, s) =>
          el.call(
            'polyglot',
            _agentRestricted(s.root)
              ? 'canvas.team.agentModal.toolsIntroOn'
              : 'canvas.team.agentModal.toolsIntroOff'
          )
      },
      Empty: {
        hide: (el, s) => _agentToolCatalog(s.root).length > 0,
        flow: 'y',
        gap: 'V',
        Row_1: { extends: 'SkeletonCard' },
        Row_2: { extends: 'SkeletonCard' }
      },
      List: {
        flow: 'y',
        gap: 'V',
        // Muted when not enforcing — the selection is informational until the
        // master switch is on. Reactive via the conditional class (raw
        // reactive `opacity` would freeze at mount in v3.14).
        isMuted: (el, s) => !_agentRestricted(s.root),
        '.isMuted': { opacity: '0.6' },
        childrenAs: 'state',
        children: (el, s) => _agentToolCatalog(s.root).map((c) => ({ ...c })),
        childExtends: 'AgentToolRow'
      }
    },

    // ─ Assignment ─
    AssignmentPanel: {
      hide: (el, s) => ((s.root && s.root._agentTab) || 'instructions') !== 'assignment',
      flow: 'y',
      gap: 'A',
      flex: '1 1 0',
      minHeight: '0',
      overflow: 'auto',
      AssignedLabel: {
        tag: 'span',
        fontSize: 'Y',
        fontWeight: '700',
        color: 'caption',
        text: '{{ canvas.team.agentModal.assignedLabel | polyglot }}'
      },
      AssignedEmpty: {
        hide: (el, s) => _agentAssignees(s.root).length > 0,
        tag: 'p',
        margin: '0',
        fontSize: 'Y',
        color: 'caption',
        text: '{{ canvas.team.agentModal.assignedEmpty | polyglot }}'
      },
      AssignedList: {
        flow: 'y',
        gap: 'V',
        childrenAs: 'state',
        children: (el, s) => _agentAssignees(s.root).map((r) => ({ ...r })),
        childExtends: 'AgentAssigneeRow'
      },
      AddLabel: {
        hide: (el, s) => !_canManageAgents(s.root),
        tag: 'span',
        fontSize: 'Y',
        fontWeight: '700',
        color: 'caption',
        text: '{{ canvas.team.agentModal.addLabel | polyglot }}'
      },
      AddEmpty: {
        hide: (el, s) => !_canManageAgents(s.root) || _assignablePeople(s.root).length > 0,
        tag: 'p',
        margin: '0',
        fontSize: 'Y',
        color: 'caption',
        text: '{{ canvas.team.agentModal.addEmpty | polyglot }}'
      },
      AddChips: {
        hide: (el, s) => !_canManageAgents(s.root),
        flow: 'x',
        gap: 'V',
        flexWrap: 'wrap',
        childrenAs: 'state',
        children: (el, s) =>
          _assignablePeople(s.root).map((p) => ({
            userId: p.userId,
            name: p.name || p.email || 'Member',
            email: p.email || ''
          })),
        childExtends: 'AgentAssignChip'
      }
    },

    // ─ Footer ─ save (Instructions/Permissions/Tools) + talk.
    AgentFooter: {
      flexShrink: '0',
      flow: 'x',
      align: 'center',
      justifyContent: 'space-between',
      gap: 'V',
      Status: {
        tag: 'span',
        fontSize: 'Y',
        color: 'caption',
        isError: (el, s) => !!(s.root && s.root._agentSaveError),
        '.isError': { color: 'red' },
        text: (el, s) => {
          const r = s.root || {}
          // Key-in-state — `_agentSaveError` holds a bundle KEY, or a raw SDK
          // message that polyglot returns verbatim on a miss.
          if (r._agentSaveError) return el.call('polyglot', r._agentSaveError)
          if (r._agentSavePending) return el.call('polyglot', 'canvas.team.agentModal.saving')
          if (r._agentSaveSavedAt) return el.call('polyglot', 'canvas.team.agentModal.saved')
          return ''
        }
      },
      Actions: {
        flow: 'x',
        gap: 'V',
        align: 'center',
        SaveBtn: {
          tag: 'button',
          type: 'button',
          hide: (el, s) => !(s.root && s.root._selectedAgent && s.root._selectedAgent.userId),
          minHeight: 'controlH',
          padding: '0 A',
          round: 'radiusPill',
          border: 'none',
          cursor: 'pointer',
          fontSize: 'Z2',
          fontWeight: '700',
          background: 'overlay',
          color: 'title',
          disabled: (el, s) => !!(s.root && s.root._agentSavePending),
          opacity: (el, s) => (s.root && s.root._agentSavePending ? '0.6' : '1'),
          text: (el, s) =>
            el.call(
              'polyglot',
              s.root && s.root._agentSavePending
                ? 'canvas.team.agentModal.saving'
                : 'canvas.team.agentModal.saveChanges'
            ),
          onClick: (e, el, s) => {
            e.preventDefault()
            _saveAgentSettings(s.root)
          }
        },
        TalkBtn: {
          tag: 'button',
          type: 'button',
          minHeight: 'controlH',
          padding: '0 A',
          round: 'radiusPill',
          border: 'none',
          cursor: 'pointer',
          fontSize: 'Z2',
          fontWeight: '700',
          color: 'fillInk',
          background: 'blue',
          text: (el, s) =>
            `Talk to ${(s.root && s.root._selectedAgent && s.root._selectedAgent.name) || 'agent'}`,
          // Open THIS agent's own thread, resumed if it has one
          // (AI-TALK-TO-AGENT-REUSES-OPEN-THREAD-1, answer C). The button used
          // to write the routing target only, so the drawer kept whatever
          // thread was last open and merely aimed the next message at the
          // agent — measured on production 2026-08-30 06:47.
          onClick: (e, el, s) => {
            e.preventDefault()
            const a = s.root && s.root._selectedAgent
            s.root.update({
              _selectedAgent: null,
              _agentDraft: null,
              _agentDetail: null,
              assistantOpen: true
            })
            el.call('openAgentThread', a ? { userId: a.userId, id: a.id, name: a.name } : null)
          }
        }
      }
    }
  }
}

// Read-only pill summarizing one granted permission. Child state = { perm }.
export const AgentPermChip = {
  tag: 'span',
  padding: 'V',
  round: 'radiusControl',
  background: 'overlay',
  color: 'title',
  fontSize: 'Y',
  fontWeight: '700',
  text: (el, s) => s.perm || ''
}

// Capability row — toggles a capability on/off and lists the tools it
// unlocks. Child state = one AgentCapabilityCatalog entry. The On/Off
// state uses the reactive `.isOn` conditional-class.
export const AgentToolRow = {
  flow: 'y',
  gap: 'V',
  padding: 'V',
  round: 'radiusControl',
  theme: 'card',
  Head: {
    flow: 'x',
    align: 'center',
    justifyContent: 'space-between',
    gap: 'V',
    Meta: {
      flow: 'y',
      gap: 'V',
      Title: {
        tag: 'span',
        fontSize: 'Z2',
        fontWeight: '700',
        color: 'title',
        text: (el, s) => s.label || s.capability || ''
      },
      Desc: {
        tag: 'span',
        fontSize: 'Y',
        lineHeight: '1.4',
        color: 'caption',
        text: (el, s) => s.description || ''
      }
    },
    ToolToggle: {
      tag: 'button',
      type: 'button',
      flexShrink: '0',
      minHeight: 'controlHCompact',
      padding: '0 A',
      round: 'radiusPill',
      border: 'none',
      cursor: 'pointer',
      fontSize: 'Y',
      fontWeight: '700',
      background: 'overlay',
      color: 'caption',
      isOn: (el, s) => _agentHasCap(s.root, s.capability),
      '.isOn': { background: 'blue', color: 'white' },
      text: (el, s) => (_agentHasCap(s.root, s.capability) ? 'On' : 'Off'),
      onClick: (e, el, s) => {
        e.preventDefault()
        _toggleAgentCapability(s.root, s.capability)
      }
    }
  },
  Tools: {
    hide: (el, s) => !(Array.isArray(s.tools) && s.tools.length),
    tag: 'span',
    fontSize: 'Y',
    lineHeight: '1.4',
    color: 'caption',
    text: (el, s) =>
      el
        .call('polyglot', 'canvas.team.agentModal.toolsList')
        .replace('{tools}', (s.tools || []).join('  ·  '))
  }
}

// One assigned-person row with a remove control (admin tier only). Child
// state = an AgentAssignment row { id, assignee, assigneeName, orgId }.
export const AgentAssigneeRow = {
  flow: 'x',
  align: 'center',
  justifyContent: 'space-between',
  gap: 'V',
  padding: 'V',
  round: 'radiusControl',
  theme: 'card',
  Name: {
    tag: 'span',
    fontSize: 'Z2',
    color: 'title',
    text: (el, s) => _assigneeLabel(el, s)
  },
  RemoveBtn: {
    hide: (el, s) => !_canManageAgents(s.root),
    tag: 'button',
    type: 'button',
    text: '{{ canvas.team.assignee.remove | polyglot }}',
    minHeight: 'controlHCompact',
    padding: '0 Z',
    round: 'radiusPill',
    border: 'none',
    cursor: 'pointer',
    fontSize: 'Y',
    fontWeight: '700',
    background: 'overlay',
    color: 'red',
    // The house confirm first (CRUD + confirm standard rule 4); the unassign
    // runs only on the verb. A failure shows in the editor's status line.
    onClick: (e, el, s) => {
      e.preventDefault()
      const root = s.root
      if (!_canManageAgents(root)) return
      const agent = root && root._selectedAgent
      const orgId = s.orgId || _orgIdForAssign(root)
      const assignment = { id: s.id, assignee: s.assignee }
      el.call('confirmAgentUnassign', {
        agent:
          (agent && (agent.name || agent.id)) || el.call('polyglot', 'canvas.team.agent.fallback'),
        person: _assigneeLabel(el, s),
        onConfirm: () =>
          Promise.resolve(
            el.call('unassignAgentFromUser', root, {
              orgId,
              assignmentId: assignment.id,
              agentUserId: agent && agent.userId,
              assigneeUserId: assignment.assignee
            })
          ).catch((err) => {
            console.warn('[agent-editor] unassign failed:', err && err.message)
            root.update({
              _agentSaveError: (err && err.message) || 'canvas.team.toast.removeFailed'
            })
          })
      })
    }
  }
}

// "+ Person" chip — assigns the open agent to a person on click. Child
// state = { userId, name, email }.
export const AgentAssignChip = {
  tag: 'button',
  type: 'button',
  padding: 'V',
  round: 'radiusControl',
  border: 'none',
  cursor: 'pointer',
  fontSize: 'Y',
  fontWeight: '700',
  background: 'overlay',
  color: 'title',
  text: (el, s) => '+ ' + (s.name || 'Member'),
  onClick: (e, el, s) => {
    e.preventDefault()
    const agent = s.root && s.root._selectedAgent
    const orgId = _orgIdForAssign(s.root)
    if (!agent || !agent.userId || !s.userId || !orgId) return
    try {
      el.call('assignAgentToUser', s.root, {
        orgId,
        agentUserId: agent.userId,
        assigneeUserId: s.userId
      })
    } catch (err) {
      console.warn('[agent-editor] assign failed:', err && err.message)
    }
  }
}
